import React, { useState, useEffect } from 'react';
import './ScallopPlus.css';

function ScallopPlus() {
  // Hero phone drift: visible scroll-linked movement (max ~220px over the
  // first viewport of scrolling) via a CSS var, so no re-renders happen.
  // Uses the independent `translate` property in CSS, never `transform`,
  // so it can't fight .reveal entrances or Framer scale/rotate rules.
  // Skipped under prefers-reduced-motion.
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const progress = Math.min(
          window.scrollY / Math.max(window.innerHeight, 1),
          1
        );
        document
          .querySelector('.page-scallopplus')
          ?.style.setProperty(
            '--plus-hero-drift',
            `${Math.round(progress * 220)}px`
          );
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  // "Coverage for every kind of trip" — Search Destinations / Select Plan /
  // Activate Plan tabs, each crossfading its own image on the right. Same
  // pattern as Home.js's "YOU CONTROL YOUR MONEY" section: one shared index
  // across all 3 responsive variants (Desktop 01/Tablet 01/Mobile 01),
  // reusing the already-generic selected/light style classes that exist for
  // each button (scallopplus-s70/s72/s73/s74 selected, s75/s76/s77/s78
  // light) rather than needing new ones.
  const [coverageIndex, setCoverageIndex] = useState(0);

  // "SET UP YOUR eSIM IN MINUTES" — Compatibility/Install/Activate step
  // tabs. Unlike the Coverage section above, only step 1's content ever
  // existed in this static export (steps 2/3 weren't duplicated blocks
  // to toggle between) — so instead of duplicating the whole content
  // block per step, the single existing block's step number/label/body
  // copy/image are parametrized by array lookup, driven by this shared
  // index. Step 1 keeps its original image untouched; steps 2/3 use the
  // new animation/3/1.png and animation/3/2.png assets.
  const [esimStepIndex, setEsimStepIndex] = useState(0);
  const esimSteps = [
    {
      number: '01.',
      label: 'Compatibility',
      body: 'Make sure your device supports eSIM.',
      image: '/framerusercontent.com/images/ugg13ztUw4RHIbB8RAkY37BHM.png',
    },
    {
      number: '02.',
      label: 'Install',
      body: 'Scan the QR code or follow the guided setup in the app.',
      image: '/framerusercontent.com/animation/3/1.png',
    },
    {
      number: '03.',
      label: 'Activate',
      body: "Turn on the eSIM in your device settings. You may need to enable 'data roaming' - this is just an iOS/Android setting and doesn't trigger any extra charges.",
      image: '/framerusercontent.com/animation/3/2.png',
    },
  ];
  return (
    <div className="page-scallopplus">
      <div
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        <>
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            id="svg-1769600017_146"
          >
            <path d="M0 0H28V28H0V0Z" fill="#012169" />
          </svg>
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            id="svg-1439609099_274"
          >
            <path
              d="M3.28125 0L13.9562 10.5583L24.5875 0H28V3.61667L17.5 14.0583L28 24.4417V28H24.5L14 17.5583L3.54375 28H0V24.5L10.4562 14.1167L0 3.73333V0H3.28125Z"
              fill="white"
            />
          </svg>
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            id="svg864966285_327"
          >
            <path
              d="M18.55 16.3917L28 25.6667V28L16.1437 16.3917H18.55ZM10.5 17.5583L10.7625 19.6L2.3625 28H0L10.5 17.5583ZM28 0V0.175L17.1062 11.1417L17.1937 8.575L25.8125 0H28ZM0 0L10.4562 10.2667H7.83125L0 2.45V0Z"
              fill="#C8102E"
            />
          </svg>
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            id="svg-1051602840_193"
          >
            <path
              d="M10.5437 0V28H17.5437V0H10.5437ZM0 9.33333V18.6667H28V9.33333H0Z"
              fill="white"
            />
          </svg>
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            id="svg149270427_195"
          >
            <path
              d="M0 11.2583V16.8583H28V11.2583H0ZM11.9437 0V28H16.1437V0H11.9437Z"
              fill="#C8102E"
            />
          </svg>
          <svg
            width="699"
            height="196"
            viewBox="0 0 699 196"
            fill="none"
            id="svg-1137231340_6119"
          >
            <path
              d="M668.903 75.4492C685.462 75.4492 698.806 91.3256 699 111.098C699 111.195 699 111.39 699 111.488C699 111.682 699 111.877 699 111.975C698.806 131.747 685.462 147.623 668.903 147.623C668.027 147.623 667.15 147.623 666.176 147.526L666.079 147.428C659.163 146.649 653.904 140.805 653.904 133.792V129.604H647.865V137.299V147.623V162.501L626.826 177.72V111.488V75.4492H647.865V85.7737V93.4684H653.904V89.2801C653.904 82.2673 659.163 76.3258 666.079 75.5466H666.176C667.15 75.4492 668.027 75.4492 668.903 75.4492ZM662.767 126.585C671.144 126.682 677.962 119.864 677.962 111.488C677.962 103.208 671.144 96.3904 662.767 96.4878C654.683 96.5852 647.962 103.208 647.865 111.39V111.488C647.865 111.585 647.865 111.585 647.865 111.682C647.962 119.864 654.683 126.487 662.767 126.585Z"
              fill="#030319"
            />
            <path
              d="M583.064 75.4492C602.933 75.4492 619.102 91.6177 619.102 111.488C619.102 131.455 602.933 147.623 583.064 147.623C563.096 147.623 546.928 131.455 546.928 111.488C546.928 91.6177 563.096 75.4492 583.064 75.4492ZM583.064 126.585C591.343 126.585 598.063 119.864 598.063 111.488C598.063 103.208 591.343 96.4878 583.064 96.4878C574.687 96.4878 567.966 103.208 567.966 111.488C567.966 119.864 574.687 126.585 583.064 126.585Z"
              fill="#030319"
            />
            <path
              d="M488.334 147.623V60.5712L509.373 45.3523V147.623H488.334ZM518.431 147.623V58.9032L539.47 43.6843V147.623H518.431Z"
              fill="#030319"
            />
            <path
              d="M459.569 85.7737V75.4492H480.608V111.488V147.623H459.569V137.299V129.604H453.53V133.792C453.53 140.805 448.368 146.649 441.355 147.428H441.258C440.381 147.526 439.407 147.623 438.531 147.623C422.07 147.623 408.628 131.747 408.434 111.975C408.434 111.78 408.434 111.682 408.434 111.488C408.434 111.39 408.434 111.195 408.434 111C408.628 91.3255 422.07 75.4492 438.531 75.4492C439.407 75.4492 440.381 75.4492 441.258 75.5466H441.355C448.368 76.3258 453.53 82.2673 453.53 89.2801V93.4684H459.569V85.7737ZM459.569 111.682C459.569 111.585 459.569 111.585 459.569 111.488V111.39C459.472 103.208 452.848 96.5852 444.667 96.4878C436.29 96.3904 429.472 103.208 429.472 111.488C429.472 119.864 436.29 126.585 444.667 126.585C452.848 126.487 459.472 119.767 459.569 111.682Z"
              fill="#030319"
            />
            <path
              d="M365.433 96.4878C357.056 96.4878 350.336 103.208 350.336 111.488C350.336 119.864 357.056 126.585 365.433 126.585C371.569 126.585 376.926 122.883 379.166 117.526H400.984C398.159 134.571 383.257 147.623 365.433 147.623C345.465 147.623 329.297 131.455 329.297 111.488C329.297 91.6177 345.465 75.4492 365.433 75.4492C383.257 75.4492 398.159 88.4035 400.984 105.546H379.166C376.926 100.189 371.569 96.4878 365.433 96.4878Z"
              fill="#030319"
            />
            <path
              d="M281.603 66.6326C266.839 66.6326 260.844 71.4881 260.844 77.8003C260.844 89.745 321.687 81.7819 321.687 115.479C321.687 120.335 315.245 147.623 278.739 147.623C254.492 147.623 244.023 133.833 239.37 127.715L238.744 126.938L254.724 115.283C254.724 115.283 262.365 126.647 278.739 126.647C293.592 126.647 299.587 121.792 299.587 115.479C299.587 103.243 238.744 111.692 238.744 77.8003C238.744 72.9448 245.186 45.6566 281.603 45.6566C305.94 45.6566 316.408 59.4464 321.061 65.5644L321.687 66.3412L305.707 77.6162C305.707 77.6162 298.066 66.6326 281.603 66.6326Z"
              fill="#030319"
            />
            <ellipse
              cx="97.8957"
              cy="97.8954"
              rx="97.8957"
              ry="97.8954"
              fill="url(#svg-1137231340_6119_paint0_linear_1843_2573)"
            />
            <path
              d="M103.576 14.4118C115.249 13.8539 129.142 17.6986 139.282 23.2509C158.722 33.8511 173.055 51.8528 179.03 73.1714C184.904 94.1022 182.275 116.505 171.709 135.505C161.278 154.663 143.583 168.825 122.604 174.805C121.916 175 121.535 175.131 120.923 174.79C120.848 174.232 123.053 171.981 123.588 171.354C127.648 166.588 129.697 162.178 131.673 156.382C133.304 155.423 134.869 154.356 136.358 153.19C151.792 141.054 161.837 123.339 164.329 103.867C166.932 83.664 161.768 65.3031 149.385 49.2548C145.6 44.5826 141.241 40.4056 136.412 36.8219C127.789 30.443 117.883 26.3644 107.081 25.9036C100.34 25.6159 94.1717 26.5885 89.9416 32.5503C88.1358 35.1174 87.0577 38.1249 86.8214 41.2544C85.8581 52.6598 94.7935 61.1826 102.438 68.3351C106.731 72.3277 111.304 76.1609 115.634 80.1021C126.117 89.6427 137.49 100.106 139.627 114.873C140.71 122.355 139.833 129.826 137.214 136.929C136.785 138.093 136.144 140.229 135.444 141.211L135.214 141.233C134.907 140.566 134.342 137.129 134.064 136.042C133.36 133.295 132.371 130.526 131.084 127.972C123.12 112.161 107.759 101.092 95.0915 89.0304C83.0497 77.5651 70.841 66.3252 69.8719 48.4363C69.4109 39.9256 72.4487 31.2566 78.2811 25.0667C85.079 17.8519 93.8037 14.7104 103.576 14.4118Z"
              fill="white"
            />
            <path
              d="M91.5742 180.82C79.901 181.378 66.0082 177.534 55.8688 171.981C36.4285 161.381 22.0955 143.379 16.1206 122.061C10.246 101.13 12.8756 78.7274 23.4413 59.7273C33.8727 40.5694 51.5673 26.4075 72.5462 20.4273C73.234 20.2325 73.6158 20.1012 74.2273 20.442C74.3028 20.9998 72.097 23.2514 71.5628 23.8785C67.5026 28.6444 65.4533 33.0547 63.4777 38.8504C61.8468 39.8095 60.2815 40.8763 58.7919 42.042C43.3582 54.178 33.3134 71.8929 30.8211 91.3651C28.218 111.568 33.3828 129.929 45.765 145.977C49.5501 150.65 53.9092 154.827 58.7385 158.41C67.3615 164.789 77.2674 168.868 88.0694 169.329C94.8106 169.616 100.979 168.644 105.209 162.682C107.015 160.115 108.093 157.107 108.329 153.978C109.292 142.572 100.357 134.05 92.7125 126.897C88.4197 122.904 83.8462 119.071 79.5159 115.13C69.0339 105.589 57.6601 95.1262 55.5235 80.3593C54.4404 72.877 55.3169 65.4066 57.9365 58.3035C58.3658 57.1393 59.0062 55.0028 59.7064 54.0216L59.9363 53.9994C60.2437 54.6664 60.8081 58.1028 61.0866 59.1903C61.7902 61.9377 62.7798 64.7057 64.0662 67.2598C72.0303 83.0713 87.391 94.1406 100.059 106.202C112.101 117.667 124.309 128.907 125.278 146.796C125.739 155.307 122.702 163.976 116.869 170.165C110.071 177.38 101.347 180.522 91.5742 180.82Z"
              fill="white"
            />
            <defs>
              <linearGradient
                id="svg-1137231340_6119_paint0_linear_1843_2573"
                x1="97.8957"
                y1="0"
                x2="46.0375"
                y2="179.202"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0F1AF1" />
                <stop offset="1" stopColor="#2E38F9" />
              </linearGradient>
            </defs>
          </svg>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            id="svg-1839680863_948"
          >
            <path
              d="M9.90033 7.61699C10.967 7.66699 11.242 8.57533 11.2587 9.00033H12.7503C12.6837 7.35033 11.5087 6.34199 9.87533 6.34199C8.03366 6.34199 6.66699 7.50033 6.66699 10.117C6.66699 11.7337 7.44199 13.6503 9.86699 13.6503C11.717 13.6503 12.7087 12.2753 12.7337 11.192H11.242C11.217 11.6837 10.867 12.342 9.88366 12.392C8.79199 12.3587 8.33366 11.5087 8.33366 10.117C8.33366 7.70866 9.40033 7.63366 9.90033 7.61699ZM10.0003 1.66699C5.40033 1.66699 1.66699 5.40033 1.66699 10.0003C1.66699 14.6003 5.40033 18.3337 10.0003 18.3337C14.6003 18.3337 18.3337 14.6003 18.3337 10.0003C18.3337 5.40033 14.6003 1.66699 10.0003 1.66699ZM10.0003 16.667C6.32533 16.667 3.33366 13.6753 3.33366 10.0003C3.33366 6.32533 6.32533 3.33366 10.0003 3.33366C13.6753 3.33366 16.667 6.32533 16.667 10.0003C16.667 13.6753 13.6753 16.667 10.0003 16.667Z"
              fill="#666D80"
            />
          </svg>
          <svg
            id="2566832247"
            display="block"
            role="presentation"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M 15.75 0 L 0.75 0 C 0.336 0 0 0.336 0 0.75 L 0 11.25 C 0 11.664 0.336 12 0.75 12 L 15.75 12 C 16.164 12 16.5 11.664 16.5 11.25 L 16.5 0.75 C 16.5 0.336 16.164 0 15.75 0 Z M 8.25 6.75 C 7.214 6.75 6.375 5.911 6.375 4.875 C 6.375 3.839 7.214 3 8.25 3 C 9.286 3 10.125 3.839 10.125 4.875 C 10.125 5.911 9.286 6.75 8.25 6.75 Z"
              fillOpacity="var(--1m6trwb, 0)"
              fill="var(--21h8s6, rgb(0, 0, 0))"
              height="12px"
              id="TfH67sxl4"
              transform="translate(3.75 8.25)"
              width="16.5px"
            />
            <path
              d="M 0.75 12 C 0.336 12 0 11.664 0 11.25 L 0 0.75 C 0 0.336 0.336 0 0.75 0 L 15.75 0 C 16.164 0 16.5 0.336 16.5 0.75 L 16.5 11.25 C 16.5 11.664 16.164 12 15.75 12 Z"
              fill="transparent"
              height="12px"
              id="u_qL5Ipuy"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(3.75 8.25)"
              width="16.5px"
            />
            <path
              d="M 0 6.75 L 0 3.75 C 0 1.679 1.679 0 3.75 0 C 5.821 0 7.5 1.679 7.5 3.75 L 7.5 6.75"
              fill="transparent"
              height="6.75px"
              id="R9U5kcF0Z"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(8.25 1.5)"
              width="7.5px"
            />
            <path
              d="M 0 1.875 C 0 0.839 0.839 0 1.875 0 C 2.911 0 3.75 0.839 3.75 1.875 C 3.75 2.911 2.911 3.75 1.875 3.75 C 0.839 3.75 0 2.911 0 1.875 Z"
              fill="transparent"
              height="3.75px"
              id="DxIjrjDB6"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(10.125 11.25)"
              width="3.75px"
            />
            <path
              d="M 0 0 L 0 2.25"
              fill="transparent"
              height="2.25px"
              id="PsR8qKhm1"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(12 15)"
              width="1px"
            />
          </svg>
          <svg
            id="175829625"
            display="block"
            role="presentation"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M 12 6.75 L 19.5 10.5 L 19.5 13.5 L 12 12 L 12 15 L 13.5 16.5 L 13.5 19.5 L 9.75 18 L 6 19.5 L 6 16.5 L 7.5 15 L 7.5 12 L 0 13.5 L 0 10.5 L 7.5 6.75 L 7.5 2.25 C 7.5 1.007 8.507 0 9.75 0 C 10.993 0 12 1.007 12 2.25 Z"
              fillOpacity="var(--1m6trwb, 0)"
              fill="var(--21h8s6, rgb(0, 0, 0))"
              height="19.5px"
              id="OSiNnMF1N"
              transform="translate(2.25 2.25)"
              width="19.5px"
            />
            <path
              d="M 12 6.75 L 19.5 10.5 L 19.5 13.5 L 12 12 L 12 15 L 13.5 16.5 L 13.5 19.5 L 9.75 18 L 6 19.5 L 6 16.5 L 7.5 15 L 7.5 12 L 0 13.5 L 0 10.5 L 7.5 6.75 L 7.5 2.25 C 7.5 1.007 8.507 0 9.75 0 C 10.993 0 12 1.007 12 2.25 Z"
              fill="transparent"
              height="19.5px"
              id="QF7jvRl_w"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(2.25 2.25)"
              width="19.5px"
            />
          </svg>
          <svg
            id="941805552"
            display="block"
            role="presentation"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M 6.75 5.673 L 3.998 3 C 3.808 2.829 3.718 2.573 3.76 2.32 C 3.802 2.068 3.969 1.854 4.205 1.753 L 4.875 1.5 L 10.268 3.485 L 15.41 0.415 C 16.685 -0.336 18.317 -0.044 19.253 1.101 L 21 3.342 L 7.18 11.592 C 6.034 12.259 4.585 12.094 3.618 11.187 L 0.237 7.875 C 0.052 7.702 -0.033 7.447 0.011 7.198 C 0.055 6.949 0.222 6.738 0.455 6.639 L 0.75 6.494 L 3.73 7.5 Z"
              fillOpacity="var(--1m6trwb, 0)"
              fill="var(--21h8s6, rgb(0, 0, 0))"
              height="11.999016919302917px"
              id="UrpsVK2qr"
              transform="translate(1.5 5.25)"
              width="20.99991123745394px"
            />
            <path
              d="M 0 0 L 13.5 0"
              fill="transparent"
              height="1px"
              id="SPJY1s_ru"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(2.25 20.25)"
              width="13.5px"
            />
            <path
              d="M 6.75 5.673 L 3.998 3 C 3.808 2.829 3.718 2.573 3.76 2.32 C 3.802 2.068 3.969 1.854 4.205 1.753 L 4.875 1.5 L 10.268 3.485 L 15.41 0.415 C 16.685 -0.336 18.317 -0.044 19.253 1.101 L 21 3.342 L 7.18 11.592 C 6.034 12.259 4.585 12.094 3.618 11.187 L 0.237 7.875 C 0.052 7.702 -0.033 7.447 0.011 7.198 C 0.055 6.949 0.222 6.738 0.455 6.639 L 0.75 6.494 L 3.73 7.5 Z"
              fill="transparent"
              height="11.999016919302917px"
              id="tsspz4TXi"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(1.5 5.25)"
              width="20.99991123745394px"
            />
          </svg>
          <svg
            id="4090123071"
            display="block"
            role="presentation"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M 0 0 L 16.5 0"
              fill="transparent"
              height="1px"
              id="OwsiYUS5O"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(3.75 6)"
              width="16.5px"
            />
            <path
              d="M 0 0 L 5.25 0"
              fill="transparent"
              height="1px"
              id="N74NQKmbf"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(3.75 12)"
              width="5.25px"
            />
            <path
              d="M 0 0 L 6.75 0"
              fill="transparent"
              height="1px"
              id="s8ediYkAd"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(3.75 18)"
              width="6.75px"
            />
            <path
              d="M 4.5 6.613 L 7.282 8.25 L 6.525 5.195 L 9 3.151 L 5.752 2.901 L 4.5 0 L 3.248 2.901 L 0 3.151 L 2.475 5.195 L 1.718 8.25 Z"
              fill="transparent"
              height="8.25px"
              id="okMMMBzBw"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(12.75 9.75)"
              width="9px"
            />
          </svg>
          <svg
            id="2883338743"
            display="block"
            role="presentation"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M 0 9 C 0 4.029 4.029 0 9 0 C 13.971 0 18 4.029 18 9 C 18 13.971 13.971 18 9 18 C 4.029 18 0 13.971 0 9 Z"
              fillOpacity="var(--1m6trwb, 0)"
              fill="var(--21h8s6, rgb(0, 0, 0))"
              height="18px"
              id="M2umBpORv"
              transform="translate(3 3)"
              width="18px"
            />
            <path
              d="M 0 3 L 2.25 5.25 L 7.5 0"
              fill="transparent"
              height="5.25px"
              id="SKKzYtdWd"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(8.25 9.75)"
              width="7.5px"
            />
            <path
              d="M 0 9 C 0 4.029 4.029 0 9 0 C 13.971 0 18 4.029 18 9 C 18 13.971 13.971 18 9 18 C 4.029 18 0 13.971 0 9 Z"
              fill="transparent"
              height="18px"
              id="Ub0giwiXO"
              strokeDasharray=""
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="var(--pgex8v, 1.5)"
              stroke="var(--21h8s6, rgb(0, 0, 0))"
              transform="translate(3 3)"
              width="18px"
            />
          </svg>
        </>
      </div>
      <>
        {/*$*/}
        <div
          data-framer-root=""
          className="framer-ikzmG framer-rcOGl framer-6lXp4 framer-1kt7ub1 scallopplus-s1"
        >
          <div className="ssr-variant hidden-18dn0ox">
            <div
              className="framer-8k3v9p-container hidden-1kt7ub1 hidden-otbxcl scallopplus-s2"
              data-framer-appear-id="8k3v9p"
            >
              <div
                className="framer-8MFL4 framer-15zawlh framer-v-15zawlh scallopplus-s3"
                data-framer-name="Desktop [Default Boxed]"
              >
                <header
                  className="framer-ooxjtq scallopplus-s4"
                  data-border="true"
                  data-framer-name="Main Area"
                >
                  <div
                    className="framer-p578i7 scallopplus-s5"
                    data-framer-name="Background [Default]"
                  />
                  <div
                    className="framer-pc27k4 scallopplus-s6"
                    data-framer-name="Background [Boxed Mini]"
                  />
                  <div className="framer-cjwofg" data-framer-name="Content">
                    {/*$*/}
                    <a className="framer-1jy87r1 framer-145ct8v" href="/">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1hjthz scallopplus-s7"
                        aria-hidden="true"
                      />
                    </a>
                    {/*/$*/}
                    <nav className="framer-xe9vp0" data-framer-name="Nav">
                      <div
                        className="framer-1hg3ikx-container"
                        data-framer-name="Menu [Desktop]"
                        name="Menu [Desktop]"
                      >
                        <div
                          name="Menu [Desktop]"
                          className="framer-n0D3w framer-1itjusz framer-v-1itjusz scallopplus-s8"
                          data-framer-name="Menu"
                        >
                          {/*$*/}
                          <div className="framer-mquu2a-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/accounts"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi scallopplus-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p
                                  dir="auto"
                                  className="framer-text scallopplus-s11"
                                >
                                  Accounts{' '}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1yixouk-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/scallop-pay"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi scallopplus-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p
                                  dir="auto"
                                  className="framer-text scallopplus-s11"
                                >
                                  Scallop Pay
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-wi7xty-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/cards"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi scallopplus-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p
                                  dir="auto"
                                  className="framer-text scallopplus-s11"
                                >
                                  Cards
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1tpzm7c-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/scallop-plus"
                              data-framer-page-link-current="true"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi scallopplus-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p
                                  dir="auto"
                                  className="framer-text scallopplus-s11"
                                >
                                  Scallop+
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-u2qi4r-container">
                            {/*$*/}
                            <a
                              className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                              data-framer-name="Menu Item Simple"
                              data-highlight="true"
                              href="/helpdesk"
                              tabIndex={0}
                            >
                              <div
                                className="framer-19g30xi scallopplus-s10 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p
                                  dir="auto"
                                  className="framer-text scallopplus-s11"
                                >
                                  FAQs
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                    </nav>
                    <div
                      className="framer-14uxsxw"
                      data-framer-name="Menu Icon"
                    >
                      <div
                        className="framer-16051d2"
                        data-framer-name="QR Overlay Trigger"
                        data-highlight="true"
                        tabIndex={0}
                      >
                        <div className="framer-168591e-container">
                          <div
                            className="framer-wWHmH framer-ly6l8a framer-v-18roq29 scallopplus-s12"
                            data-border="true"
                            data-framer-name="Variant 2"
                            data-highlight="true"
                            tabIndex={0}
                          >
                            <div
                              className="framer-1ebu3nm scallopplus-s13 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s14"
                              >
                                Get Started
                              </p>
                            </div>
                            <div
                              className="framer-1u4w7ze scallopplus-s15 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p className="framer-text scallopplus-s16">
                                Coming soon
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </header>
              </div>
            </div>
          </div>
          <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
            <div
              className="framer-8k3v9p-container hidden-1kt7ub1 hidden-otbxcl"
              data-framer-appear-id="8k3v9p"
            >
              <div
                className="framer-8MFL4 framer-15zawlh framer-v-ewbo4l scallopplus-s3"
                data-framer-name="Mobile"
              >
                <header
                  className="framer-ooxjtq scallopplus-s17"
                  data-border="true"
                  data-framer-name="Main Area"
                >
                  <div
                    className="framer-p578i7 scallopplus-s5"
                    data-framer-name="Background [Default]"
                  />
                  <div
                    className="framer-pc27k4 scallopplus-s6"
                    data-framer-name="Background [Boxed Mini]"
                  />
                  <div className="framer-cjwofg" data-framer-name="Content">
                    {/*$*/}
                    <a className="framer-1jy87r1 framer-145ct8v" href="/">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1hjthz scallopplus-s7"
                        aria-hidden="true"
                      />
                    </a>
                    {/*/$*/}
                    <nav className="framer-xe9vp0" data-framer-name="Nav" />
                    <div
                      className="framer-14uxsxw"
                      data-framer-name="Menu Icon"
                    >
                      <div className="framer-1yr51k6-container">
                        {/*$*/}
                        <div
                          className="framer-OuhX3 framer-1ib4qbi framer-v-1ib4qbi scallopplus-s18"
                          data-framer-name="Mobile Menu"
                          data-highlight="true"
                          tabIndex={0}
                        >
                          <div className="framer-1cux5of scallopplus-s19" />
                          <div className="framer-1w1bul6 scallopplus-s19" />
                          <div className="framer-1ku2ucf scallopplus-s20" />
                        </div>
                        {/*/$*/}
                      </div>
                    </div>
                  </div>
                </header>
              </div>
            </div>
          </div>
          <div className="framer-1pkbs22" data-framer-name="Main Container">
            <header className="framer-jrcvhy" data-framer-name="Hero Section">
              <div className="framer-68k96a" data-framer-name="Hero">
                <div className="framer-qt7rbc" data-framer-name="Container">
                  <div className="framer-uulf8w" data-framer-name="Header">
                    <div className="framer-1h9hnl9" data-framer-name="Heading">
                      <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                        <div
                          className="framer-kd004m scallopplus-s21 reveal"
                          data-framer-appear-id="kd004m"
                          data-framer-name="One account. One balance. Built for global money."
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text scallopplus-s22">
                            Data that runs on
                            <br className="framer-text" />
                            <span className="framer-text scallopplus-s23">
                              your money
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-1kt7ub1">
                        <div
                          className="framer-kd004m scallopplus-s21 reveal"
                          data-framer-appear-id="kd004m"
                          data-framer-name="One account. One balance. Built for global money."
                          data-framer-component-type="RichTextContainer"
                        >
                          <h1
                            className="framer-text framer-styles-preset-18sajzf scallopplus-s24"
                            data-styles-preset="nXmNeSqma"
                            dir="auto"
                          >
                            Data that runs on
                            <br className="framer-text" />
                            <span className="framer-text scallopplus-s25">
                              your money
                            </span>
                          </h1>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                        <div
                          className="framer-gtllao scallopplus-s21 reveal reveal-1"
                          data-framer-appear-id="gtllao"
                          data-framer-name="52+ fiat currencies and digital assets. One unifie"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text scallopplus-s26">
                            Global eSIMs, paid from the same balance you use for
                            everything else.
                          </p>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-1kt7ub1">
                        <div
                          className="framer-gtllao scallopplus-s21 reveal reveal-1"
                          data-framer-appear-id="gtllao"
                          data-framer-name="52+ fiat currencies and digital assets. One unifie"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p dir="auto" className="framer-text scallopplus-s26">
                            Global eSIMs, paid from the same balance you use for
                            everything else.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant">
                      <div
                        className="framer-1ssbtox scallopplus-s27 reveal reveal-2"
                        data-framer-name="image 1577"
                      >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          width="1254"
                          height="1254"
                          sizes="(min-width: 1440px) 300px, (max-width: 809.98px) 300px, (min-width: 810px) and (max-width: 1439.98px) 300px"
                          srcSet="/framerusercontent.com/images/vM8VVvdezH0f86cOHzmnavy918.png 512w,/framerusercontent.com/images/vM8VVvdezH0f86cOHzmnavy918.png 1024w,/framerusercontent.com/images/vM8VVvdezH0f86cOHzmnavy918.png 1254w"
                          src="/framerusercontent.com/images/vM8VVvdezH0f86cOHzmnavy918.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="framer-wydjrt" data-framer-name="Visual">
                    <div className="ssr-variant">
                      <div
                        className="framer-1v9i2jm-container hidden-18dn0ox scallopplus-s30"
                        data-framer-appear-id="1v9i2jm"
                      >
                        <div
                          className="framer-SFexE framer-f39156 framer-v-f39156 scallopplus-s31"
                          data-framer-name="Default"
                        >
                          <div className="framer-1bok59">
                            <div
                              className="framer-1lok788"
                              data-framer-name="Text"
                            >
                              <div
                                className="framer-1u2j8fn"
                                data-framer-name="Frame 32402"
                              >
                                <div
                                  className="framer-1jkp6a3 scallopplus-s32"
                                  data-framer-name="Countries"
                                >
                                  <div
                                    data-framer-component-type="SVG"
                                    data-framer-name="Vector"
                                    parentsize="0"
                                    _constraints="[object Object]"
                                    rotation="0"
                                    shadows=""
                                    className="framer-w526dc scallopplus-s33"
                                    aria-hidden="true"
                                  >
                                    <div className="svgContainer scallopplus-s34">
                                      <svg
                                        viewBox="0 0 28 28"
                                        className="scallopplus-s35"
                                      >
                                        <use href="#svg-1769600017_146" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div
                                    data-framer-component-type="SVG"
                                    data-framer-name="Vector"
                                    parentsize="0"
                                    _constraints="[object Object]"
                                    rotation="0"
                                    shadows=""
                                    className="framer-118gwtw scallopplus-s33"
                                    aria-hidden="true"
                                  >
                                    <div className="svgContainer scallopplus-s34">
                                      <svg
                                        viewBox="0 0 28 28"
                                        className="scallopplus-s35"
                                      >
                                        <use href="#svg-1439609099_274" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div
                                    data-framer-component-type="SVG"
                                    data-framer-name="Vector"
                                    parentsize="0"
                                    _constraints="[object Object]"
                                    rotation="0"
                                    shadows=""
                                    className="framer-xjfmjz scallopplus-s33"
                                    aria-hidden="true"
                                  >
                                    <div className="svgContainer scallopplus-s34">
                                      <svg
                                        viewBox="0 0 28 28"
                                        className="scallopplus-s35"
                                      >
                                        <use href="#svg864966285_327" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div
                                    data-framer-component-type="SVG"
                                    data-framer-name="Vector"
                                    parentsize="0"
                                    _constraints="[object Object]"
                                    rotation="0"
                                    shadows=""
                                    className="framer-m536so scallopplus-s33"
                                    aria-hidden="true"
                                  >
                                    <div className="svgContainer scallopplus-s34">
                                      <svg
                                        viewBox="0 0 28 28"
                                        className="scallopplus-s35"
                                      >
                                        <use href="#svg-1051602840_193" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div
                                    data-framer-component-type="SVG"
                                    data-framer-name="Vector"
                                    parentsize="0"
                                    _constraints="[object Object]"
                                    rotation="0"
                                    shadows=""
                                    className="framer-1c98zar scallopplus-s33"
                                    aria-hidden="true"
                                  >
                                    <div className="svgContainer scallopplus-s34">
                                      <svg
                                        viewBox="0 0 28 28"
                                        className="scallopplus-s35"
                                      >
                                        <use href="#svg149270427_195" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  className="framer-1rl4oak"
                                  data-framer-name="Frame 1437257188"
                                >
                                  <div
                                    className="framer-1ypbybj scallopplus-s36 reveal"
                                    data-framer-name="United Kingdom"
                                    data-framer-component-type="RichTextContainer"
                                  >
                                    <p
                                      dir="auto"
                                      className="framer-text scallopplus-s37"
                                    >
                                      United Kingdom
                                    </p>
                                  </div>
                                  <div
                                    className="framer-1haana8 scallopplus-s38 reveal"
                                    data-framer-name="Expires on 5 March"
                                    data-framer-component-type="RichTextContainer"
                                  >
                                    <p
                                      dir="auto"
                                      className="framer-text scallopplus-s39"
                                    >
                                      Expires on 5 March
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="framer-18gmqzp">
                                <div
                                  className="framer-1nip7hr scallopplus-s40 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s41"
                                  >
                                    15 GB | $18.97{' '}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant">
                      <div
                        className="framer-kpoqkd-container hidden-18dn0ox scallopplus-s30"
                        data-framer-appear-id="kpoqkd"
                      >
                        <div
                          className="framer-guwLY framer-x694bb framer-v-x694bb scallopplus-s42"
                          data-framer-name="Default"
                        >
                          <svg
                            className="framer-tVXS1 framer-crezud scallopplus-s43"
                            role="presentation"
                            viewBox="0 0 24 24"
                          >
                            <use href="#2566832247" />
                          </svg>
                          <div
                            className="framer-1piczct scallopplus-s44 reveal"
                            data-framer-name="Build wealth, one step closer"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s45"
                            >
                              Secure payment
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1ihft9t reveal reveal-3" data-framer-name="Phone">
                      <div className="ssr-variant">
                        <div
                          className="framer-1hyvsw scallopplus-s46"
                          data-framer-appear-id="1hyvsw"
                          data-framer-name="Mockup"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="540"
                              height="1018"
                              sizes="(min-width: 1440px) 400px, (max-width: 809.98px) max(min(max((min(100vw, 1300px) - 60px) * 1, 1px), 400px) - 24px, 1px), (min-width: 810px) and (max-width: 1439.98px) 350px"
                              srcSet="/framerusercontent.com/images/EA3Hh7sFUFYJiSdacTCT6MNc.png 540w"
                              src="/framerusercontent.com/images/EA3Hh7sFUFYJiSdacTCT6MNc.png"
                              alt=""
                              className="scallopplus-s47"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-otbxcl">
                      <div
                        className="framer-17yjytf-container hidden-18dn0ox scallopplus-s30"
                        data-framer-appear-id="17yjytf"
                      >
                        <div
                          className="framer-guwLY framer-x694bb framer-v-x694bb scallopplus-s42"
                          data-framer-name="Default"
                        >
                          <svg
                            className="framer-E9iqg framer-crezud scallopplus-s43"
                            role="presentation"
                            viewBox="0 0 24 24"
                          >
                            <use href="#175829625" />
                          </svg>
                          <div
                            className="framer-1piczct scallopplus-s44 reveal"
                            data-framer-name="Build wealth, one step closer"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s45"
                            >
                              Travel Ready
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                      <div
                        className="framer-17yjytf-container hidden-18dn0ox scallopplus-s30"
                        data-framer-appear-id="17yjytf"
                      >
                        <div
                          className="framer-guwLY framer-x694bb framer-v-x694bb scallopplus-s48"
                          data-framer-name="Default"
                        >
                          <svg
                            className="framer-E9iqg framer-crezud scallopplus-s43"
                            role="presentation"
                            viewBox="0 0 24 24"
                          >
                            <use href="#175829625" />
                          </svg>
                          <div
                            className="framer-1piczct scallopplus-s44 reveal"
                            data-framer-name="Build wealth, one step closer"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s45"
                            >
                              Travel Ready
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-otbxcl">
                      <div
                        className="framer-1v8hint-container hidden-18dn0ox scallopplus-s30"
                        data-framer-appear-id="1v8hint"
                      >
                        <div
                          className="framer-fc3ds framer-1xbc31s framer-v-1xbc31s scallopplus-s31"
                          data-framer-name="Card"
                        >
                          <div className="framer-fitfpb">
                            <div className="framer-1s2032u">
                              <div
                                className="framer-kbmpoa"
                                data-framer-name="flags/united kingdom"
                              >
                                <div
                                  data-framer-background-image-wrapper="true"
                                  className="scallopplus-s28"
                                >
                                  <img
                                    decoding="async"
                                    loading="lazy"
                                    width="512"
                                    height="512"
                                    src="/framerusercontent.com/images/KaHLu8CnQchQtnbFW1eFbsck0Y.png"
                                    alt=""
                                    className="scallopplus-s29"
                                  />
                                </div>
                              </div>
                              <div
                                className="framer-y3j42p"
                                data-framer-name="Text"
                              >
                                <div
                                  className="framer-1n0a03k scallopplus-s49 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s50"
                                  >
                                    United States
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-xorhm4 scallopplus-s49 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s50"
                              >
                                30 GB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                      <div
                        className="framer-1v8hint-container hidden-18dn0ox scallopplus-s30"
                        data-framer-appear-id="1v8hint"
                      >
                        <div
                          className="framer-fc3ds framer-1xbc31s framer-v-1xbc31s scallopplus-s31"
                          data-framer-name="Card"
                        >
                          <div className="framer-fitfpb">
                            <div className="framer-1s2032u">
                              <div
                                className="framer-kbmpoa"
                                data-framer-name="flags/united kingdom"
                              >
                                <div
                                  data-framer-background-image-wrapper="true"
                                  className="scallopplus-s28"
                                >
                                  <img
                                    decoding="async"
                                    width="512"
                                    height="512"
                                    src="/framerusercontent.com/images/KaHLu8CnQchQtnbFW1eFbsck0Y.png"
                                    alt=""
                                    className="scallopplus-s29"
                                  />
                                </div>
                              </div>
                              <div
                                className="framer-y3j42p"
                                data-framer-name="Text"
                              >
                                <div
                                  className="framer-1n0a03k scallopplus-s49 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s50"
                                  >
                                    United States
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-xorhm4 scallopplus-s49 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s50"
                              >
                                30 GB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-otbxcl">
                      <div
                        className="framer-1dtph3j-container hidden-18dn0ox scallopplus-s30"
                        data-framer-appear-id="1dtph3j"
                      >
                        <div
                          className="framer-fc3ds framer-1xbc31s framer-v-olfq04 scallopplus-s31"
                          data-framer-name="Variant 2"
                        >
                          <div className="framer-fitfpb">
                            <div className="framer-1s2032u">
                              <div
                                className="framer-kbmpoa"
                                data-framer-name="flags/united kingdom"
                              >
                                <div
                                  data-framer-background-image-wrapper="true"
                                  className="scallopplus-s28"
                                >
                                  <img
                                    decoding="async"
                                    loading="lazy"
                                    width="512"
                                    height="512"
                                    src="/framerusercontent.com/images/KaHLu8CnQchQtnbFW1eFbsck0Y.png"
                                    alt=""
                                    className="scallopplus-s29"
                                  />
                                </div>
                                <div
                                  className="framer-fmnxi1 scallopplus-s32"
                                  data-framer-name="Countries"
                                >
                                  <div
                                    className="framer-yfzdbw"
                                    data-framer-name="Image"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="scallopplus-s28"
                                    >
                                      <img
                                        decoding="async"
                                        loading="lazy"
                                        width="263"
                                        height="160"
                                        src="/framerusercontent.com/images/WCcjvWELFL7CaDn4iiIcg8vo4.png"
                                        alt=""
                                        className="scallopplus-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div
                                className="framer-y3j42p"
                                data-framer-name="Text"
                              >
                                <div
                                  className="framer-1n0a03k scallopplus-s49 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s50"
                                  >
                                    HKD
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-xorhm4 scallopplus-s49 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s50"
                              >
                                Unlimited GB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                      <div
                        className="framer-1dtph3j-container hidden-18dn0ox scallopplus-s30"
                        data-framer-appear-id="1dtph3j"
                      >
                        <div
                          className="framer-fc3ds framer-1xbc31s framer-v-olfq04 scallopplus-s31"
                          data-framer-name="Variant 2"
                        >
                          <div className="framer-fitfpb">
                            <div className="framer-1s2032u">
                              <div
                                className="framer-kbmpoa"
                                data-framer-name="flags/united kingdom"
                              >
                                <div
                                  data-framer-background-image-wrapper="true"
                                  className="scallopplus-s28"
                                >
                                  <img
                                    decoding="async"
                                    width="512"
                                    height="512"
                                    src="/framerusercontent.com/images/KaHLu8CnQchQtnbFW1eFbsck0Y.png"
                                    alt=""
                                    className="scallopplus-s29"
                                  />
                                </div>
                                <div
                                  className="framer-fmnxi1 scallopplus-s32"
                                  data-framer-name="Countries"
                                >
                                  <div
                                    className="framer-yfzdbw"
                                    data-framer-name="Image"
                                  >
                                    <div
                                      data-framer-background-image-wrapper="true"
                                      className="scallopplus-s28"
                                    >
                                      <img
                                        decoding="async"
                                        width="263"
                                        height="160"
                                        src="/framerusercontent.com/images/WCcjvWELFL7CaDn4iiIcg8vo4.png"
                                        alt=""
                                        className="scallopplus-s29"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div
                                className="framer-y3j42p"
                                data-framer-name="Text"
                              >
                                <div
                                  className="framer-1n0a03k scallopplus-s49 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s50"
                                  >
                                    HKD
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div
                              className="framer-xorhm4 scallopplus-s49 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s50"
                              >
                                Unlimited GB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="framer-1wmfqu4"
                  data-framer-name="Serene Sky with Fluffy Clouds"
                >
                  <div
                    data-framer-background-image-wrapper="true"
                    className="scallopplus-s28"
                  >
                    <img
                      decoding="async"
                      width="2400"
                      height="1600"
                      sizes="(min-width: 1440px) 100vw, (min-width: 810px) and (max-width: 1439.98px) 100vw, (max-width: 809.98px) 100vw"
                      srcSet="/framerusercontent.com/images/Yx0Pz1GXYHNpQKHjvXJ1NjjNCaU.png 512w,/framerusercontent.com/images/Yx0Pz1GXYHNpQKHjvXJ1NjjNCaU.png 1024w,/framerusercontent.com/images/Yx0Pz1GXYHNpQKHjvXJ1NjjNCaU.png 2048w,/framerusercontent.com/images/Yx0Pz1GXYHNpQKHjvXJ1NjjNCaU.png 2400w"
                      src="/framerusercontent.com/images/Yx0Pz1GXYHNpQKHjvXJ1NjjNCaU.png"
                      alt=""
                      className="scallopplus-s29"
                    />
                  </div>
                </div>
              </div>
            </header>
            <div className="framer-1mmfzjg" data-framer-name="Container">
              <div className="framer-122ib8x" data-framer-name="Container">
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div
                    className="framer-5kkn7p scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s52">
                      Connectivity, built into your money
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                  <div
                    className="framer-5kkn7p scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s53">
                      Connectivity, built into your money
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                  <div
                    className="framer-5kkn7p scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s54">
                      Connectivity, built into your money
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div
                    className="framer-fv9g17 scallopplus-s55 reveal"
                    data-framer-name="More features, higher limits, and priority access "
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s56">
                      Buy, activate, and manage mobile data using the same
                      balance you use for everything else.
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1">
                  <div
                    className="framer-fv9g17 scallopplus-s51 reveal"
                    data-framer-name="More features, higher limits, and priority access "
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s26">
                      Buy, activate, and manage mobile data using the same
                      balance you use for everything else.
                    </p>
                  </div>
                </div>
              </div>
              <div className="framer-1l6p85c" data-framer-name="Cards">
                <div className="framer-1c6tw8w" data-framer-name="Cards">
                  <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                    <div className="framer-lou7pd-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s58"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              Pay With What You Hold
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Pay with USD, stablecoins, or any currency you
                              hold
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="607"
                              height="352"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png 512w,/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png 607w"
                              src="/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                    <div className="framer-lou7pd-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s63"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              Pay With What You Hold
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Pay with USD, stablecoins, or any currency you
                              hold
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="607"
                              height="352"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png 512w,/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png 607w"
                              src="/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                    <div className="framer-lou7pd-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s64"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              Pay With What You Hold
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Pay with USD, stablecoins, or any currency you
                              hold
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="607"
                              height="352"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png 512w,/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png 607w"
                              src="/framerusercontent.com/images/kzEGGmG92hzwcGIuFScjiBGYw.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                    <div className="framer-11xsq0p-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s58"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              One app for your global life
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Money, payments, cards, and travel services
                              working together in one experience.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="618"
                              height="360"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png 512w,/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png 618w"
                              src="/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                    <div className="framer-11xsq0p-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s63"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              One app for your global life
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Money, payments, cards, and travel services
                              working together in one experience.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="618"
                              height="360"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png 512w,/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png 618w"
                              src="/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                    <div className="framer-11xsq0p-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s64"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              One app for your global life
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Money, payments, cards, and travel services
                              working together in one experience.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="618"
                              height="360"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png 512w,/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png 618w"
                              src="/framerusercontent.com/images/inQNYpnVgcfyEBklCvWhKvARcw.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                    <div className="framer-pixh9q-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s58"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              Global data. No roaming fees.
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Stay connected locally without expensive roaming
                              charges.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="618"
                              height="360"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png 512w,/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png 618w"
                              src="/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                    <div className="framer-pixh9q-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s63"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              Global data. No roaming fees.
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Stay connected locally without expensive roaming
                              charges.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="618"
                              height="360"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png 512w,/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png 618w"
                              src="/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                    <div className="framer-pixh9q-container scallopplus-s57">
                      <div
                        className="framer-Ywpfc framer-1wz77zg framer-v-1wz77zg scallopplus-s64"
                        data-border="true"
                        data-framer-name="Layout 1"
                      >
                        <div
                          className="framer-8aco1i"
                          data-framer-name="Content"
                        >
                          <div
                            className="framer-j3rxv8 scallopplus-s59 reveal"
                            data-framer-name="Real-Time Analytics"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h4
                              dir="auto"
                              className="framer-text scallopplus-s60"
                            >
                              Global data. No roaming fees.
                            </h4>
                          </div>
                          <div
                            className="framer-gjjboo scallopplus-s61 reveal"
                            data-framer-name="Track your financial data as it happens with clean, intuitive dashboards."
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s62"
                            >
                              Stay connected locally without expensive roaming
                              charges.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-awcpao"
                          data-framer-name="Feature Image"
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              width="618"
                              height="360"
                              sizes="(min-width: 1440px) min(max((100vw - 272px) / 3, 1px) - 80px, 280px), (max-width: 809.98px) min(calc(100vw - 40px) - 48px, 280px), (min-width: 810px) and (max-width: 1439.98px) min(max((100vw - 112px) / 3, 1px) - 48px, 280px)"
                              srcSet="/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png 512w,/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png 618w"
                              src="/framerusercontent.com/images/8CuVG5YJJRcLhlFSItXmJsL0kQ.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <section
              className="framer-18t9s4v"
              data-framer-name="How it works section"
            >
              <div className="framer-1vio4e0" data-framer-name="BgBlur" />
              <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                <div className="framer-3tdt3b-container">
                  <div
                    className="framer-cO5Xn framer-e0qd9n framer-v-e0qd9n scallopplus-s65"
                    data-framer-name="Desktop 01"
                  >
                    <div className="framer-oiy9ek" data-framer-name="Left">
                      <div className="framer-bc6n4a">
                        <div
                          className="framer-ioyjrr"
                          data-framer-name="Heading and supporting text"
                        >
                          <div
                            className="framer-r2gnys scallopplus-s66 reveal"
                            data-framer-name="Heading"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h2
                              dir="auto"
                              className="framer-text scallopplus-s67"
                            >
                              Coverage for every kind of trip
                            </h2>
                          </div>
                          <div
                            className="framer-b414sw scallopplus-s68 reveal"
                            data-framer-name="Supporting text"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s69"
                            >
                              Pick local, regional, or global data whichever
                              fits where you&apos;re going.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-1hmsn9z"
                          data-framer-name="Icon Box List"
                        >
                          <div className="framer-1it8w7i-container">
                            <div
                              className={coverageIndex === 0 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 0 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(0)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 0 ? 'framer-wwXki framer-1mag844 scallopplus-s72' : 'framer-wwXki framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#941805552" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 0 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 0 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Search Destinations
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="framer-1lzdxv8-container">
                            <div
                              className={coverageIndex === 1 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 1 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(1)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 1 ? 'framer-drpen framer-1mag844 scallopplus-s72' : 'framer-drpen framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#4090123071" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 1 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 1 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Select Plan
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="framer-1dsbxes-container">
                            <div
                              className={coverageIndex === 2 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 2 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(2)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 2 ? 'framer-30Bgq framer-1mag844 scallopplus-s72' : 'framer-30Bgq framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#2883338743" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 2 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 2 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Activate Plan
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="framer-12i6w1o scallopplus-s80"
                      data-border="true"
                      data-framer-name="Right"
                    >
                      <div className="framer-1c7mm5i" data-framer-name="Images">
                        <div
                          className="framer-1oza88n"
                          data-framer-name="Image 1"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 0 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 0 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-1ll1gcx scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/jUjTVWPMacgEv8B56DsF2i3Yyk.png 499w"
                                src="/framerusercontent.com/images/jUjTVWPMacgEv8B56DsF2i3Yyk.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-96xnf4"
                          data-framer-name="Image 2"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 1 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 1 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-1gbd5ma scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/s5hg7hJxR9f0AqhqxQC7Dl7PpR4.png 499w"
                                src="/framerusercontent.com/images/s5hg7hJxR9f0AqhqxQC7Dl7PpR4.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-1ahq1aj"
                          data-framer-name="Image 3"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 2 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 2 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-19xqtvz scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/nnEfdB6KD80LlrkexvfZ7Tcfo.png 499w"
                                src="/framerusercontent.com/images/nnEfdB6KD80LlrkexvfZ7Tcfo.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1oz04k scallopplus-s82"
                        data-framer-name="BgBlur"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                <div className="framer-3tdt3b-container">
                  <div
                    className="framer-cO5Xn framer-e0qd9n framer-v-1tzsv5k scallopplus-s65"
                    data-framer-name="Mobile 01"
                  >
                    <div className="framer-oiy9ek" data-framer-name="Left">
                      <div className="framer-bc6n4a">
                        <div
                          className="framer-ioyjrr"
                          data-framer-name="Heading and supporting text"
                        >
                          <div
                            className="framer-r2gnys scallopplus-s66 reveal"
                            data-framer-name="Heading"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h2
                              dir="auto"
                              className="framer-text scallopplus-s83"
                            >
                              Coverage for every kind of trip
                            </h2>
                          </div>
                          <div
                            className="framer-b414sw scallopplus-s68 reveal"
                            data-framer-name="Supporting text"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s84"
                            >
                              Pick local, regional, or global data whichever
                              fits where you&apos;re going.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-1hmsn9z"
                          data-framer-name="Icon Box List"
                        >
                          <div className="framer-1it8w7i-container scallopplus-s85">
                            <div
                              className={coverageIndex === 0 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 0 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(0)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 0 ? 'framer-wwXki framer-1mag844 scallopplus-s72' : 'framer-wwXki framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#941805552" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 0 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 0 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Search Destinations
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="framer-1lzdxv8-container scallopplus-s85">
                            <div
                              className={coverageIndex === 1 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 1 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(1)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 1 ? 'framer-drpen framer-1mag844 scallopplus-s72' : 'framer-drpen framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#4090123071" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 1 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 1 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Select Plan
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="framer-1dsbxes-container scallopplus-s85">
                            <div
                              className={coverageIndex === 2 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 2 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(2)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 2 ? 'framer-30Bgq framer-1mag844 scallopplus-s72' : 'framer-30Bgq framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#2883338743" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 2 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 2 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Activate Plan
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="framer-12i6w1o scallopplus-s88"
                      data-border="true"
                      data-framer-name="Right"
                    >
                      <div className="framer-1c7mm5i" data-framer-name="Images">
                        <div
                          className="framer-1oza88n"
                          data-framer-name="Image 1"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 0 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 0 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-1ll1gcx scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/jUjTVWPMacgEv8B56DsF2i3Yyk.png 499w"
                                src="/framerusercontent.com/images/jUjTVWPMacgEv8B56DsF2i3Yyk.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-96xnf4"
                          data-framer-name="Image 2"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 1 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 1 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-1gbd5ma scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/s5hg7hJxR9f0AqhqxQC7Dl7PpR4.png 499w"
                                src="/framerusercontent.com/images/s5hg7hJxR9f0AqhqxQC7Dl7PpR4.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-1ahq1aj"
                          data-framer-name="Image 3"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 2 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 2 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-19xqtvz scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/nnEfdB6KD80LlrkexvfZ7Tcfo.png 499w"
                                src="/framerusercontent.com/images/nnEfdB6KD80LlrkexvfZ7Tcfo.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1oz04k scallopplus-s82"
                        data-framer-name="BgBlur"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                <div className="framer-3tdt3b-container">
                  <div
                    className="framer-cO5Xn framer-e0qd9n framer-v-bb2x3d scallopplus-s65"
                    data-framer-name="Tablet 01"
                  >
                    <div className="framer-oiy9ek" data-framer-name="Left">
                      <div className="framer-bc6n4a">
                        <div
                          className="framer-ioyjrr"
                          data-framer-name="Heading and supporting text"
                        >
                          <div
                            className="framer-r2gnys scallopplus-s66 reveal"
                            data-framer-name="Heading"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h2
                              dir="auto"
                              className="framer-text scallopplus-s89"
                            >
                              Coverage for every kind of trip
                            </h2>
                          </div>
                          <div
                            className="framer-b414sw scallopplus-s68 reveal"
                            data-framer-name="Supporting text"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s69"
                            >
                              Pick local, regional, or global data whichever
                              fits where you&apos;re going.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-1hmsn9z"
                          data-framer-name="Icon Box List"
                        >
                          <div className="framer-1it8w7i-container">
                            <div
                              className={coverageIndex === 0 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 0 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(0)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 0 ? 'framer-wwXki framer-1mag844 scallopplus-s72' : 'framer-wwXki framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#941805552" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 0 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 0 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Search Destinations
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="framer-1lzdxv8-container">
                            <div
                              className={coverageIndex === 1 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 1 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(1)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 1 ? 'framer-drpen framer-1mag844 scallopplus-s72' : 'framer-drpen framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#4090123071" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 1 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 1 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Select Plan
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="framer-1dsbxes-container">
                            <div
                              className={coverageIndex === 2 ? 'framer-GOdgB framer-1odpd79 framer-v-46wece scallopplus-s70' : 'framer-GOdgB framer-1odpd79 framer-v-16qccmr scallopplus-s75'}
                              data-border="true"
                              data-framer-name={coverageIndex === 2 ? 'Selected' : 'Light'}
                              data-highlight="true"
                              tabIndex={0}
                              role="button"
                              onClick={() => setCoverageIndex(2)}
                            >
                              <div
                                className="framer-bn4x3c scallopplus-s71"
                                data-framer-name="Icon Bg"
                              >
                                <svg
                                  className={coverageIndex === 2 ? 'framer-30Bgq framer-1mag844 scallopplus-s72' : 'framer-30Bgq framer-1mag844 scallopplus-s76'}
                                  role="presentation"
                                  viewBox="0 0 24 24"
                                >
                                  <use href="#2883338743" />
                                </svg>
                              </div>
                              <div
                                className="framer-hcwan2"
                                data-framer-name="Text Wrapper"
                              >
                                <div
                                  className={coverageIndex === 2 ? 'framer-a6ee5t scallopplus-s73' : 'framer-a6ee5t scallopplus-s77'}
                                  data-framer-name="Experience the future of eCommerce."
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <h5
                                    dir="auto"
                                    className={coverageIndex === 2 ? 'framer-text scallopplus-s74' : 'framer-text scallopplus-s78'}
                                  >
                                    Activate Plan
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="framer-12i6w1o scallopplus-s92"
                      data-border="true"
                      data-framer-name="Right"
                    >
                      <div className="framer-1c7mm5i" data-framer-name="Images">
                        <div
                          className="framer-1oza88n"
                          data-framer-name="Image 1"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 0 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 0 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-1ll1gcx scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/jUjTVWPMacgEv8B56DsF2i3Yyk.png 499w"
                                src="/framerusercontent.com/images/jUjTVWPMacgEv8B56DsF2i3Yyk.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-96xnf4"
                          data-framer-name="Image 2"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 1 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 1 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-1gbd5ma scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/s5hg7hJxR9f0AqhqxQC7Dl7PpR4.png 499w"
                                src="/framerusercontent.com/images/s5hg7hJxR9f0AqhqxQC7Dl7PpR4.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className="framer-1ahq1aj"
                          data-framer-name="Image 3"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            opacity: coverageIndex === 2 ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: coverageIndex === 2 ? 'auto' : 'none',
                          }}
                        >
                          <div className="framer-19xqtvz scallopplus-s81">
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                loading="lazy"
                                width="499"
                                height="1024"
                                sizes="(min-width: 1440px) max(433 * 0.4965, 1px), (max-width: 809.98px) 164px, (min-width: 810px) and (max-width: 1439.98px) max(401 * 0.4965, 1px)"
                                srcSet="/framerusercontent.com/images/nnEfdB6KD80LlrkexvfZ7Tcfo.png 499w"
                                src="/framerusercontent.com/images/nnEfdB6KD80LlrkexvfZ7Tcfo.png"
                                alt=""
                                className="scallopplus-s47"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1oz04k scallopplus-s82"
                        data-framer-name="BgBlur"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <div className="framer-oc1zat" data-framer-name="Container">
              <div className="framer-1ikpw3c" data-framer-name="Container">
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div
                    className="framer-b9b5s7 scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s93">
                      SET UP YOUR eSIM IN MINUTES
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                  <div
                    className="framer-b9b5s7 scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s94">
                      SET UP YOUR eSIM IN MINUTES
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                  <div
                    className="framer-b9b5s7 scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s95">
                      SET UP YOUR eSIM IN MINUTES
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div
                    className="framer-19k5nz8 scallopplus-s55 reveal"
                    data-framer-name="More features, higher limits, and priority access "
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s56">
                      Install and activate directly from the app. Most users are
                      online in under two minutes.
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1">
                  <div
                    className="framer-19k5nz8 scallopplus-s51 reveal"
                    data-framer-name="More features, higher limits, and priority access "
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s26">
                      Install and activate directly from the app. Most users are
                      online in under two minutes.
                    </p>
                  </div>
                </div>
              </div>
              <div className="framer-y6keb8" data-framer-name="Cards">
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div className="framer-1t4iu37-container">
                    <div
                      className="framer-ru5pW framer-vmt6pk framer-v-vmt6pk scallopplus-s96"
                      data-framer-name="Desktop 1"
                    >
                      <div
                        className="framer-1p1vpc0 scallopplus-s97"
                        data-border="true"
                        data-framer-name="Buttons Wrapper"
                      >
                        <div
                          className="framer-pi6avg"
                          data-framer-name="Button 1"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(0)}
                        >
                          <div
                            className="framer-1y73rv9 scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              01
                            </p>
                          </div>
                          <div
                            className="framer-1su7mqt scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Compatibility
                            </p>
                          </div>
                          <div className="framer-ksfu6j-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-ryphs0"
                          data-framer-name="Button 2"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(1)}
                        >
                          <div
                            className="framer-1ockb9m scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              02
                            </p>
                          </div>
                          <div
                            className="framer-1d9eafa scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Install
                            </p>
                          </div>
                          <div className="framer-j92owh-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-oh9eip"
                          data-framer-name="Button 3"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(2)}
                        >
                          <div
                            className="framer-2vmmoc scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              03
                            </p>
                          </div>
                          <div
                            className="framer-dx8dna scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Activate
                            </p>
                          </div>
                          <div className="framer-n0ge6d-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-elprcb scallopplus-s103"
                          data-framer-name="Sliding Frame"
                          style={{
                            left: `calc(${esimStepIndex * 33.33}% + 8px)`,
                            transition: 'left 0.3s ease',
                          }}
                        />
                      </div>
                      <div
                        className="framer-1r5y29k scallopplus-s104"
                        data-framer-name="Content Wrapper"
                      >
                        <div
                          className="framer-165wt3r"
                          data-framer-name="Text Wrapper"
                        >
                          <div
                            className="framer-1t95eb scallopplus-s105 reveal"
                            data-framer-name="Heading 3"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h3
                              dir="auto"
                              className="framer-text scallopplus-s106"
                            >
                              {esimSteps[esimStepIndex].number}
                            </h3>
                          </div>
                          <div
                            className="framer-y7j3z6 scallopplus-s107 reveal"
                            data-framer-name="Heading 3"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h3
                              dir="auto"
                              className="framer-text scallopplus-s108"
                            >
                              {esimSteps[esimStepIndex].label}
                            </h3>
                          </div>
                          <div
                            className="framer-1o8bffc scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s109"
                            >
                              {esimSteps[esimStepIndex].body}
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-x32t1c"
                          data-framer-name="Image Wrapper"
                        >
                          <div
                            className="framer-77z0b2 scallopplus-s110"
                            data-framer-name="Image 1 (Replace and set size here)"
                          >
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                width="1131"
                                height="691"
                                sizes="(min-width: 1440px) max(((1000px - 96px) - 48px) / 1.5, 1px), (max-width: 809.98px) calc(335px - 48px), (min-width: 810px) and (max-width: 1439.98px) max(((max(100vw - 48px, 1px) - 72px) - 36px) / 1.5, 1px)"
                                src={esimSteps[esimStepIndex].image}
                                alt=""
                                className="scallopplus-s29"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                  <div className="framer-1t4iu37-container">
                    <div
                      className="framer-ru5pW framer-vmt6pk framer-v-12gx6l8 scallopplus-s96"
                      data-framer-name="Mobile 1"
                    >
                      <div
                        className="framer-1p1vpc0 scallopplus-s97"
                        data-border="true"
                        data-framer-name="Buttons Wrapper"
                      >
                        <div
                          className="framer-pi6avg"
                          data-framer-name="Button 1"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(0)}
                        >
                          <div
                            className="framer-1y73rv9 scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              01
                            </p>
                          </div>
                          <div
                            className="framer-1su7mqt scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Compatibility
                            </p>
                          </div>
                          <div className="framer-ksfu6j-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-ryphs0"
                          data-framer-name="Button 2"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(1)}
                        >
                          <div
                            className="framer-1ockb9m scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              02
                            </p>
                          </div>
                          <div
                            className="framer-1d9eafa scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Install
                            </p>
                          </div>
                          <div className="framer-j92owh-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-oh9eip"
                          data-framer-name="Button 3"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(2)}
                        >
                          <div
                            className="framer-2vmmoc scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              03
                            </p>
                          </div>
                          <div
                            className="framer-dx8dna scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Activate
                            </p>
                          </div>
                          <div className="framer-n0ge6d-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-elprcb scallopplus-s103"
                          data-framer-name="Sliding Frame"
                          style={{
                            top: `calc(${16.67 + esimStepIndex * 33.33}% - 33.5px)`,
                            transition: 'top 0.3s ease',
                          }}
                        />
                      </div>
                      <div
                        className="framer-1r5y29k scallopplus-s111"
                        data-framer-name="Content Wrapper"
                      >
                        <div
                          className="framer-165wt3r"
                          data-framer-name="Text Wrapper"
                        >
                          <div
                            className="framer-1t95eb scallopplus-s105 reveal"
                            data-framer-name="Heading 3"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h3
                              dir="auto"
                              className="framer-text scallopplus-s106"
                            >
                              {esimSteps[esimStepIndex].number}
                            </h3>
                          </div>
                          <div
                            className="framer-y7j3z6 scallopplus-s107 reveal"
                            data-framer-name="Heading 3"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h3
                              dir="auto"
                              className="framer-text scallopplus-s108"
                            >
                              {esimSteps[esimStepIndex].label}
                            </h3>
                          </div>
                          <div
                            className="framer-1o8bffc scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s109"
                            >
                              {esimSteps[esimStepIndex].body}
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-x32t1c"
                          data-framer-name="Image Wrapper"
                        >
                          <div
                            className="framer-77z0b2 scallopplus-s110"
                            data-framer-name="Image 1 (Replace and set size here)"
                          >
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                width="1131"
                                height="691"
                                sizes="(min-width: 1440px) max(((1000px - 96px) - 48px) / 1.5, 1px), (max-width: 809.98px) calc(335px - 48px), (min-width: 810px) and (max-width: 1439.98px) max(((max(100vw - 48px, 1px) - 72px) - 36px) / 1.5, 1px)"
                                src={esimSteps[esimStepIndex].image}
                                alt=""
                                className="scallopplus-s29"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                  <div className="framer-1t4iu37-container">
                    <div
                      className="framer-ru5pW framer-vmt6pk framer-v-vmt6pk scallopplus-s96"
                      data-framer-name="Desktop 1"
                    >
                      <div
                        className="framer-1p1vpc0 scallopplus-s97"
                        data-border="true"
                        data-framer-name="Buttons Wrapper"
                      >
                        <div
                          className="framer-pi6avg"
                          data-framer-name="Button 1"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(0)}
                        >
                          <div
                            className="framer-1y73rv9 scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              01
                            </p>
                          </div>
                          <div
                            className="framer-1su7mqt scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Compatibility
                            </p>
                          </div>
                          <div className="framer-ksfu6j-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-ryphs0"
                          data-framer-name="Button 2"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(1)}
                        >
                          <div
                            className="framer-1ockb9m scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              02
                            </p>
                          </div>
                          <div
                            className="framer-1d9eafa scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Install
                            </p>
                          </div>
                          <div className="framer-j92owh-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-oh9eip"
                          data-framer-name="Button 3"
                          data-highlight="true"
                          tabIndex={0}
                          role="button"
                          onClick={() => setEsimStepIndex(2)}
                        >
                          <div
                            className="framer-2vmmoc scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s99"
                            >
                              03
                            </p>
                          </div>
                          <div
                            className="framer-dx8dna scallopplus-s100 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s101"
                            >
                              Activate
                            </p>
                          </div>
                          <div className="framer-n0ge6d-container">
                            {/*$*/}
                            <div className="scallopplus-s102" />
                            {/*/$*/}
                          </div>
                        </div>
                        <div
                          className="framer-elprcb scallopplus-s103"
                          data-framer-name="Sliding Frame"
                          style={{
                            left: `calc(${esimStepIndex * 33.33}% + 8px)`,
                            transition: 'left 0.3s ease',
                          }}
                        />
                      </div>
                      <div
                        className="framer-1r5y29k scallopplus-s112"
                        data-framer-name="Content Wrapper"
                      >
                        <div
                          className="framer-165wt3r"
                          data-framer-name="Text Wrapper"
                        >
                          <div
                            className="framer-1t95eb scallopplus-s105 reveal"
                            data-framer-name="Heading 3"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h3
                              dir="auto"
                              className="framer-text scallopplus-s106"
                            >
                              {esimSteps[esimStepIndex].number}
                            </h3>
                          </div>
                          <div
                            className="framer-y7j3z6 scallopplus-s107 reveal"
                            data-framer-name="Heading 3"
                            data-framer-component-type="RichTextContainer"
                          >
                            <h3
                              dir="auto"
                              className="framer-text scallopplus-s108"
                            >
                              {esimSteps[esimStepIndex].label}
                            </h3>
                          </div>
                          <div
                            className="framer-1o8bffc scallopplus-s98 reveal"
                            data-framer-name="Body Large"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s109"
                            >
                              {esimSteps[esimStepIndex].body}
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-x32t1c"
                          data-framer-name="Image Wrapper"
                        >
                          <div
                            className="framer-77z0b2 scallopplus-s110"
                            data-framer-name="Image 1 (Replace and set size here)"
                          >
                            <div
                              data-framer-background-image-wrapper="true"
                              className="scallopplus-s28"
                            >
                              <img
                                decoding="async"
                                width="1131"
                                height="691"
                                sizes="(min-width: 1440px) max(((1000px - 96px) - 48px) / 1.5, 1px), (max-width: 809.98px) calc(335px - 48px), (min-width: 810px) and (max-width: 1439.98px) max(((max(100vw - 48px, 1px) - 72px) - 36px) / 1.5, 1px)"
                                src={esimSteps[esimStepIndex].image}
                                alt=""
                                className="scallopplus-s29"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <section
              className="framer-1dbs8gj"
              data-framer-name="AI Suggestions"
              id="smart-assist"
            >
              <div className="framer-1nocfmp" data-framer-name="Container">
                <div className="framer-hiehjt" data-framer-name="Content">
                  <div className="framer-13otxq9" data-framer-name="Left">
                    <div className="framer-wa8h12" data-framer-name="Top">
                      <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                        <div
                          className="framer-em1106 scallopplus-s51 reveal"
                          data-framer-name="Choose how you use your account"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s113"
                          >
                            Everything managed directly in the app
                          </p>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                        <div
                          className="framer-em1106 scallopplus-s51 reveal"
                          data-framer-name="Choose how you use your account"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s114"
                          >
                            Everything managed directly in the app
                          </p>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                        <div
                          className="framer-em1106 scallopplus-s51 reveal"
                          data-framer-name="Choose how you use your account"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s115"
                          >
                            Everything managed directly in the app
                          </p>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                        <div
                          className="framer-nv27dn scallopplus-s55 reveal"
                          data-framer-name="More features, higher limits, and priority access "
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s116"
                          >
                            Monitor usage, top up plans, and manage all your
                            active eSIMs in real time.
                          </p>
                        </div>
                      </div>
                      <div className="ssr-variant hidden-1kt7ub1">
                        <div
                          className="framer-nv27dn scallopplus-s51 reveal"
                          data-framer-name="More features, higher limits, and priority access "
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s117"
                          >
                            Monitor usage, top up plans, and manage all your
                            active eSIMs in real time.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="framer-38fpwk" data-framer-name="Right">
                    <div className="framer-1a69ufk" data-framer-name="Vectors">
                      <div
                        className="framer-pg11gr hidden-otbxcl hidden-18dn0ox scallopplus-s118"
                        data-framer-name="vector 01"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="409"
                            height="206"
                            src="/framerusercontent.com/images/sxUIuRqUUr5anBnrGt01Qq0tU.png"
                            alt="Phone"
                            className="scallopplus-s47"
                          />
                        </div>
                      </div>
                      <div
                        className="framer-1vd3m6o hidden-otbxcl hidden-18dn0ox scallopplus-s118"
                        data-framer-name="Vector 02"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="409"
                            height="206"
                            src="/framerusercontent.com/images/BGWjjencX60EYlCwSEDyuAmKFE.png"
                            alt="Phone"
                            className="scallopplus-s47"
                          />
                        </div>
                      </div>
                      <div
                        className="framer-1kwqf05"
                        data-framer-name="Vector 04"
                      />
                    </div>
                    <div className="ssr-variant">
                      <div
                        className="framer-dw9ega scallopplus-s119 reveal"
                        data-framer-name="Phone"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="499"
                            height="1024"
                            sizes="(min-width: 1440px) 350px, (max-width: 809.98px) 240px, (min-width: 810px) and (max-width: 1439.98px) 300px"
                            srcSet="/framerusercontent.com/images/1J0OmHdNPGvYhMGazVmN8D01gQU.png 499w"
                            src="/framerusercontent.com/images/1J0OmHdNPGvYhMGazVmN8D01gQU.png"
                            alt="Phone"
                            className="scallopplus-s47"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="framer-7bhew3" data-framer-name="Bottom">
                    <div className="ssr-variant hidden-18dn0ox">
                      <div className="framer-1jd013u-container">
                        <div
                          className="framer-9gV63 framer-14xn65z framer-v-14xn65z scallopplus-s120"
                          data-framer-name="Default"
                        >
                          <div
                            className="framer-1m6wz0r scallopplus-s121"
                            data-framer-name="Icon"
                          >
                            <div className="framer-xo5wx6">
                              <div
                                data-framer-background-image-wrapper="true"
                                className="scallopplus-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="120"
                                  height="120"
                                  src="/framerusercontent.com/images/51NUYsvOBkkOQ29PapA2qSALc.png"
                                  alt="Icon"
                                  className="scallopplus-s47"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-xdnosl"
                            data-framer-name="Empty Space"
                          />
                          <div
                            className="framer-8c3bys"
                            data-framer-name="Bottom"
                          >
                            <div
                              className="framer-l2hzh5 scallopplus-s122 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h2
                                dir="auto"
                                className="framer-text scallopplus-s123"
                              >
                                View remaining and total data usage
                              </h2>
                            </div>
                            <div
                              className="framer-6f4pb1 scallopplus-s124 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s125"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                      <div className="framer-1jd013u-container">
                        <div
                          className="framer-9gV63 framer-14xn65z framer-v-1hfhjey scallopplus-s120"
                          data-framer-name="Default - Phone"
                        >
                          <div
                            className="framer-1m6wz0r scallopplus-s121"
                            data-framer-name="Icon"
                          >
                            <div className="framer-xo5wx6">
                              <div
                                data-framer-background-image-wrapper="true"
                                className="scallopplus-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="120"
                                  height="120"
                                  src="/framerusercontent.com/images/51NUYsvOBkkOQ29PapA2qSALc.png"
                                  alt="Icon"
                                  className="scallopplus-s47"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-xdnosl"
                            data-framer-name="Empty Space"
                          />
                          <div
                            className="framer-8c3bys"
                            data-framer-name="Bottom"
                          >
                            <div
                              className="framer-l2hzh5 scallopplus-s122 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h2
                                dir="auto"
                                className="framer-text scallopplus-s123"
                              >
                                View remaining and total data usage
                              </h2>
                            </div>
                            <div
                              className="framer-6f4pb1 scallopplus-s124 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s125"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-18dn0ox">
                      <div className="framer-tf3sju-container">
                        <div
                          className="framer-9gV63 framer-14xn65z framer-v-14xn65z scallopplus-s120"
                          data-framer-name="Default"
                        >
                          <div
                            className="framer-1m6wz0r scallopplus-s126"
                            data-framer-name="Icon"
                          >
                            <div className="framer-xo5wx6">
                              <div
                                data-framer-background-image-wrapper="true"
                                className="scallopplus-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="120"
                                  height="120"
                                  src="/framerusercontent.com/images/ddo8Z3bVp3wvEXiUGDVTrO6vwJc.png"
                                  alt="Icon"
                                  className="scallopplus-s47"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-xdnosl"
                            data-framer-name="Empty Space"
                          />
                          <div
                            className="framer-8c3bys"
                            data-framer-name="Bottom"
                          >
                            <div
                              className="framer-l2hzh5 scallopplus-s122 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h2
                                dir="auto"
                                className="framer-text scallopplus-s123"
                              >
                                Top up active or expired plans anytime
                              </h2>
                            </div>
                            <div
                              className="framer-6f4pb1 scallopplus-s124 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s125"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                      <div className="framer-tf3sju-container">
                        <div
                          className="framer-9gV63 framer-14xn65z framer-v-1hfhjey scallopplus-s120"
                          data-framer-name="Default - Phone"
                        >
                          <div
                            className="framer-1m6wz0r scallopplus-s126"
                            data-framer-name="Icon"
                          >
                            <div className="framer-xo5wx6">
                              <div
                                data-framer-background-image-wrapper="true"
                                className="scallopplus-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="120"
                                  height="120"
                                  src="/framerusercontent.com/images/ddo8Z3bVp3wvEXiUGDVTrO6vwJc.png"
                                  alt="Icon"
                                  className="scallopplus-s47"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-xdnosl"
                            data-framer-name="Empty Space"
                          />
                          <div
                            className="framer-8c3bys"
                            data-framer-name="Bottom"
                          >
                            <div
                              className="framer-l2hzh5 scallopplus-s122 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h2
                                dir="auto"
                                className="framer-text scallopplus-s123"
                              >
                                Top up active or expired plans anytime
                              </h2>
                            </div>
                            <div
                              className="framer-6f4pb1 scallopplus-s124 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s125"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-18dn0ox">
                      <div className="framer-ckctf-container">
                        <div
                          className="framer-9gV63 framer-14xn65z framer-v-14xn65z scallopplus-s120"
                          data-framer-name="Default"
                        >
                          <div
                            className="framer-1m6wz0r scallopplus-s127"
                            data-framer-name="Icon"
                          >
                            <div className="framer-xo5wx6">
                              <div
                                data-framer-background-image-wrapper="true"
                                className="scallopplus-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="120"
                                  height="120"
                                  src="/framerusercontent.com/images/C2mpTMVNWL5hjzRznGzfUxWiKsY.png"
                                  alt="Icon"
                                  className="scallopplus-s47"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-xdnosl"
                            data-framer-name="Empty Space"
                          />
                          <div
                            className="framer-8c3bys"
                            data-framer-name="Bottom"
                          >
                            <div
                              className="framer-l2hzh5 scallopplus-s122 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h2
                                dir="auto"
                                className="framer-text scallopplus-s123"
                              >
                                Manage multiple eSIMs from one place
                              </h2>
                            </div>
                            <div
                              className="framer-6f4pb1 scallopplus-s124 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s125"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                      <div className="framer-ckctf-container">
                        <div
                          className="framer-9gV63 framer-14xn65z framer-v-1hfhjey scallopplus-s120"
                          data-framer-name="Default - Phone"
                        >
                          <div
                            className="framer-1m6wz0r scallopplus-s127"
                            data-framer-name="Icon"
                          >
                            <div className="framer-xo5wx6">
                              <div
                                data-framer-background-image-wrapper="true"
                                className="scallopplus-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="120"
                                  height="120"
                                  src="/framerusercontent.com/images/C2mpTMVNWL5hjzRznGzfUxWiKsY.png"
                                  alt="Icon"
                                  className="scallopplus-s47"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-xdnosl"
                            data-framer-name="Empty Space"
                          />
                          <div
                            className="framer-8c3bys"
                            data-framer-name="Bottom"
                          >
                            <div
                              className="framer-l2hzh5 scallopplus-s122 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h2
                                dir="auto"
                                className="framer-text scallopplus-s123"
                              >
                                Manage multiple eSIMs from one place
                              </h2>
                            </div>
                            <div
                              className="framer-6f4pb1 scallopplus-s124 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s125"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-18dn0ox">
                      <div className="framer-1srgmsd-container">
                        <div
                          className="framer-9gV63 framer-14xn65z framer-v-14xn65z scallopplus-s128"
                          data-framer-name="Default"
                        >
                          <div
                            className="framer-1m6wz0r scallopplus-s129"
                            data-framer-name="Icon"
                          >
                            <div className="framer-xo5wx6">
                              <div
                                data-framer-background-image-wrapper="true"
                                className="scallopplus-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="120"
                                  height="120"
                                  src="/framerusercontent.com/images/oitI5GbQNFAgzNb8zSHZCZj0pI.png"
                                  alt="Icon"
                                  className="scallopplus-s47"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-xdnosl"
                            data-framer-name="Empty Space"
                          />
                          <div
                            className="framer-8c3bys"
                            data-framer-name="Bottom"
                          >
                            <div
                              className="framer-l2hzh5 scallopplus-s122 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h2
                                dir="auto"
                                className="framer-text scallopplus-s123"
                              >
                                Extend data and validity instantly
                              </h2>
                            </div>
                            <div
                              className="framer-6f4pb1 scallopplus-s124 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s125"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                      <div className="framer-1srgmsd-container">
                        <div
                          className="framer-9gV63 framer-14xn65z framer-v-1hfhjey scallopplus-s128"
                          data-framer-name="Default - Phone"
                        >
                          <div
                            className="framer-1m6wz0r scallopplus-s129"
                            data-framer-name="Icon"
                          >
                            <div className="framer-xo5wx6">
                              <div
                                data-framer-background-image-wrapper="true"
                                className="scallopplus-s28"
                              >
                                <img
                                  decoding="async"
                                  loading="lazy"
                                  width="120"
                                  height="120"
                                  src="/framerusercontent.com/images/oitI5GbQNFAgzNb8zSHZCZj0pI.png"
                                  alt="Icon"
                                  className="scallopplus-s47"
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="framer-xdnosl"
                            data-framer-name="Empty Space"
                          />
                          <div
                            className="framer-8c3bys"
                            data-framer-name="Bottom"
                          >
                            <div
                              className="framer-l2hzh5 scallopplus-s122 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h2
                                dir="auto"
                                className="framer-text scallopplus-s123"
                              >
                                Extend data and validity instantly
                              </h2>
                            </div>
                            <div
                              className="framer-6f4pb1 scallopplus-s124 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s125"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section
              className="framer-592f1h"
              data-framer-name="Bento Cards Section"
              id="bento-cards"
            >
              <div className="framer-nrft9p" data-framer-name="Container">
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div
                    className="framer-vxlt65 scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s52">
                      Quick tips
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                  <div
                    className="framer-vxlt65 scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s53">
                      Quick tips
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                  <div
                    className="framer-vxlt65 scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s54">
                      Quick tips
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div
                    className="framer-7rsgre scallopplus-s55 reveal"
                    data-framer-name="More features, higher limits, and priority access "
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s56">
                      Helpful information to get connected faster and make the
                      most of your eSIM.
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1">
                  <div
                    className="framer-7rsgre scallopplus-s51 reveal"
                    data-framer-name="More features, higher limits, and priority access "
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s26">
                      Helpful information to get connected faster and make the
                      most of your eSIM.
                    </p>
                  </div>
                </div>
              </div>
              <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                <section
                  className="framer-cs6r2t"
                  data-framer-name="Content \xB7 14"
                >
                  <div className="framer-763jom" data-framer-name="Container">
                    <div
                      className="framer-m2jsxw"
                      data-framer-name="Cards Wrapper"
                    >
                      <div
                        className="framer-mfd6wh"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-h658se"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-dx3rsk-container hidden-otbxcl hidden-18dn0ox">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1v8frsn"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1h612el scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s131"
                              >
                                Your eSIM stays ready
                              </h6>
                            </div>
                            <div
                              className="framer-117j2ki scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s132"
                              >
                                Your eSIM stays installed on your device even
                                after a plan expires. You&apos;ll see the
                                network, but data pauses until you top up or
                                activate a new plan - so reconnecting takes
                                seconds, not setup.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-15j1f7t"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-n9v0xb"
                          data-framer-name="Content Wrapper"
                        >
                          <div
                            className="framer-1bamk9e"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1r0zlz6 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Top up anytime
                              </h6>
                            </div>
                            <div
                              className="framer-ca8bvh scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Out of data? Reactivate connectivity instantly
                                by topping up your existing eSIM or adding a new
                                plan - all from the app, in seconds.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-15f50j9"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="1766"
                            height="1182"
                            sizes="(min-width: 1440px) calc(max((min(100vw - 224px, 1280px) - 72px) / 4, 10px) * 2 + 24px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 512w,/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 1024w,/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 1766w"
                            src="/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-qpj78o-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s136" />
                            <div className="scallopplus-s137" />
                            <div className="scallopplus-s138" />
                            <div className="scallopplus-s139" />
                            <div className="scallopplus-s140" />
                            <div className="scallopplus-s141" />
                            <div className="scallopplus-s142" />
                            <div className="scallopplus-s143" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-1x80cus"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-131lf0p-container hidden-1kt7ub1 hidden-otbxcl">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-pl0jsx"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1h9lu61 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Use any balance to pay
                              </h6>
                            </div>
                            <div
                              className="framer-o23yct scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Purchase eSIM plans using the funds already
                                available in your Scallop account, whether fiat,
                                stablecoins, or digital assets.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1nbpz68"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="705"
                            height="470"
                            sizes="(min-width: 1440px) calc(max((min(100vw - 224px, 1280px) - 72px) / 4, 10px) * 2 + 24px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png 512w,/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png 705w"
                            src="/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-57fbr-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s136" />
                            <div className="scallopplus-s137" />
                            <div className="scallopplus-s138" />
                            <div className="scallopplus-s139" />
                            <div className="scallopplus-s140" />
                            <div className="scallopplus-s141" />
                            <div className="scallopplus-s142" />
                            <div className="scallopplus-s143" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-486ytg"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-m0blom-container hidden-1kt7ub1 hidden-otbxcl">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1kd1l"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-jwsmoj scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Ready before you land
                              </h6>
                            </div>
                            <div
                              className="framer-1cj3xr0 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Install your eSIM before departure and connect
                                as soon as you arrive, without searching for
                                local SIM cards or Wi-Fi.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-89f90k"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="1920"
                            height="1080"
                            sizes="(min-width: 1440px) max((min(100vw - 224px, 1280px) - 72px) / 4, 10px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 512w,/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 1024w,/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 1920w"
                            src="/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-1s33hxj-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s144" />
                            <div className="scallopplus-s145" />
                            <div className="scallopplus-s146" />
                            <div className="scallopplus-s147" />
                            <div className="scallopplus-s148" />
                            <div className="scallopplus-s149" />
                            <div className="scallopplus-s150" />
                            <div className="scallopplus-s151" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-k25sly"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-4e1aca-container">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1pazdt8"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-x5noyx scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Stay Connected Anywhere
                              </h6>
                            </div>
                            <div
                              className="framer-18bmsug scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Skip expensive roaming Get connected with local
                                data plans and avoid unexpected roaming charges
                                while traveling.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1qtu06w"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-28sava"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-wao0s6-container hidden-otbxcl hidden-18dn0ox">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1dcr9h9"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1xhp1qp scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Powered by local networks
                              </h6>
                            </div>
                            <div
                              className="framer-1nz3hjc scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Your connection runs on trusted local network
                                partners in each country, so speed and coverage
                                match what locals get.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
              <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                <section className="framer-cs6r2t" data-framer-name="Phone">
                  <div className="framer-763jom" data-framer-name="Container">
                    <div
                      className="framer-m2jsxw"
                      data-framer-name="Cards Wrapper"
                    >
                      <div
                        className="framer-mfd6wh"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-h658se"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-dx3rsk-container hidden-otbxcl hidden-18dn0ox">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1v8frsn"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1h612el scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s152"
                              >
                                Your eSIM stays ready
                              </h6>
                            </div>
                            <div
                              className="framer-117j2ki scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s153"
                              >
                                Your eSIM stays installed on your device even
                                after a plan expires. You&apos;ll see the
                                network, but data pauses until you top up or
                                activate a new plan - so reconnecting takes
                                seconds, not setup.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-15j1f7t"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-n9v0xb"
                          data-framer-name="Content Wrapper"
                        >
                          <div
                            className="framer-1bamk9e"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1r0zlz6 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s154"
                              >
                                Top up anytime
                              </h6>
                            </div>
                            <div
                              className="framer-ca8bvh scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s155"
                              >
                                Out of data? Reactivate connectivity instantly
                                by topping up your existing eSIM or adding a new
                                plan - all from the app, in seconds.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-15f50j9"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="1766"
                            height="1182"
                            sizes="(min-width: 1440px) calc(max((min(100vw - 224px, 1280px) - 72px) / 4, 10px) * 2 + 24px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 512w,/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 1024w,/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 1766w"
                            src="/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-qpj78o-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s136" />
                            <div className="scallopplus-s137" />
                            <div className="scallopplus-s138" />
                            <div className="scallopplus-s139" />
                            <div className="scallopplus-s140" />
                            <div className="scallopplus-s141" />
                            <div className="scallopplus-s142" />
                            <div className="scallopplus-s143" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-1x80cus"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-131lf0p-container hidden-1kt7ub1 hidden-otbxcl">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-pl0jsx"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1h9lu61 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s154"
                              >
                                Use any balance to pay
                              </h6>
                            </div>
                            <div
                              className="framer-o23yct scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s155"
                              >
                                Purchase eSIM plans using the funds already
                                available in your Scallop account, whether fiat,
                                stablecoins, or digital assets.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1nbpz68"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="705"
                            height="470"
                            sizes="(min-width: 1440px) calc(max((min(100vw - 224px, 1280px) - 72px) / 4, 10px) * 2 + 24px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png 512w,/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png 705w"
                            src="/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-57fbr-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s136" />
                            <div className="scallopplus-s137" />
                            <div className="scallopplus-s138" />
                            <div className="scallopplus-s139" />
                            <div className="scallopplus-s140" />
                            <div className="scallopplus-s141" />
                            <div className="scallopplus-s142" />
                            <div className="scallopplus-s143" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-486ytg"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-m0blom-container hidden-1kt7ub1 hidden-otbxcl">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1kd1l"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-jwsmoj scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s154"
                              >
                                Ready before you land
                              </h6>
                            </div>
                            <div
                              className="framer-1cj3xr0 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s155"
                              >
                                Install your eSIM before departure and connect
                                as soon as you arrive, without searching for
                                local SIM cards or Wi-Fi.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-89f90k"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="1920"
                            height="1080"
                            sizes="(min-width: 1440px) max((min(100vw - 224px, 1280px) - 72px) / 4, 10px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 512w,/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 1024w,/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 1920w"
                            src="/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-1s33hxj-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s144" />
                            <div className="scallopplus-s145" />
                            <div className="scallopplus-s146" />
                            <div className="scallopplus-s147" />
                            <div className="scallopplus-s148" />
                            <div className="scallopplus-s149" />
                            <div className="scallopplus-s150" />
                            <div className="scallopplus-s151" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-k25sly"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-4e1aca-container">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1pazdt8"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-x5noyx scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s154"
                              >
                                Stay Connected Anywhere
                              </h6>
                            </div>
                            <div
                              className="framer-18bmsug scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s155"
                              >
                                Skip expensive roaming Get connected with local
                                data plans and avoid unexpected roaming charges
                                while traveling.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1qtu06w"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-28sava"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-wao0s6-container hidden-otbxcl hidden-18dn0ox">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1dcr9h9"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1xhp1qp scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s154"
                              >
                                Powered by local networks
                              </h6>
                            </div>
                            <div
                              className="framer-1nz3hjc scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s155"
                              >
                                Your connection runs on trusted local network
                                partners in each country, so speed and coverage
                                match what locals get.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
              <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                <section className="framer-cs6r2t" data-framer-name="Tablet">
                  <div className="framer-763jom" data-framer-name="Container">
                    <div
                      className="framer-m2jsxw"
                      data-framer-name="Cards Wrapper"
                    >
                      <div
                        className="framer-mfd6wh"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-h658se"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-dx3rsk-container hidden-otbxcl hidden-18dn0ox">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1v8frsn"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1h612el scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s131"
                              >
                                Your eSIM stays ready
                              </h6>
                            </div>
                            <div
                              className="framer-117j2ki scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s132"
                              >
                                Your eSIM stays installed on your device even
                                after a plan expires. You&apos;ll see the
                                network, but data pauses until you top up or
                                activate a new plan - so reconnecting takes
                                seconds, not setup.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-15j1f7t"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-n9v0xb"
                          data-framer-name="Content Wrapper"
                        >
                          <div
                            className="framer-1bamk9e"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1r0zlz6 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Top up anytime
                              </h6>
                            </div>
                            <div
                              className="framer-ca8bvh scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Out of data? Reactivate connectivity instantly
                                by topping up your existing eSIM or adding a new
                                plan - all from the app, in seconds.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-15f50j9"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="1766"
                            height="1182"
                            sizes="(min-width: 1440px) calc(max((min(100vw - 224px, 1280px) - 72px) / 4, 10px) * 2 + 24px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 512w,/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 1024w,/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png 1766w"
                            src="/framerusercontent.com/images/ACsvdDiRNjmpSI4C0EAu49AGgY.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-qpj78o-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s136" />
                            <div className="scallopplus-s137" />
                            <div className="scallopplus-s138" />
                            <div className="scallopplus-s139" />
                            <div className="scallopplus-s140" />
                            <div className="scallopplus-s141" />
                            <div className="scallopplus-s142" />
                            <div className="scallopplus-s143" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-1x80cus"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-131lf0p-container hidden-1kt7ub1 hidden-otbxcl">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-pl0jsx"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1h9lu61 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Use any balance to pay
                              </h6>
                            </div>
                            <div
                              className="framer-o23yct scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Purchase eSIM plans using the funds already
                                available in your Scallop account, whether fiat,
                                stablecoins, or digital assets.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1nbpz68"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="705"
                            height="470"
                            sizes="(min-width: 1440px) calc(max((min(100vw - 224px, 1280px) - 72px) / 4, 10px) * 2 + 24px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png 512w,/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png 705w"
                            src="/framerusercontent.com/images/7et9KXJ8ti6IHvJZzWXPpdPpW8.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-57fbr-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s136" />
                            <div className="scallopplus-s137" />
                            <div className="scallopplus-s138" />
                            <div className="scallopplus-s139" />
                            <div className="scallopplus-s140" />
                            <div className="scallopplus-s141" />
                            <div className="scallopplus-s142" />
                            <div className="scallopplus-s143" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-486ytg"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-m0blom-container hidden-1kt7ub1 hidden-otbxcl">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1kd1l"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-jwsmoj scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Ready before you land
                              </h6>
                            </div>
                            <div
                              className="framer-1cj3xr0 scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Install your eSIM before departure and connect
                                as soon as you arrive, without searching for
                                local SIM cards or Wi-Fi.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-89f90k"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="1920"
                            height="1080"
                            sizes="(min-width: 1440px) max((min(100vw - 224px, 1280px) - 72px) / 4, 10px), (max-width: 809.98px) max(min(100vw - 56px, 1280px), 10px), (min-width: 810px) and (max-width: 1439.98px) max(min(100vw - 136px, 1280px), 10px)"
                            srcSet="/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 512w,/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 1024w,/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png 1920w"
                            src="/framerusercontent.com/images/kUpugnMwJIJkYoxwTn2EHIhWDeo.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                        <div
                          className="framer-1s33hxj-container"
                          data-framer-name="Progressive Blur Gradient"
                          name="Progressive Blur Gradient"
                        >
                          {/*$*/}
                          <div className="scallopplus-s135">
                            <div className="scallopplus-s144" />
                            <div className="scallopplus-s145" />
                            <div className="scallopplus-s146" />
                            <div className="scallopplus-s147" />
                            <div className="scallopplus-s148" />
                            <div className="scallopplus-s149" />
                            <div className="scallopplus-s150" />
                            <div className="scallopplus-s151" />
                          </div>
                          {/*/$*/}
                        </div>
                        <div
                          className="framer-k25sly"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-4e1aca-container">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1pazdt8"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-x5noyx scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Stay Connected Anywhere
                              </h6>
                            </div>
                            <div
                              className="framer-18bmsug scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Skip expensive roaming Get connected with local
                                data plans and avoid unexpected roaming charges
                                while traveling.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="framer-1qtu06w"
                        data-framer-name="Feature / Feature Card"
                      >
                        <div
                          className="framer-28sava"
                          data-framer-name="Content Wrapper"
                        >
                          <div className="framer-wao0s6-container hidden-otbxcl hidden-18dn0ox">
                            {/*$*/}
                            <div
                              className="framer-XheVr framer-tvqkjn framer-v-egds2c scallopplus-s130"
                              data-border="true"
                              data-framer-name="Large"
                            >
                              <div className="framer-8dp4e7-container">
                                <div className="scallopplus-s102" />
                              </div>
                            </div>
                            {/*/$*/}
                          </div>
                          <div
                            className="framer-1dcr9h9"
                            data-framer-name="Text Wrapper"
                          >
                            <div
                              className="framer-1xhp1qp scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <h6
                                dir="auto"
                                className="framer-text scallopplus-s133"
                              >
                                Powered by local networks
                              </h6>
                            </div>
                            <div
                              className="framer-1nz3hjc scallopplus-s55 reveal"
                              data-framer-component-type="RichTextContainer"
                            >
                              <p
                                dir="auto"
                                className="framer-text scallopplus-s134"
                              >
                                Your connection runs on trusted local network
                                partners in each country, so speed and coverage
                                match what locals get.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </section>
            <div className="framer-gcahs1" data-framer-name="Container">
              <div className="framer-3f4qmq" data-framer-name="Container">
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div
                    className="framer-6hr6qx scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s156">
                      More travel, less friction
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                  <div
                    className="framer-6hr6qx scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s157">
                      More travel, less friction
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                  <div
                    className="framer-6hr6qx scallopplus-s51 reveal"
                    data-framer-name="Choose how you use your account"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s158">
                      More travel, less friction
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                  <div
                    className="framer-ze8o5 scallopplus-s55 reveal"
                    data-framer-name="More features, higher limits, and priority access "
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s159">
                      eSIMs are the first step. We&apos;re building Scallop+
                      into a full travel layer, so your money, your trip, and
                      everything in between live in one place.
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1">
                  <div
                    className="framer-ze8o5 scallopplus-s51 reveal"
                    data-framer-name="More features, higher limits, and priority access "
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s26">
                      eSIMs are the first step. We&apos;re building Scallop+
                      into a full travel layer, so your money, your trip, and
                      everything in between live in one place.
                    </p>
                  </div>
                </div>
              </div>
              <section
                className="framer-ujpb7a"
                data-framer-name="Integration Section"
                id="integrations"
              >
                <div
                  className="framer-12bof7s"
                  data-framer-name="Integration Wrapper"
                >
                  <div
                    className="framer-9wccdj"
                    data-framer-name="Logo Animation Wrapper"
                  >
                    <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                      <div
                        className="framer-1kfu73e"
                        data-framer-name="Frame 1707479998"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="512"
                            height="512"
                            src="/framerusercontent.com/images/bP8kSHDF2fmdSBJXnWrHqm1KHf0.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="ssr-variant hidden-1kt7ub1">
                      <div
                        className="framer-1kfu73e scallopplus-s160"
                        data-framer-name="Frame 1707479998"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            loading="lazy"
                            width="512"
                            height="512"
                            src="/framerusercontent.com/images/bP8kSHDF2fmdSBJXnWrHqm1KHf0.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-1hlbt2u"
                    data-framer-name="Header Wrapper"
                  >
                    <div
                      className="framer-k5kkor"
                      data-framer-name="Heading Wrapper"
                    >
                      <div className="framer-vzq4to" data-framer-name="Item">
                        <div
                          className="framer-ostw5q scallopplus-s55 reveal"
                          data-framer-name="Label"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s161"
                          >
                            Coming soon
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1a9hj45 scallopplus-s55 reveal"
                        data-framer-name="Heading 3"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h3 dir="auto" className="framer-text scallopplus-s162">
                          Travel insurance
                        </h3>
                      </div>
                    </div>
                    <div
                      className="framer-11dfu27 scallopplus-s55 reveal"
                      data-framer-name="Body Large"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s163">
                        Connect your favorite tools in seconds, from
                        communication to data, our agents work wherever you do.
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className="framer-1gfrwlr"
                  data-framer-name="Popular Integrations Wrapper"
                >
                  <div
                    className="framer-1njdiot"
                    data-framer-name="First Integration Wrapper"
                  >
                    <div
                      className="framer-1iqmpcp"
                      data-framer-name="First 1:1 Logo Image"
                      draggable={false}
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="321"
                          height="321"
                          src="/framerusercontent.com/images/RoLJBMtaBLoF3nMHzNKNhJdQhM.png"
                          alt=""
                          draggable={false}
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-3hoona"
                      data-framer-name="Text Wrapper"
                    >
                      <div
                        className="framer-g8u2ez"
                        data-border="true"
                        data-framer-name="Item"
                      >
                        <div
                          className="framer-6pegee scallopplus-s164 reveal"
                          data-framer-name="Label"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s165"
                          >
                            Soon
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1aw2new scallopplus-s55 reveal"
                        data-framer-name="Heading 5"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h5 dir="auto" className="framer-text scallopplus-s166">
                          Flights &amp; hotels
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-5h3onl"
                    data-framer-name="Second Integration Wrapper"
                  >
                    <div
                      className="framer-11t3eym"
                      data-framer-name="Second 1:1 Logo Image"
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="321"
                          height="321"
                          src="/framerusercontent.com/images/FLHKdt7fIeEWOpbgivOjjIxk.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-11s0uob"
                      data-framer-name="Text Wrapper"
                    >
                      <div
                        className="framer-1kvxxjb"
                        data-border="true"
                        data-framer-name="Item"
                      >
                        <div
                          className="framer-i9sod7 scallopplus-s164 reveal"
                          data-framer-name="Label"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s165"
                          >
                            Soon
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1q7xq0x scallopplus-s55 reveal"
                        data-framer-name="Heading 5"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h5 dir="auto" className="framer-text scallopplus-s166">
                          Car rentals &amp; transfers
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-1ppn98l"
                    data-framer-name="Third Integration Wrapper"
                  >
                    <div
                      className="framer-1g590jp"
                      data-framer-name="Third 1:1 Logo Image"
                    >
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="321"
                          height="321"
                          src="/framerusercontent.com/images/KCuCgoIwzuFFUk5UiPFOhoEI.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-1fo79tz"
                      data-framer-name="Text Wrapper"
                    >
                      <div
                        className="framer-1ux3rgy"
                        data-border="true"
                        data-framer-name="Item"
                      >
                        <div
                          className="framer-cay6tb scallopplus-s164 reveal"
                          data-framer-name="Label"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s165"
                          >
                            Future
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-fo9ndw scallopplus-s55 reveal"
                        data-framer-name="Heading 5"
                        data-framer-component-type="RichTextContainer"
                      >
                        <h5 dir="auto" className="framer-text scallopplus-s166">
                          And more to come
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            <div className="framer-fmf9jg" data-framer-name="c2a">
              <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
                <div className="framer-1pqfmc-container" id="cta">
                  <section
                    className="framer-WvUDv framer-p2e38s framer-v-p2e38s scallopplus-s3"
                    data-framer-name="Desktop"
                  >
                    <div
                      className="framer-68oizx scallopplus-s167"
                      data-framer-name="CTA Wrapper"
                    >
                      <div
                        className="framer-1j3tetx"
                        data-framer-name="Content Wrapper"
                      >
                        <div className="framer-1kvw61i" data-framer-name="Top">
                          <div
                            className="framer-1u5643w scallopplus-s168 reveal"
                            data-framer-name="Choose how you use your account"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s169"
                            >
                              Land connected on your next trip
                            </p>
                          </div>
                          <div
                            className="framer-1n8ovd7 scallopplus-s170 reveal"
                            data-framer-name="More features, higher limits, and priority access "
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s171"
                            >
                              Set up your eSIM in minutes. Pick a plan when you
                              need it. <br className="framer-text" />
                              Pay the way you want.
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-x3nt5l"
                          data-framer-appear-id="x3nt5l"
                        >
                          {/*$*/}
                          <a
                            className="framer-1wusars framer-1r6wjfa"
                            data-framer-name="Appstore"
                            href="https://apps.apple.com/app/scallop/id6746711366"
                            target="_blank"
                            rel="noopener"
                          >
                            <img
                              alt=""
                              className="framer-fhIeM framer-rpojg6"
                              src="data:image/svg+xml,<svg display=%22block%22 role=%22presentation%22 viewBox=%220 0 180 60%22 xmlns=%22http://www.w3.org/2000/svg%22><g d=%22M 0 60 L 0 0 L 180 0 L 180 60 Z M 169.5 0.75 L 10.5 0.75 C 5.115 0.75 0.75 5.115 0.75 10.5 L 0.75 49.5 C 0.75 54.885 5.115 59.25 10.5 59.25 L 169.5 59.25 C 174.885 59.25 179.25 54.885 179.25 49.5 L 179.25 10.5 C 179.25 5.115 174.885 0.75 169.5 0.75 Z M 169.5 0.75 L 10.5 0.75 C 5.115 0.75 0.75 5.115 0.75 10.5 L 0.75 49.5 C 0.75 54.885 5.115 59.25 10.5 59.25 L 169.5 59.25 C 174.885 59.25 179.25 54.885 179.25 49.5 L 179.25 10.5 C 179.25 5.115 174.885 0.75 169.5 0.75 Z M 122.291 28.802 L 122.291 32.239 L 120.137 32.239 L 120.137 34.492 L 122.291 34.492 L 122.291 42.15 C 122.291 44.764 123.474 45.81 126.449 45.81 C 126.972 45.81 127.471 45.748 127.906 45.673 L 127.906 43.445 C 127.533 43.482 127.296 43.507 126.885 43.507 C 125.553 43.507 124.968 42.884 124.968 41.465 L 124.968 34.492 L 127.906 34.492 L 127.906 32.239 L 124.968 32.239 L 124.968 28.802 Z M 135.486 45.997 C 139.445 45.997 141.873 43.345 141.873 38.95 C 141.873 34.579 139.433 31.915 135.486 31.915 C 131.526 31.915 129.086 34.579 129.086 38.95 C 129.086 43.345 131.514 45.997 135.486 45.997 Z M 135.486 43.619 C 133.158 43.619 131.85 41.913 131.85 38.95 C 131.85 36.011 133.158 34.293 135.486 34.293 C 137.802 34.293 139.122 36.011 139.122 38.95 C 139.122 41.901 137.802 43.619 135.486 43.619 Z M 143.949 45.736 L 146.626 45.736 L 146.626 37.73 C 146.626 35.825 148.058 34.542 150.088 34.542 C 150.56 34.542 151.358 34.629 151.582 34.704 L 151.582 32.064 C 151.295 31.989 150.785 31.952 150.386 31.952 C 148.618 31.952 147.112 32.923 146.726 34.256 L 146.527 34.256 L 146.527 32.176 L 143.949 32.176 Z M 158.229 34.193 C 160.209 34.193 161.504 35.576 161.567 37.705 L 154.718 37.705 C 154.868 35.588 156.249 34.193 158.229 34.193 Z M 161.555 42.075 C 161.057 43.133 159.948 43.719 158.33 43.719 C 156.188 43.719 154.806 42.212 154.718 39.834 L 154.718 39.684 L 164.294 39.684 L 164.294 38.751 C 164.294 34.492 162.015 31.915 158.243 31.915 C 154.419 31.915 151.992 34.667 151.992 39 C 151.992 43.333 154.37 45.997 158.255 45.997 C 161.355 45.997 163.521 44.503 164.132 42.075 Z M 104.734 40.728 C 104.941 44.058 107.716 46.187 111.845 46.187 C 116.259 46.187 119.021 43.955 119.021 40.393 C 119.021 37.592 117.446 36.044 113.626 35.153 L 111.574 34.65 C 109.148 34.082 108.167 33.321 108.167 31.992 C 108.167 30.314 109.69 29.217 111.974 29.217 C 114.142 29.217 115.639 30.288 115.91 32.005 L 118.724 32.005 C 118.556 28.868 115.794 26.662 112.013 26.662 C 107.948 26.662 105.238 28.868 105.238 32.185 C 105.238 34.921 106.773 36.547 110.142 37.334 L 112.542 37.915 C 115.007 38.496 116.091 39.348 116.091 40.767 C 116.091 42.419 114.388 43.619 112.065 43.619 C 109.574 43.619 107.845 42.496 107.599 40.728 Z M 77.004 31.952 C 75.161 31.952 73.567 32.874 72.745 34.418 L 72.546 34.418 L 72.546 32.176 L 69.969 32.176 L 69.969 50.243 L 72.646 50.243 L 72.646 43.681 L 72.857 43.681 C 73.567 45.113 75.099 45.96 77.029 45.96 C 80.453 45.96 82.632 43.258 82.632 38.95 C 82.632 34.642 80.453 31.952 77.004 31.952 Z M 76.244 43.557 C 74.003 43.557 72.596 41.789 72.596 38.962 C 72.596 36.123 74.003 34.355 76.257 34.355 C 78.523 34.355 79.88 36.086 79.88 38.95 C 79.88 41.826 78.523 43.557 76.244 43.557 Z M 91.996 31.952 C 90.153 31.952 88.559 32.874 87.737 34.418 L 87.538 34.418 L 87.538 32.176 L 84.961 32.176 L 84.961 50.243 L 87.638 50.243 L 87.638 43.681 L 87.849 43.681 C 88.559 45.113 90.091 45.96 92.021 45.96 C 95.445 45.96 97.624 43.258 97.624 38.95 C 97.624 34.642 95.445 31.952 91.996 31.952 Z M 91.236 43.557 C 88.995 43.557 87.588 41.789 87.588 38.962 C 87.588 36.123 88.995 34.355 91.249 34.355 C 93.515 34.355 94.872 36.086 94.872 38.95 C 94.872 41.826 93.515 43.557 91.236 43.557 Z M 65.166 45.736 L 68.237 45.736 L 61.513 27.113 L 58.403 27.113 L 51.68 45.736 L 54.648 45.736 L 56.364 40.793 L 63.462 40.793 Z M 59.81 30.495 L 60.029 30.495 L 62.727 38.367 L 57.1 38.367 Z M 53.477 13.066 L 53.477 22.05 L 56.72 22.05 C 59.397 22.05 60.947 20.4 60.947 17.53 C 60.947 14.703 59.385 13.066 56.72 13.066 Z M 54.871 14.336 L 56.565 14.336 C 58.426 14.336 59.528 15.519 59.528 17.548 C 59.528 19.609 58.445 20.779 56.565 20.779 L 54.871 20.779 Z M 65.696 22.18 C 67.676 22.18 68.89 20.854 68.89 18.657 C 68.89 16.472 67.67 15.139 65.696 15.139 C 63.716 15.139 62.496 16.472 62.496 18.657 C 62.496 20.854 63.71 22.18 65.696 22.18 Z M 65.696 20.991 C 64.532 20.991 63.878 20.138 63.878 18.657 C 63.878 17.188 64.532 16.328 65.696 16.328 C 66.854 16.328 67.514 17.188 67.514 18.657 C 67.514 20.132 66.854 20.991 65.696 20.991 Z M 79.227 15.27 L 77.888 15.27 L 76.68 20.443 L 76.574 20.443 L 75.18 15.27 L 73.897 15.27 L 72.503 20.443 L 72.403 20.443 L 71.189 15.27 L 69.832 15.27 L 71.7 22.05 L 73.076 22.05 L 74.47 17.057 L 74.576 17.057 L 75.977 22.05 L 77.365 22.05 Z M 80.77 22.05 L 82.108 22.05 L 82.108 18.084 C 82.108 17.026 82.737 16.366 83.727 16.366 C 84.717 16.366 85.19 16.907 85.19 17.997 L 85.19 22.05 L 86.528 22.05 L 86.528 17.661 C 86.528 16.048 85.694 15.139 84.181 15.139 C 83.16 15.139 82.488 15.594 82.158 16.347 L 82.058 16.347 L 82.058 15.27 L 80.77 15.27 Z M 88.637 22.049 L 89.975 22.049 L 89.975 12.624 L 88.637 12.624 Z M 95.009 22.18 C 96.988 22.18 98.202 20.854 98.202 18.657 C 98.202 16.472 96.982 15.139 95.009 15.139 C 93.029 15.139 91.809 16.472 91.809 18.657 C 91.809 20.854 93.023 22.18 95.009 22.18 Z M 95.009 20.991 C 93.844 20.991 93.191 20.138 93.191 18.657 C 93.191 17.188 93.844 16.328 95.009 16.328 C 96.167 16.328 96.826 17.188 96.826 18.657 C 96.826 20.132 96.167 20.991 95.009 20.991 Z M 102.188 21.035 C 101.46 21.035 100.931 20.68 100.931 20.07 C 100.931 19.472 101.354 19.155 102.288 19.093 L 103.944 18.987 L 103.944 19.553 C 103.944 20.394 103.197 21.035 102.188 21.035 Z M 101.846 22.162 C 102.736 22.162 103.477 21.776 103.882 21.097 L 103.987 21.097 L 103.987 22.05 L 105.276 22.05 L 105.276 17.418 C 105.276 15.986 104.317 15.139 102.618 15.139 C 101.08 15.139 99.984 15.886 99.847 17.05 L 101.142 17.05 C 101.292 16.571 101.808 16.297 102.555 16.297 C 103.471 16.297 103.944 16.702 103.944 17.418 L 103.944 18.003 L 102.107 18.109 C 100.495 18.208 99.586 18.912 99.586 20.132 C 99.586 21.371 100.538 22.162 101.846 22.162 Z M 109.818 22.162 C 110.752 22.162 111.542 21.719 111.947 20.979 L 112.053 20.979 L 112.053 22.049 L 113.335 22.049 L 113.335 12.624 L 111.997 12.624 L 111.997 16.347 L 111.897 16.347 C 111.53 15.6 110.746 15.158 109.818 15.158 C 108.106 15.158 107.004 16.515 107.004 18.656 C 107.004 20.804 108.093 22.162 109.818 22.162 Z M 110.198 16.359 C 111.318 16.359 112.022 17.25 112.022 18.663 C 112.022 20.082 111.325 20.96 110.198 20.96 C 109.065 20.96 108.386 20.095 108.386 18.656 C 108.386 17.231 109.071 16.359 110.198 16.359 Z M 122.016 22.18 C 123.996 22.18 125.21 20.854 125.21 18.657 C 125.21 16.472 123.99 15.139 122.016 15.139 C 120.037 15.139 118.816 16.472 118.816 18.657 C 118.816 20.854 120.031 22.18 122.016 22.18 Z M 122.016 20.991 C 120.852 20.991 120.199 20.138 120.199 18.657 C 120.199 17.188 120.852 16.328 122.016 16.328 C 123.174 16.328 123.834 17.188 123.834 18.657 C 123.834 20.132 123.174 20.991 122.016 20.991 Z M 126.98 22.05 L 128.319 22.05 L 128.319 18.084 C 128.319 17.026 128.948 16.366 129.938 16.366 C 130.927 16.366 131.401 16.907 131.401 17.997 L 131.401 22.05 L 132.739 22.05 L 132.739 17.661 C 132.739 16.048 131.905 15.139 130.392 15.139 C 129.371 15.139 128.699 15.594 128.369 16.347 L 128.269 16.347 L 128.269 15.27 L 126.98 15.27 Z M 138.905 13.583 L 138.905 15.301 L 137.828 15.301 L 137.828 16.428 L 138.905 16.428 L 138.905 20.257 C 138.905 21.564 139.497 22.087 140.984 22.087 C 141.246 22.087 141.495 22.056 141.713 22.018 L 141.713 20.904 C 141.526 20.923 141.408 20.935 141.202 20.935 C 140.536 20.935 140.244 20.624 140.244 19.914 L 140.244 16.428 L 141.713 16.428 L 141.713 15.301 L 140.244 15.301 L 140.244 13.583 Z M 143.512 22.049 L 144.85 22.049 L 144.85 18.09 C 144.85 17.063 145.46 16.372 146.556 16.372 C 147.502 16.372 148.007 16.92 148.007 18.003 L 148.007 22.049 L 149.345 22.049 L 149.345 17.673 C 149.345 16.06 148.455 15.145 147.011 15.145 C 145.99 15.145 145.274 15.6 144.944 16.359 L 144.838 16.359 L 144.838 12.624 L 143.512 12.624 Z M 154.173 16.279 C 155.163 16.279 155.811 16.97 155.843 18.034 L 152.418 18.034 C 152.493 16.976 153.183 16.279 154.173 16.279 Z M 155.835 20.219 C 155.586 20.749 155.033 21.041 154.223 21.041 C 153.153 21.041 152.462 20.288 152.418 19.099 L 152.418 19.024 L 157.205 19.024 L 157.205 18.557 C 157.205 16.428 156.066 15.139 154.179 15.139 C 152.268 15.139 151.055 16.515 151.055 18.682 C 151.055 20.848 152.244 22.18 154.185 22.18 C 155.736 22.18 156.819 21.433 157.124 20.219 Z M 37.153 30.452 C 37.185 27.93 38.54 25.544 40.688 24.224 C 39.333 22.288 37.063 21.061 34.701 20.987 C 32.182 20.723 29.74 22.494 28.457 22.494 C 27.148 22.494 25.172 21.013 23.044 21.057 C 20.27 21.147 17.684 22.724 16.335 25.149 C 13.434 30.172 15.597 37.553 18.376 41.613 C 19.767 43.601 21.392 45.822 23.518 45.743 C 25.598 45.657 26.375 44.416 28.887 44.416 C 31.375 44.416 32.104 45.743 34.273 45.693 C 36.506 45.657 37.913 43.696 39.254 41.689 C 40.253 40.273 41.022 38.707 41.532 37.05 C 38.908 35.941 37.156 33.3 37.153 30.452 Z M 33.057 18.317 C 34.274 16.856 34.874 14.977 34.729 13.081 C 32.869 13.276 31.151 14.165 29.917 15.571 C 28.711 16.943 28.083 18.789 28.202 20.613 C 30.062 20.632 31.891 19.767 33.057 18.317 Z%22 fill=%22transparent%22 height=%2260px%22 id=%22V_5LYGIvk%22 width=%22180px%22><path d=%22M 0 60 L 0 0 L 180 0 L 180 60 Z%22 fill=%22transparent%22 height=%2260px%22 id=%22kHaGt6RyA%22 width=%22180px%22/><path d=%22M 168.75 0 L 9.75 0 C 4.365 0 0 4.365 0 9.75 L 0 48.75 C 0 54.135 4.365 58.5 9.75 58.5 L 168.75 58.5 C 174.135 58.5 178.5 54.135 178.5 48.75 L 178.5 9.75 C 178.5 4.365 174.135 0 168.75 0 Z%22 fill=%22rgb(12, 13, 16)%22 height=%2258.5px%22 id=%22FEPf0vY4T%22 transform=%22translate(0.75 0.75)%22 width=%22178.5px%22/><path d=%22M 168.75 0 L 9.75 0 C 4.365 0 0 4.365 0 9.75 L 0 48.75 C 0 54.135 4.365 58.5 9.75 58.5 L 168.75 58.5 C 174.135 58.5 178.5 54.135 178.5 48.75 L 178.5 9.75 C 178.5 4.365 174.135 0 168.75 0 Z%22 fill=%22transparent%22 height=%2258.5px%22 id=%22R3Yc3UvLE%22 stroke-dasharray=%22%22 stroke-linecap=%22butt%22 stroke-linejoin=%22miter%22 stroke-miterlimit=%2210%22 stroke-width=%221.5%22 stroke=%22rgb(166, 166, 166)%22 transform=%22translate(0.75 0.75)%22 width=%22178.5px%22/><path d=%22M 2.154 0 L 2.154 3.437 L 0 3.437 L 0 5.69 L 2.154 5.69 L 2.154 13.348 C 2.154 15.962 3.337 17.008 6.312 17.008 C 6.835 17.008 7.334 16.946 7.769 16.871 L 7.769 14.643 C 7.396 14.68 7.159 14.705 6.748 14.705 C 5.416 14.705 4.831 14.082 4.831 12.663 L 4.831 5.69 L 7.769 5.69 L 7.769 3.437 L 4.831 3.437 L 4.831 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2217.008399999999998px%22 id=%22cNda9Ea2m%22 transform=%22translate(120.137 28.802)%22 width=%227.7690000000000055px%22/><path d=%22M 6.4 14.082 C 10.359 14.082 12.787 11.43 12.787 7.035 C 12.787 2.665 10.347 0 6.4 0 C 2.44 0 0 2.665 0 7.035 C 0 11.43 2.428 14.082 6.4 14.082 Z M 6.4 11.704 C 4.072 11.704 2.764 9.998 2.764 7.035 C 2.764 4.096 4.072 2.378 6.4 2.378 C 8.716 2.378 10.036 4.096 10.036 7.035 C 10.036 9.986 8.716 11.704 6.4 11.704 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2214.0823px%22 id=%22qqfvCfrtg%22 transform=%22translate(129.086 31.915)%22 width=%2212.786999999999978px%22/><path d=%22M 0 13.783 L 2.677 13.783 L 2.677 5.777 C 2.677 3.872 4.109 2.59 6.139 2.59 C 6.611 2.59 7.409 2.677 7.633 2.752 L 7.633 0.112 C 7.346 0.037 6.836 0 6.437 0 C 4.669 0 3.163 0.971 2.777 2.303 L 2.578 2.303 L 2.578 0.224 L 0 0.224 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2213.783499999999997px%22 id=%22ozncIZnPB%22 transform=%22translate(143.949 31.952)%22 width=%227.632999999999981px%22/><path d=%22M 6.237 2.279 C 8.217 2.279 9.512 3.661 9.575 5.79 L 2.726 5.79 C 2.876 3.673 4.257 2.279 6.237 2.279 Z M 9.563 10.16 C 9.065 11.218 7.956 11.804 6.338 11.804 C 4.196 11.804 2.814 10.297 2.726 7.919 L 2.726 7.77 L 12.302 7.77 L 12.302 6.836 C 12.302 2.577 10.023 0 6.251 0 C 2.427 0 0 2.752 0 7.085 C 0 11.418 2.378 14.082 6.263 14.082 C 9.363 14.082 11.529 12.588 12.14 10.16 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2214.082300000000004px%22 id=%22ChYaxBatK%22 transform=%22translate(151.992 31.915)%22 width=%2212.302000000000021px%22/><path d=%22M 0 14.067 C 0.207 17.396 2.982 19.526 7.111 19.526 C 11.525 19.526 14.287 17.293 14.287 13.731 C 14.287 10.931 12.712 9.382 8.892 8.492 L 6.84 7.988 C 4.414 7.42 3.433 6.659 3.433 5.33 C 3.433 3.652 4.956 2.555 7.24 2.555 C 9.408 2.555 10.905 3.626 11.176 5.343 L 13.99 5.343 C 13.822 2.207 11.06 0 7.279 0 C 3.214 0 0.504 2.207 0.504 5.523 C 0.504 8.26 2.039 9.886 5.408 10.673 L 7.808 11.254 C 10.273 11.834 11.357 12.686 11.357 14.106 C 11.357 15.757 9.654 16.958 7.331 16.958 C 4.84 16.958 3.111 15.835 2.865 14.067 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2219.5257px%22 id=%22IjThgD9dd%22 transform=%22translate(104.734 26.662)%22 width=%2214.287000000000006px%22/><path d=%22M 7.035 0 C 5.192 0 3.598 0.921 2.776 2.465 L 2.577 2.465 L 2.577 0.224 L 0 0.224 L 0 18.291 L 2.677 18.291 L 2.677 11.729 L 2.888 11.729 C 3.598 13.161 5.13 14.008 7.06 14.008 C 10.484 14.008 12.663 11.306 12.663 6.998 C 12.663 2.689 10.484 0 7.035 0 Z M 6.275 11.605 C 4.034 11.605 2.627 9.837 2.627 7.01 C 2.627 4.171 4.034 2.403 6.288 2.403 C 8.554 2.403 9.911 4.134 9.911 6.998 C 9.911 9.874 8.554 11.605 6.275 11.605 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.290799999999997px%22 id=%22iVaLanCV_%22 transform=%22translate(69.969 31.952)%22 width=%2212.662800000000004px%22/><path d=%22M 7.035 0 C 5.192 0 3.598 0.921 2.776 2.465 L 2.577 2.465 L 2.577 0.224 L 0 0.224 L 0 18.291 L 2.677 18.291 L 2.677 11.729 L 2.889 11.729 C 3.598 13.161 5.13 14.008 7.06 14.008 C 10.484 14.008 12.663 11.306 12.663 6.998 C 12.663 2.689 10.484 0 7.035 0 Z M 6.275 11.605 C 4.034 11.605 2.627 9.837 2.627 7.01 C 2.627 4.171 4.034 2.403 6.288 2.403 C 8.554 2.403 9.911 4.134 9.911 6.998 C 9.911 9.874 8.554 11.605 6.275 11.605 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.290799999999997px%22 id=%22wCMU1UaNq%22 transform=%22translate(84.961 31.952)%22 width=%2212.662900000000008px%22/><path d=%22M 13.486 18.622 L 16.557 18.622 L 9.834 0 L 6.724 0 L 0 18.622 L 2.968 18.622 L 4.685 13.68 L 11.783 13.68 Z M 8.13 3.381 L 8.35 3.381 L 11.047 11.253 L 5.42 11.253 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.6223px%22 id=%22dv51fN_AU%22 transform=%22translate(51.68 27.113)%22 width=%2216.5574px%22/><path d=%22M 0 0 L 0 8.984 L 3.244 8.984 C 5.921 8.984 7.471 7.334 7.471 4.464 C 7.471 1.637 5.908 0 3.244 0 Z M 1.395 1.27 L 3.088 1.27 C 4.949 1.27 6.051 2.453 6.051 4.482 C 6.051 6.543 4.968 7.713 3.088 7.713 L 1.395 7.713 Z%22 fill=%22rgb(255, 255, 255)%22 height=%228.983600000000001px%22 id=%22kgSqGhbT6%22 transform=%22translate(53.477 13.066)%22 width=%227.470700000000001px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.22 0 0 1.332 0 3.517 C 0 5.715 1.214 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.382 4.999 1.382 3.517 C 1.382 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22X6oepGtxA%22 transform=%22translate(62.496 15.139)%22 width=%226.393600000000006px%22/><path d=%22M 9.395 0 L 8.056 0 L 6.848 5.174 L 6.742 5.174 L 5.348 0 L 4.065 0 L 2.671 5.174 L 2.571 5.174 L 1.357 0 L 0 0 L 1.868 6.78 L 3.244 6.78 L 4.638 1.787 L 4.744 1.787 L 6.145 6.78 L 7.533 6.78 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.779699999999998px%22 id=%22J5qrRVDfp%22 transform=%22translate(69.832 15.27)%22 width=%229.394500000000008px%22/><path d=%22M 0 6.91 L 1.339 6.91 L 1.339 2.945 C 1.339 1.886 1.967 1.226 2.957 1.226 C 3.947 1.226 4.42 1.768 4.42 2.857 L 4.42 6.91 L 5.759 6.91 L 5.759 2.521 C 5.759 0.909 4.925 0 3.412 0 C 2.391 0 1.718 0.454 1.388 1.208 L 1.289 1.208 L 1.289 0.131 L 0 0.131 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.910400000000001px%22 id=%22cRWG_5cD1%22 transform=%22translate(80.769 15.139)%22 width=%225.758700000000005px%22/><path d=%22M 0 9.425 L 1.339 9.425 L 1.339 0 L 0 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.425499999999998px%22 id=%22eJ7ddPydU%22 transform=%22translate(88.637 12.624)%22 width=%221.3385999999999996px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.22 0 0 1.332 0 3.517 C 0 5.715 1.214 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.382 4.999 1.382 3.517 C 1.382 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22JDAra7JIl%22 transform=%22translate(91.809 15.139)%22 width=%226.393600000000006px%22/><path d=%22M 2.602 5.896 C 1.874 5.896 1.345 5.541 1.345 4.931 C 1.345 4.333 1.768 4.015 2.702 3.953 L 4.358 3.847 L 4.358 4.414 C 4.358 5.254 3.611 5.896 2.602 5.896 Z M 2.26 7.022 C 3.15 7.022 3.891 6.636 4.296 5.958 L 4.401 5.958 L 4.401 6.91 L 5.69 6.91 L 5.69 2.278 C 5.69 0.847 4.731 0 3.032 0 C 1.494 0 0.398 0.747 0.261 1.911 L 1.556 1.911 C 1.706 1.432 2.222 1.158 2.969 1.158 C 3.885 1.158 4.358 1.563 4.358 2.278 L 4.358 2.864 L 2.521 2.97 C 0.909 3.069 0 3.773 0 4.993 C 0 6.232 0.952 7.022 2.26 7.022 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.022399999999999px%22 id=%22e2IYJHcgl%22 transform=%22translate(99.586 15.139)%22 width=%225.690100000000001px%22/><path d=%22M 2.814 9.538 C 3.748 9.538 4.538 9.095 4.943 8.355 L 5.049 8.355 L 5.049 9.425 L 6.331 9.425 L 6.331 0 L 4.993 0 L 4.993 3.723 L 4.893 3.723 C 4.526 2.976 3.742 2.534 2.814 2.534 C 1.102 2.534 0 3.891 0 6.033 C 0 8.18 1.089 9.538 2.814 9.538 Z M 3.194 3.735 C 4.314 3.735 5.018 4.626 5.018 6.039 C 5.018 7.458 4.321 8.336 3.194 8.336 C 2.061 8.336 1.382 7.471 1.382 6.033 C 1.382 4.607 2.067 3.735 3.194 3.735 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.5376px%22 id=%22K5cX3nCIG%22 transform=%22translate(107.004 12.624)%22 width=%226.330999999999989px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.221 0 0 1.332 0 3.517 C 0 5.715 1.215 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.383 4.999 1.383 3.517 C 1.383 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22NIqN4iVvg%22 transform=%22translate(118.816 15.139)%22 width=%226.393999999999991px%22/><path d=%22M 0 6.91 L 1.339 6.91 L 1.339 2.945 C 1.339 1.886 1.968 1.226 2.958 1.226 C 3.947 1.226 4.421 1.768 4.421 2.857 L 4.421 6.91 L 5.759 6.91 L 5.759 2.521 C 5.759 0.909 4.925 0 3.412 0 C 2.391 0 1.719 0.454 1.389 1.208 L 1.289 1.208 L 1.289 0.131 L 0 0.131 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.910400000000001px%22 id=%22NTi0rfi1Y%22 transform=%22translate(126.98 15.139)%22 width=%225.759px%22/><path d=%22M 1.077 0 L 1.077 1.718 L 0 1.718 L 0 2.845 L 1.077 2.845 L 1.077 6.674 C 1.077 7.981 1.669 8.504 3.156 8.504 C 3.418 8.504 3.667 8.473 3.885 8.436 L 3.885 7.321 C 3.698 7.34 3.58 7.352 3.374 7.352 C 2.708 7.352 2.416 7.041 2.416 6.331 L 2.416 2.845 L 3.885 2.845 L 3.885 1.718 L 2.416 1.718 L 2.416 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%228.5041px%22 id=%22Nos9Cp5Hh%22 transform=%22translate(137.828 13.583)%22 width=%223.884999999999991px%22/><path d=%22M 0 9.425 L 1.338 9.425 L 1.338 5.466 C 1.338 4.439 1.948 3.748 3.044 3.748 C 3.99 3.748 4.495 4.296 4.495 5.379 L 4.495 9.425 L 5.833 9.425 L 5.833 5.049 C 5.833 3.437 4.943 2.521 3.499 2.521 C 2.478 2.521 1.762 2.976 1.432 3.735 L 1.326 3.735 L 1.326 0 L 0 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.425499999999998px%22 id=%22LVQbLUqUF%22 transform=%22translate(143.512 12.624)%22 width=%225.832999999999998px%22/><path d=%22M 3.118 1.139 C 4.108 1.139 4.756 1.83 4.788 2.895 L 1.363 2.895 C 1.438 1.836 2.128 1.139 3.118 1.139 Z M 4.78 5.08 C 4.531 5.609 3.978 5.902 3.168 5.902 C 2.098 5.902 1.407 5.149 1.363 3.959 L 1.363 3.885 L 6.15 3.885 L 6.15 3.418 C 6.15 1.289 5.011 0 3.124 0 C 1.213 0 0 1.376 0 3.542 C 0 5.709 1.189 7.041 3.13 7.041 C 4.681 7.041 5.764 6.294 6.069 5.08 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22vrbdmjN_S%22 transform=%22translate(151.055 15.139)%22 width=%226.150000000000006px%22/><path d=%22M 22.196 9.492 C 22.228 6.97 23.583 4.584 25.731 3.263 C 24.376 1.328 22.106 0.101 19.744 0.027 C 17.225 -0.237 14.783 1.534 13.5 1.534 C 12.191 1.534 10.215 0.053 8.087 0.097 C 5.313 0.187 2.727 1.764 1.378 4.189 C -1.523 9.211 0.64 16.593 3.419 20.653 C 4.81 22.641 6.435 24.862 8.561 24.783 C 10.641 24.696 11.418 23.456 13.93 23.456 C 16.418 23.456 17.147 24.783 19.316 24.733 C 21.549 24.696 22.956 22.736 24.297 20.729 C 25.296 19.312 26.065 17.747 26.575 16.09 C 23.951 14.98 22.199 12.34 22.196 9.492 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2224.78491613833904px%22 id=%22PM7rCRhKC%22 transform=%22translate(14.957 20.96)%22 width=%2226.574785466484332px%22/><path d=%22M 4.869 5.236 C 6.087 3.775 6.686 1.896 6.541 0 C 4.681 0.195 2.964 1.084 1.73 2.489 C 0.523 3.862 -0.105 5.708 0.014 7.532 C 1.875 7.551 3.703 6.686 4.869 5.236 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.531813143919933px%22 id=%22KMIzbdHNa%22 transform=%22translate(28.188 13.081)%22 width=%226.562434978114929px%22/></g></svg>"
                            />
                          </a>
                          {/*/$*/}
                          {/*$*/}
                          <a
                            className="framer-1mj9ybf framer-1r6wjfa"
                            data-framer-name="Playstore"
                            href="https://play.google.com/store/apps/details?id=com.app.scallop"
                            target="_blank"
                            rel="noopener"
                          >
                            <img
                              alt=""
                              className="framer-z0HLy framer-1u48p0z"
                              src="data:image/svg+xml,<svg display=%22block%22 role=%22presentation%22 viewBox=%220 0 201 59%22 xmlns=%22http://www.w3.org/2000/svg%22><g d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z M 158.062 43.115 L 160.834 43.115 L 160.834 24.844 L 158.062 24.844 Z M 183.023 31.426 L 179.846 39.348 L 179.751 39.348 L 176.455 31.426 L 173.47 31.426 L 178.415 42.498 L 175.594 48.657 L 178.484 48.657 L 186.105 31.426 Z M 167.307 41.04 C 166.401 41.04 165.133 40.593 165.133 39.489 C 165.133 38.078 166.711 37.537 168.071 37.537 C 169.289 37.537 169.864 37.795 170.603 38.148 C 170.388 39.842 168.907 41.04 167.307 41.04 Z M 167.641 31.027 C 165.635 31.027 163.557 31.897 162.698 33.824 L 165.157 34.834 C 165.683 33.824 166.661 33.495 167.689 33.495 C 169.122 33.495 170.579 34.34 170.602 35.846 L 170.602 36.033 C 170.1 35.751 169.025 35.328 167.712 35.328 C 165.06 35.328 162.362 36.762 162.362 39.441 C 162.362 41.887 164.536 43.461 166.972 43.461 C 168.834 43.461 169.863 42.639 170.506 41.674 L 170.602 41.674 L 170.602 43.086 L 173.279 43.086 L 173.279 36.08 C 173.279 32.836 170.816 31.027 167.641 31.027 Z M 150.516 33.65 L 146.574 33.65 L 146.574 27.387 L 150.516 27.387 C 152.587 27.387 153.764 29.075 153.764 30.519 C 153.764 31.935 152.587 33.65 150.516 33.65 Z M 150.445 24.844 L 143.804 24.844 L 143.804 43.115 L 146.574 43.115 L 146.574 36.194 L 150.445 36.194 C 153.516 36.194 156.535 34.005 156.535 30.519 C 156.535 27.033 153.516 24.844 150.445 24.844 Z M 114.236 41.044 C 112.321 41.044 110.719 39.465 110.719 37.3 C 110.719 35.109 112.321 33.509 114.236 33.509 C 116.126 33.509 117.609 35.109 117.609 37.3 C 117.609 39.465 116.126 41.044 114.236 41.044 Z M 117.418 32.449 L 117.322 32.449 C 116.7 31.719 115.503 31.06 113.996 31.06 C 110.838 31.06 107.943 33.792 107.943 37.3 C 107.943 40.785 110.838 43.492 113.996 43.492 C 115.503 43.492 116.7 42.833 117.322 42.08 L 117.418 42.08 L 117.418 42.974 C 117.418 45.353 116.126 46.624 114.044 46.624 C 112.346 46.624 111.293 45.423 110.863 44.41 L 108.446 45.399 C 109.139 47.047 110.982 49.073 114.044 49.073 C 117.299 49.073 120.05 47.189 120.05 42.597 L 120.05 31.437 L 117.418 31.437 Z M 121.964 43.115 L 124.738 43.115 L 124.738 24.844 L 121.964 24.844 Z M 128.83 37.088 C 128.759 34.686 130.721 33.461 132.132 33.461 C 133.233 33.461 134.166 34.003 134.477 34.78 Z M 137.443 35.016 C 136.917 33.627 135.314 31.06 132.036 31.06 C 128.782 31.06 126.079 33.579 126.079 37.276 C 126.079 40.761 128.759 43.492 132.347 43.492 C 135.242 43.492 136.917 41.75 137.611 40.738 L 135.458 39.324 C 134.74 40.361 133.76 41.044 132.347 41.044 C 130.936 41.044 129.93 40.408 129.286 39.159 L 137.73 35.722 Z M 70.158 32.967 L 70.158 35.604 L 76.569 35.604 C 76.378 37.088 75.875 38.171 75.11 38.925 C 74.177 39.843 72.717 40.855 70.158 40.855 C 66.21 40.855 63.124 37.723 63.124 33.839 C 63.124 29.954 66.21 26.821 70.158 26.821 C 72.287 26.821 73.842 27.645 74.99 28.706 L 76.881 26.845 C 75.277 25.338 73.149 24.185 70.158 24.185 C 64.749 24.185 60.204 28.518 60.204 33.839 C 60.204 39.159 64.749 43.492 70.158 43.492 C 73.076 43.492 75.277 42.55 77 40.785 C 78.77 39.042 79.321 36.593 79.321 34.615 C 79.321 34.003 79.272 33.438 79.177 32.967 Z M 86.611 41.044 C 84.696 41.044 83.045 39.49 83.045 37.276 C 83.045 35.039 84.696 33.509 86.611 33.509 C 88.524 33.509 90.175 35.039 90.175 37.276 C 90.175 39.49 88.524 41.044 86.611 41.044 Z M 86.611 31.06 C 83.117 31.06 80.27 33.674 80.27 37.276 C 80.27 40.855 83.117 43.492 86.611 43.492 C 90.103 43.492 92.951 40.855 92.951 37.276 C 92.951 33.674 90.103 31.06 86.611 31.06 Z M 100.441 41.044 C 98.528 41.044 96.876 39.49 96.876 37.276 C 96.876 35.039 98.528 33.509 100.441 33.509 C 102.355 33.509 104.006 35.039 104.006 37.276 C 104.006 39.49 102.355 41.044 100.441 41.044 Z M 100.441 31.06 C 96.948 31.06 94.101 33.674 94.101 37.276 C 94.101 40.855 96.948 43.492 100.441 43.492 C 103.934 43.492 106.782 40.855 106.782 37.276 C 106.782 33.674 103.934 31.06 100.441 31.06 Z M 65.309 18.612 C 63.977 18.612 62.832 18.15 61.909 17.242 C 60.985 16.333 60.517 15.194 60.517 13.871 C 60.517 12.547 60.985 11.411 61.909 10.5 C 62.832 9.591 63.977 9.13 65.309 9.13 C 65.987 9.13 66.619 9.246 67.224 9.485 C 67.83 9.725 68.325 10.059 68.715 10.495 L 68.806 10.598 L 67.768 11.62 L 67.665 11.495 C 67.406 11.182 67.077 10.939 66.66 10.763 C 66.245 10.586 65.791 10.505 65.309 10.505 C 64.371 10.505 63.593 10.818 62.942 11.456 C 62.307 12.105 61.988 12.897 61.988 13.871 C 61.988 14.845 62.308 15.637 62.943 16.287 C 63.593 16.925 64.371 17.239 65.309 17.239 C 66.167 17.239 66.877 17.004 67.435 16.537 C 67.955 16.102 68.267 15.515 68.377 14.766 L 65.16 14.766 L 65.16 13.419 L 69.768 13.419 L 69.788 13.542 C 69.825 13.779 69.852 14.007 69.852 14.228 C 69.852 15.485 69.462 16.511 68.699 17.266 C 67.835 18.166 66.702 18.612 65.309 18.612 Z M 105.319 18.612 C 103.986 18.612 102.853 18.15 101.942 17.242 C 101.942 17.242 101.941 17.242 101.941 17.241 C 101.029 16.343 100.576 15.205 100.576 13.871 C 100.576 12.537 101.029 11.399 101.941 10.501 C 101.941 10.5 101.941 10.5 101.941 10.5 C 102.852 9.592 103.986 9.13 105.319 9.13 C 106.65 9.13 107.782 9.592 108.694 10.513 C 109.606 11.412 110.06 12.548 110.06 13.871 C 110.06 15.205 109.606 16.343 108.694 17.241 C 107.782 18.15 106.639 18.612 105.319 18.612 Z M 71.171 18.415 L 71.171 9.327 L 71.319 9.327 L 76.686 9.327 L 76.686 10.701 L 72.616 10.701 L 72.616 13.198 L 76.287 13.198 L 76.287 14.544 L 72.616 14.544 L 72.616 17.043 L 76.686 17.043 L 76.686 18.415 Z M 80.061 18.415 L 80.061 10.701 L 77.564 10.701 L 77.564 9.327 L 84.004 9.327 L 84.004 10.701 L 83.855 10.701 L 81.507 10.701 L 81.507 18.415 Z M 88.119 18.415 L 88.119 9.327 L 89.565 9.327 L 89.565 9.473 L 89.565 18.415 Z M 93.204 18.415 L 93.204 10.701 L 90.707 10.701 L 90.707 9.327 L 97.147 9.327 L 97.147 10.701 L 96.998 10.701 L 94.65 10.701 L 94.65 18.415 Z M 111.343 18.415 L 111.343 9.327 L 112.973 9.327 L 117.122 15.863 L 117.086 14.622 L 117.086 9.327 L 118.532 9.327 L 118.532 18.415 L 117.101 18.415 L 112.753 11.549 L 112.788 12.788 L 112.788 12.79 L 112.788 18.415 Z M 105.319 17.239 C 106.257 17.239 107.022 16.925 107.646 16.288 L 107.647 16.287 L 107.648 16.286 C 108.281 15.664 108.591 14.861 108.591 13.871 C 108.591 12.883 108.281 12.078 107.648 11.456 L 107.647 11.455 L 107.646 11.454 C 107.022 10.817 106.257 10.505 105.319 10.505 C 104.379 10.505 103.614 10.817 102.978 11.453 L 102.978 11.454 C 102.356 12.092 102.047 12.883 102.047 13.871 C 102.047 14.861 102.356 15.65 102.978 16.288 C 103.614 16.925 104.38 17.239 105.319 17.239 Z M 30.02 27.657 L 14.209 44.173 C 14.21 44.175 14.211 44.178 14.211 44.182 C 14.696 45.975 16.361 47.296 18.336 47.296 C 19.126 47.296 19.867 47.086 20.503 46.717 L 20.553 46.687 L 38.351 36.581 Z M 46.018 24.845 L 46.003 24.835 L 38.319 20.452 L 29.663 28.032 L 38.35 36.579 L 45.993 32.24 C 47.332 31.528 48.242 30.138 48.242 28.534 C 48.242 26.943 47.344 25.559 46.018 24.845 Z M 14.206 12.827 C 14.111 13.172 14.062 13.534 14.062 13.909 L 14.062 43.092 C 14.062 43.467 14.111 43.83 14.207 44.173 L 30.562 28.08 Z M 30.138 28.5 L 38.321 20.449 L 20.545 10.305 C 19.898 9.924 19.144 9.705 18.337 9.705 C 16.362 9.705 14.695 11.027 14.21 12.823 C 14.209 12.825 14.209 12.825 14.209 12.827 Z%22 fill=%22transparent%22 height=%2257px%22 id=%22R56oJ523S%22 transform=%22translate(1 1)%22 width=%22199px%22><path d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z%22 fill=%22rgb(0, 0, 0)%22 height=%2257px%22 id=%22PoPW8TCll%22 width=%22199px%22/><path d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z%22 fill=%22transparent%22 height=%2257px%22 id=%22h1k_KDtzz%22 stroke-dasharray=%22%22 stroke-linecap=%22butt%22 stroke-linejoin=%22miter%22 stroke-miterlimit=%2210%22 stroke-width=%221.5%22 stroke=%22rgb(166, 166, 166)%22 width=%22199px%22/><path d=%22M 97.858 18.931 L 100.63 18.931 L 100.63 0.66 L 97.858 0.66 Z M 122.819 7.241 L 119.643 15.163 L 119.548 15.163 L 116.251 7.241 L 113.266 7.241 L 118.211 18.313 L 115.39 24.472 L 118.28 24.472 L 125.901 7.241 Z M 107.103 16.855 C 106.197 16.855 104.929 16.408 104.929 15.304 C 104.929 13.893 106.507 13.353 107.867 13.353 C 109.085 13.353 109.66 13.611 110.399 13.964 C 110.184 15.657 108.703 16.855 107.103 16.855 Z M 107.437 6.842 C 105.431 6.842 103.353 7.712 102.494 9.639 L 104.953 10.65 C 105.479 9.639 106.457 9.31 107.485 9.31 C 108.918 9.31 110.375 10.156 110.398 11.661 L 110.398 11.848 C 109.897 11.567 108.821 11.144 107.509 11.144 C 104.856 11.144 102.158 12.577 102.158 15.257 C 102.158 17.702 104.332 19.277 106.768 19.277 C 108.63 19.277 109.659 18.454 110.302 17.49 L 110.398 17.49 L 110.398 18.902 L 113.075 18.902 L 113.075 11.895 C 113.075 8.651 110.612 6.842 107.437 6.842 Z M 90.312 9.466 L 86.37 9.466 L 86.37 3.203 L 90.312 3.203 C 92.384 3.203 93.56 4.89 93.56 6.334 C 93.56 7.75 92.384 9.466 90.312 9.466 Z M 90.241 0.66 L 83.6 0.66 L 83.6 18.931 L 86.37 18.931 L 86.37 12.009 L 90.241 12.009 C 93.312 12.009 96.331 9.821 96.331 6.334 C 96.331 2.848 93.312 0.66 90.241 0.66 Z M 54.032 16.859 C 52.117 16.859 50.515 15.281 50.515 13.116 C 50.515 10.925 52.117 9.325 54.032 9.325 C 55.922 9.325 57.405 10.925 57.405 13.116 C 57.405 15.281 55.922 16.859 54.032 16.859 Z M 57.214 8.265 L 57.118 8.265 C 56.496 7.535 55.299 6.875 53.792 6.875 C 50.634 6.875 47.739 9.607 47.739 13.116 C 47.739 16.6 50.634 19.308 53.792 19.308 C 55.299 19.308 56.496 18.648 57.118 17.895 L 57.214 17.895 L 57.214 18.79 C 57.214 21.168 55.922 22.439 53.84 22.439 C 52.142 22.439 51.09 21.238 50.659 20.226 L 48.242 21.215 C 48.935 22.862 50.778 24.888 53.84 24.888 C 57.095 24.888 59.847 23.005 59.847 18.412 L 59.847 7.252 L 57.214 7.252 Z M 61.76 18.931 L 64.534 18.931 L 64.534 0.66 L 61.76 0.66 Z M 68.626 12.903 C 68.555 10.502 70.517 9.277 71.928 9.277 C 73.029 9.277 73.962 9.819 74.274 10.595 Z M 77.239 10.831 C 76.713 9.443 75.11 6.875 71.832 6.875 C 68.578 6.875 65.875 9.395 65.875 13.091 C 65.875 16.577 68.555 19.308 72.143 19.308 C 75.038 19.308 76.713 17.566 77.407 16.553 L 75.254 15.14 C 74.536 16.176 73.556 16.859 72.143 16.859 C 70.732 16.859 69.726 16.223 69.082 14.975 L 77.526 11.537 Z M 9.954 8.783 L 9.954 11.419 L 16.366 11.419 C 16.174 12.903 15.671 13.986 14.906 14.74 C 13.973 15.658 12.513 16.67 9.954 16.67 C 6.006 16.67 2.92 13.539 2.92 9.654 C 2.92 5.769 6.006 2.637 9.954 2.637 C 12.083 2.637 13.638 3.461 14.786 4.521 L 16.677 2.66 C 15.074 1.154 12.945 0 9.954 0 C 4.545 0 0 4.333 0 9.654 C 0 14.975 4.545 19.308 9.954 19.308 C 12.872 19.308 15.074 18.366 16.796 16.6 C 18.566 14.857 19.117 12.408 19.117 10.431 C 19.117 9.819 19.068 9.253 18.973 8.783 Z M 26.407 16.859 C 24.492 16.859 22.841 15.305 22.841 13.091 C 22.841 10.854 24.492 9.325 26.407 9.325 C 28.32 9.325 29.971 10.854 29.971 13.091 C 29.971 15.305 28.32 16.859 26.407 16.859 Z M 26.407 6.875 C 22.913 6.875 20.066 9.489 20.066 13.091 C 20.066 16.67 22.913 19.308 26.407 19.308 C 29.9 19.308 32.747 16.67 32.747 13.091 C 32.747 9.489 29.9 6.875 26.407 6.875 Z M 40.237 16.859 C 38.324 16.859 36.672 15.305 36.672 13.091 C 36.672 10.854 38.324 9.325 40.237 9.325 C 42.151 9.325 43.802 10.854 43.802 13.091 C 43.802 15.305 42.151 16.859 40.237 16.859 Z M 40.237 6.875 C 36.744 6.875 33.897 9.489 33.897 13.091 C 33.897 16.67 36.744 19.308 40.237 19.308 C 43.73 19.308 46.578 16.67 46.578 13.091 C 46.578 9.489 43.73 6.875 40.237 6.875 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2224.88805208343115px%22 id=%22VRx02W92J%22 transform=%22translate(60.204 24.185)%22 width=%22125.90066281355669px%22/><path d=%22M 4.792 9.482 C 3.46 9.482 2.315 9.021 1.392 8.113 C 0.468 7.203 0 6.064 0 4.741 C 0 3.417 0.468 2.281 1.392 1.37 C 2.315 0.461 3.46 0 4.792 0 C 5.47 0 6.103 0.116 6.708 0.356 C 7.313 0.595 7.809 0.93 8.199 1.365 L 8.29 1.468 L 7.251 2.49 L 7.148 2.365 C 6.889 2.052 6.56 1.809 6.143 1.633 C 5.728 1.457 5.274 1.375 4.792 1.375 C 3.854 1.375 3.076 1.689 2.426 2.326 C 1.79 2.976 1.471 3.767 1.471 4.741 C 1.471 5.716 1.791 6.508 2.427 7.158 C 3.076 7.795 3.854 8.11 4.792 8.11 C 5.65 8.11 6.36 7.874 6.918 7.407 C 7.438 6.973 7.75 6.385 7.86 5.637 L 4.643 5.637 L 4.643 4.289 L 9.251 4.289 L 9.271 4.412 C 9.308 4.65 9.335 4.878 9.335 5.098 C 9.335 6.355 8.945 7.381 8.182 8.136 C 7.318 9.036 6.185 9.482 4.792 9.482 Z M 44.802 9.482 C 43.469 9.482 42.337 9.021 41.426 8.113 C 41.426 8.113 41.425 8.113 41.425 8.112 C 40.512 7.213 40.059 6.075 40.059 4.741 C 40.059 3.407 40.512 2.269 41.425 1.371 C 41.425 1.37 41.425 1.37 41.425 1.37 C 42.336 0.462 43.469 0 44.802 0 C 46.133 0 47.265 0.462 48.177 1.384 C 49.09 2.282 49.543 3.418 49.543 4.741 C 49.543 6.075 49.09 7.213 48.177 8.112 C 47.265 9.021 46.122 9.482 44.802 9.482 Z M 10.654 9.286 L 10.654 0.197 L 10.802 0.197 L 16.169 0.197 L 16.169 1.572 L 12.099 1.572 L 12.099 4.068 L 15.771 4.068 L 15.771 5.415 L 12.099 5.415 L 12.099 7.913 L 16.169 7.913 L 16.169 9.286 Z M 19.545 9.286 L 19.545 1.572 L 17.048 1.572 L 17.048 0.197 L 23.487 0.197 L 23.487 1.572 L 23.338 1.572 L 20.99 1.572 L 20.99 9.286 Z M 27.603 9.286 L 27.603 0.197 L 29.048 0.197 L 29.048 0.343 L 29.048 9.286 Z M 32.687 9.286 L 32.687 1.572 L 30.191 1.572 L 30.191 0.197 L 36.63 0.197 L 36.63 1.572 L 36.481 1.572 L 34.133 1.572 L 34.133 9.286 Z M 50.826 9.286 L 50.826 0.197 L 52.456 0.197 L 56.605 6.733 L 56.569 5.492 L 56.569 0.197 L 58.015 0.197 L 58.015 9.286 L 56.584 9.286 L 52.236 2.419 L 52.272 3.659 L 52.272 3.661 L 52.272 9.286 Z M 44.802 8.11 C 45.74 8.11 46.506 7.795 47.129 7.159 L 47.13 7.158 L 47.131 7.157 C 47.764 6.534 48.074 5.731 48.074 4.741 C 48.074 3.753 47.764 2.948 47.131 2.326 L 47.13 2.325 L 47.129 2.324 C 46.506 1.688 45.74 1.375 44.802 1.375 C 43.862 1.375 43.097 1.688 42.461 2.323 L 42.461 2.324 C 41.84 2.962 41.531 3.753 41.531 4.741 C 41.531 5.731 41.84 6.52 42.461 7.159 C 43.097 7.795 43.863 8.11 44.802 8.11 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.48246153846153px%22 id=%22tGaJPZoet%22 transform=%22translate(60.517 9.13)%22 width=%2258.014938377570175px%22/><path d=%22M 15.811 0 L 0 16.515 C 0.001 16.518 0.002 16.521 0.002 16.525 C 0.487 18.318 2.151 19.639 4.127 19.639 C 4.917 19.639 5.658 19.429 6.294 19.059 L 6.344 19.03 L 24.141 8.924 Z%22 fill=%22rgb(234, 67, 53)%22 height=%2219.639179487179494px%22 id=%22u8JhnVJkI%22 transform=%22translate(14.209 27.657)%22 width=%2224.141373214987368px%22/><path d=%22M 16.355 4.393 L 16.34 4.384 L 8.656 0 L 0 7.581 L 8.687 16.128 L 16.33 11.788 C 17.669 11.077 18.579 9.686 18.579 8.082 C 18.579 6.491 17.681 5.108 16.355 4.393 Z%22 fill=%22rgb(251, 188, 4)%22 height=%2216.127589743589745px%22 id=%22l0goY9u0b%22 transform=%22translate(29.663 20.452)%22 width=%2218.579273631840792px%22/><path d=%22M 0.145 0 C 0.05 0.345 0 0.706 0 1.082 L 0 30.265 C 0 30.64 0.05 31.002 0.146 31.345 L 16.5 15.253 Z%22 fill=%22rgb(66, 133, 244)%22 height=%2231.345128205128226px%22 id=%22j_ASOP5W1%22 transform=%22translate(14.062 12.827)%22 width=%2216.500169154228853px%22/><path d=%22M 15.929 18.795 L 24.112 10.744 L 6.335 0.6 C 5.689 0.219 4.934 0 4.128 0 C 2.152 0 0.486 1.322 0.001 3.118 C 0 3.12 0 3.121 0 3.123 Z%22 fill=%22rgb(52, 168, 83)%22 height=%2218.795384615384606px%22 id=%22QO6a9HaeR%22 transform=%22translate(14.209 9.705)%22 width=%2224.111671722450055px%22/></g></svg>"
                            />
                          </a>
                          {/*/$*/}
                        </div>
                      </div>
                      <div className="framer-upau9f-container scallopplus-s172">
                        {/*$*/}
                        <div className="scallopplus-s173">
                          <div className="scallopplus-s174">
                            <div className="scallopplus-s175">
                              <div className="scallopplus-s102">
                                <div />
                              </div>
                            </div>
                          </div>
                        </div>
                        {/*/$*/}
                      </div>
                      <div
                        className="framer-snkriq"
                        data-framer-name="Image Wrapper"
                      >
                        <div
                          className="framer-mzxoip scallopplus-s176"
                          data-framer-name="(Replace 2608x1855 Image) Screenshot"
                          tabIndex={0}
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              loading="lazy"
                              width="1024"
                              height="880"
                              sizes="(min-width: 1440px) 685px, (min-width: 810px) and (max-width: 1439.98px) 482px"
                              srcSet="/framerusercontent.com/images/Ra6gAwrRiHXlnb1P0399RrvcEHs.png 512w,/framerusercontent.com/images/Ra6gAwrRiHXlnb1P0399RrvcEHs.png 1024w"
                              src="/framerusercontent.com/images/Ra6gAwrRiHXlnb1P0399RrvcEHs.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
              <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                <div className="framer-1pqfmc-container" id="cta">
                  <section
                    className="framer-WvUDv framer-p2e38s framer-v-o6b5s9 scallopplus-s3"
                    data-framer-name="Phone"
                  >
                    <div
                      className="framer-68oizx scallopplus-s177"
                      data-framer-name="CTA Wrapper"
                    >
                      <div
                        className="framer-1j3tetx"
                        data-framer-name="Content Wrapper"
                      >
                        <div className="framer-1kvw61i" data-framer-name="Top">
                          <div
                            className="framer-1u5643w scallopplus-s168 reveal"
                            data-framer-name="Choose how you use your account"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s178"
                            >
                              Land connected on your next trip
                            </p>
                          </div>
                          <div
                            className="framer-1n8ovd7 scallopplus-s170 reveal"
                            data-framer-name="More features, higher limits, and priority access "
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s179"
                            >
                              <span className="framer-text scallopplus-s180">
                                Set up your eSIM in minutes. Pick a plan when
                                you need it.{' '}
                              </span>
                              <br className="framer-text" />
                              <span className="framer-text scallopplus-s180">
                                Pay the way you want.
                              </span>
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-x3nt5l scallopplus-s85"
                          data-framer-appear-id="x3nt5l"
                        >
                          {/*$*/}
                          <a
                            className="framer-1wusars framer-1r6wjfa"
                            data-framer-name="Appstore"
                            href="https://apps.apple.com/app/scallop/id6746711366"
                            target="_blank"
                            rel="noopener"
                          >
                            <img
                              alt=""
                              className="framer-fhIeM framer-rpojg6"
                              src="data:image/svg+xml,<svg display=%22block%22 role=%22presentation%22 viewBox=%220 0 180 60%22 xmlns=%22http://www.w3.org/2000/svg%22><g d=%22M 0 60 L 0 0 L 180 0 L 180 60 Z M 169.5 0.75 L 10.5 0.75 C 5.115 0.75 0.75 5.115 0.75 10.5 L 0.75 49.5 C 0.75 54.885 5.115 59.25 10.5 59.25 L 169.5 59.25 C 174.885 59.25 179.25 54.885 179.25 49.5 L 179.25 10.5 C 179.25 5.115 174.885 0.75 169.5 0.75 Z M 169.5 0.75 L 10.5 0.75 C 5.115 0.75 0.75 5.115 0.75 10.5 L 0.75 49.5 C 0.75 54.885 5.115 59.25 10.5 59.25 L 169.5 59.25 C 174.885 59.25 179.25 54.885 179.25 49.5 L 179.25 10.5 C 179.25 5.115 174.885 0.75 169.5 0.75 Z M 122.291 28.802 L 122.291 32.239 L 120.137 32.239 L 120.137 34.492 L 122.291 34.492 L 122.291 42.15 C 122.291 44.764 123.474 45.81 126.449 45.81 C 126.972 45.81 127.471 45.748 127.906 45.673 L 127.906 43.445 C 127.533 43.482 127.296 43.507 126.885 43.507 C 125.553 43.507 124.968 42.884 124.968 41.465 L 124.968 34.492 L 127.906 34.492 L 127.906 32.239 L 124.968 32.239 L 124.968 28.802 Z M 135.486 45.997 C 139.445 45.997 141.873 43.345 141.873 38.95 C 141.873 34.579 139.433 31.915 135.486 31.915 C 131.526 31.915 129.086 34.579 129.086 38.95 C 129.086 43.345 131.514 45.997 135.486 45.997 Z M 135.486 43.619 C 133.158 43.619 131.85 41.913 131.85 38.95 C 131.85 36.011 133.158 34.293 135.486 34.293 C 137.802 34.293 139.122 36.011 139.122 38.95 C 139.122 41.901 137.802 43.619 135.486 43.619 Z M 143.949 45.736 L 146.626 45.736 L 146.626 37.73 C 146.626 35.825 148.058 34.542 150.088 34.542 C 150.56 34.542 151.358 34.629 151.582 34.704 L 151.582 32.064 C 151.295 31.989 150.785 31.952 150.386 31.952 C 148.618 31.952 147.112 32.923 146.726 34.256 L 146.527 34.256 L 146.527 32.176 L 143.949 32.176 Z M 158.229 34.193 C 160.209 34.193 161.504 35.576 161.567 37.705 L 154.718 37.705 C 154.868 35.588 156.249 34.193 158.229 34.193 Z M 161.555 42.075 C 161.057 43.133 159.948 43.719 158.33 43.719 C 156.188 43.719 154.806 42.212 154.718 39.834 L 154.718 39.684 L 164.294 39.684 L 164.294 38.751 C 164.294 34.492 162.015 31.915 158.243 31.915 C 154.419 31.915 151.992 34.667 151.992 39 C 151.992 43.333 154.37 45.997 158.255 45.997 C 161.355 45.997 163.521 44.503 164.132 42.075 Z M 104.734 40.728 C 104.941 44.058 107.716 46.187 111.845 46.187 C 116.259 46.187 119.021 43.955 119.021 40.393 C 119.021 37.592 117.446 36.044 113.626 35.153 L 111.574 34.65 C 109.148 34.082 108.167 33.321 108.167 31.992 C 108.167 30.314 109.69 29.217 111.974 29.217 C 114.142 29.217 115.639 30.288 115.91 32.005 L 118.724 32.005 C 118.556 28.868 115.794 26.662 112.013 26.662 C 107.948 26.662 105.238 28.868 105.238 32.185 C 105.238 34.921 106.773 36.547 110.142 37.334 L 112.542 37.915 C 115.007 38.496 116.091 39.348 116.091 40.767 C 116.091 42.419 114.388 43.619 112.065 43.619 C 109.574 43.619 107.845 42.496 107.599 40.728 Z M 77.004 31.952 C 75.161 31.952 73.567 32.874 72.745 34.418 L 72.546 34.418 L 72.546 32.176 L 69.969 32.176 L 69.969 50.243 L 72.646 50.243 L 72.646 43.681 L 72.857 43.681 C 73.567 45.113 75.099 45.96 77.029 45.96 C 80.453 45.96 82.632 43.258 82.632 38.95 C 82.632 34.642 80.453 31.952 77.004 31.952 Z M 76.244 43.557 C 74.003 43.557 72.596 41.789 72.596 38.962 C 72.596 36.123 74.003 34.355 76.257 34.355 C 78.523 34.355 79.88 36.086 79.88 38.95 C 79.88 41.826 78.523 43.557 76.244 43.557 Z M 91.996 31.952 C 90.153 31.952 88.559 32.874 87.737 34.418 L 87.538 34.418 L 87.538 32.176 L 84.961 32.176 L 84.961 50.243 L 87.638 50.243 L 87.638 43.681 L 87.849 43.681 C 88.559 45.113 90.091 45.96 92.021 45.96 C 95.445 45.96 97.624 43.258 97.624 38.95 C 97.624 34.642 95.445 31.952 91.996 31.952 Z M 91.236 43.557 C 88.995 43.557 87.588 41.789 87.588 38.962 C 87.588 36.123 88.995 34.355 91.249 34.355 C 93.515 34.355 94.872 36.086 94.872 38.95 C 94.872 41.826 93.515 43.557 91.236 43.557 Z M 65.166 45.736 L 68.237 45.736 L 61.513 27.113 L 58.403 27.113 L 51.68 45.736 L 54.648 45.736 L 56.364 40.793 L 63.462 40.793 Z M 59.81 30.495 L 60.029 30.495 L 62.727 38.367 L 57.1 38.367 Z M 53.477 13.066 L 53.477 22.05 L 56.72 22.05 C 59.397 22.05 60.947 20.4 60.947 17.53 C 60.947 14.703 59.385 13.066 56.72 13.066 Z M 54.871 14.336 L 56.565 14.336 C 58.426 14.336 59.528 15.519 59.528 17.548 C 59.528 19.609 58.445 20.779 56.565 20.779 L 54.871 20.779 Z M 65.696 22.18 C 67.676 22.18 68.89 20.854 68.89 18.657 C 68.89 16.472 67.67 15.139 65.696 15.139 C 63.716 15.139 62.496 16.472 62.496 18.657 C 62.496 20.854 63.71 22.18 65.696 22.18 Z M 65.696 20.991 C 64.532 20.991 63.878 20.138 63.878 18.657 C 63.878 17.188 64.532 16.328 65.696 16.328 C 66.854 16.328 67.514 17.188 67.514 18.657 C 67.514 20.132 66.854 20.991 65.696 20.991 Z M 79.227 15.27 L 77.888 15.27 L 76.68 20.443 L 76.574 20.443 L 75.18 15.27 L 73.897 15.27 L 72.503 20.443 L 72.403 20.443 L 71.189 15.27 L 69.832 15.27 L 71.7 22.05 L 73.076 22.05 L 74.47 17.057 L 74.576 17.057 L 75.977 22.05 L 77.365 22.05 Z M 80.77 22.05 L 82.108 22.05 L 82.108 18.084 C 82.108 17.026 82.737 16.366 83.727 16.366 C 84.717 16.366 85.19 16.907 85.19 17.997 L 85.19 22.05 L 86.528 22.05 L 86.528 17.661 C 86.528 16.048 85.694 15.139 84.181 15.139 C 83.16 15.139 82.488 15.594 82.158 16.347 L 82.058 16.347 L 82.058 15.27 L 80.77 15.27 Z M 88.637 22.049 L 89.975 22.049 L 89.975 12.624 L 88.637 12.624 Z M 95.009 22.18 C 96.988 22.18 98.202 20.854 98.202 18.657 C 98.202 16.472 96.982 15.139 95.009 15.139 C 93.029 15.139 91.809 16.472 91.809 18.657 C 91.809 20.854 93.023 22.18 95.009 22.18 Z M 95.009 20.991 C 93.844 20.991 93.191 20.138 93.191 18.657 C 93.191 17.188 93.844 16.328 95.009 16.328 C 96.167 16.328 96.826 17.188 96.826 18.657 C 96.826 20.132 96.167 20.991 95.009 20.991 Z M 102.188 21.035 C 101.46 21.035 100.931 20.68 100.931 20.07 C 100.931 19.472 101.354 19.155 102.288 19.093 L 103.944 18.987 L 103.944 19.553 C 103.944 20.394 103.197 21.035 102.188 21.035 Z M 101.846 22.162 C 102.736 22.162 103.477 21.776 103.882 21.097 L 103.987 21.097 L 103.987 22.05 L 105.276 22.05 L 105.276 17.418 C 105.276 15.986 104.317 15.139 102.618 15.139 C 101.08 15.139 99.984 15.886 99.847 17.05 L 101.142 17.05 C 101.292 16.571 101.808 16.297 102.555 16.297 C 103.471 16.297 103.944 16.702 103.944 17.418 L 103.944 18.003 L 102.107 18.109 C 100.495 18.208 99.586 18.912 99.586 20.132 C 99.586 21.371 100.538 22.162 101.846 22.162 Z M 109.818 22.162 C 110.752 22.162 111.542 21.719 111.947 20.979 L 112.053 20.979 L 112.053 22.049 L 113.335 22.049 L 113.335 12.624 L 111.997 12.624 L 111.997 16.347 L 111.897 16.347 C 111.53 15.6 110.746 15.158 109.818 15.158 C 108.106 15.158 107.004 16.515 107.004 18.656 C 107.004 20.804 108.093 22.162 109.818 22.162 Z M 110.198 16.359 C 111.318 16.359 112.022 17.25 112.022 18.663 C 112.022 20.082 111.325 20.96 110.198 20.96 C 109.065 20.96 108.386 20.095 108.386 18.656 C 108.386 17.231 109.071 16.359 110.198 16.359 Z M 122.016 22.18 C 123.996 22.18 125.21 20.854 125.21 18.657 C 125.21 16.472 123.99 15.139 122.016 15.139 C 120.037 15.139 118.816 16.472 118.816 18.657 C 118.816 20.854 120.031 22.18 122.016 22.18 Z M 122.016 20.991 C 120.852 20.991 120.199 20.138 120.199 18.657 C 120.199 17.188 120.852 16.328 122.016 16.328 C 123.174 16.328 123.834 17.188 123.834 18.657 C 123.834 20.132 123.174 20.991 122.016 20.991 Z M 126.98 22.05 L 128.319 22.05 L 128.319 18.084 C 128.319 17.026 128.948 16.366 129.938 16.366 C 130.927 16.366 131.401 16.907 131.401 17.997 L 131.401 22.05 L 132.739 22.05 L 132.739 17.661 C 132.739 16.048 131.905 15.139 130.392 15.139 C 129.371 15.139 128.699 15.594 128.369 16.347 L 128.269 16.347 L 128.269 15.27 L 126.98 15.27 Z M 138.905 13.583 L 138.905 15.301 L 137.828 15.301 L 137.828 16.428 L 138.905 16.428 L 138.905 20.257 C 138.905 21.564 139.497 22.087 140.984 22.087 C 141.246 22.087 141.495 22.056 141.713 22.018 L 141.713 20.904 C 141.526 20.923 141.408 20.935 141.202 20.935 C 140.536 20.935 140.244 20.624 140.244 19.914 L 140.244 16.428 L 141.713 16.428 L 141.713 15.301 L 140.244 15.301 L 140.244 13.583 Z M 143.512 22.049 L 144.85 22.049 L 144.85 18.09 C 144.85 17.063 145.46 16.372 146.556 16.372 C 147.502 16.372 148.007 16.92 148.007 18.003 L 148.007 22.049 L 149.345 22.049 L 149.345 17.673 C 149.345 16.06 148.455 15.145 147.011 15.145 C 145.99 15.145 145.274 15.6 144.944 16.359 L 144.838 16.359 L 144.838 12.624 L 143.512 12.624 Z M 154.173 16.279 C 155.163 16.279 155.811 16.97 155.843 18.034 L 152.418 18.034 C 152.493 16.976 153.183 16.279 154.173 16.279 Z M 155.835 20.219 C 155.586 20.749 155.033 21.041 154.223 21.041 C 153.153 21.041 152.462 20.288 152.418 19.099 L 152.418 19.024 L 157.205 19.024 L 157.205 18.557 C 157.205 16.428 156.066 15.139 154.179 15.139 C 152.268 15.139 151.055 16.515 151.055 18.682 C 151.055 20.848 152.244 22.18 154.185 22.18 C 155.736 22.18 156.819 21.433 157.124 20.219 Z M 37.153 30.452 C 37.185 27.93 38.54 25.544 40.688 24.224 C 39.333 22.288 37.063 21.061 34.701 20.987 C 32.182 20.723 29.74 22.494 28.457 22.494 C 27.148 22.494 25.172 21.013 23.044 21.057 C 20.27 21.147 17.684 22.724 16.335 25.149 C 13.434 30.172 15.597 37.553 18.376 41.613 C 19.767 43.601 21.392 45.822 23.518 45.743 C 25.598 45.657 26.375 44.416 28.887 44.416 C 31.375 44.416 32.104 45.743 34.273 45.693 C 36.506 45.657 37.913 43.696 39.254 41.689 C 40.253 40.273 41.022 38.707 41.532 37.05 C 38.908 35.941 37.156 33.3 37.153 30.452 Z M 33.057 18.317 C 34.274 16.856 34.874 14.977 34.729 13.081 C 32.869 13.276 31.151 14.165 29.917 15.571 C 28.711 16.943 28.083 18.789 28.202 20.613 C 30.062 20.632 31.891 19.767 33.057 18.317 Z%22 fill=%22transparent%22 height=%2260px%22 id=%22V_5LYGIvk%22 width=%22180px%22><path d=%22M 0 60 L 0 0 L 180 0 L 180 60 Z%22 fill=%22transparent%22 height=%2260px%22 id=%22kHaGt6RyA%22 width=%22180px%22/><path d=%22M 168.75 0 L 9.75 0 C 4.365 0 0 4.365 0 9.75 L 0 48.75 C 0 54.135 4.365 58.5 9.75 58.5 L 168.75 58.5 C 174.135 58.5 178.5 54.135 178.5 48.75 L 178.5 9.75 C 178.5 4.365 174.135 0 168.75 0 Z%22 fill=%22rgb(12, 13, 16)%22 height=%2258.5px%22 id=%22FEPf0vY4T%22 transform=%22translate(0.75 0.75)%22 width=%22178.5px%22/><path d=%22M 168.75 0 L 9.75 0 C 4.365 0 0 4.365 0 9.75 L 0 48.75 C 0 54.135 4.365 58.5 9.75 58.5 L 168.75 58.5 C 174.135 58.5 178.5 54.135 178.5 48.75 L 178.5 9.75 C 178.5 4.365 174.135 0 168.75 0 Z%22 fill=%22transparent%22 height=%2258.5px%22 id=%22R3Yc3UvLE%22 stroke-dasharray=%22%22 stroke-linecap=%22butt%22 stroke-linejoin=%22miter%22 stroke-miterlimit=%2210%22 stroke-width=%221.5%22 stroke=%22rgb(166, 166, 166)%22 transform=%22translate(0.75 0.75)%22 width=%22178.5px%22/><path d=%22M 2.154 0 L 2.154 3.437 L 0 3.437 L 0 5.69 L 2.154 5.69 L 2.154 13.348 C 2.154 15.962 3.337 17.008 6.312 17.008 C 6.835 17.008 7.334 16.946 7.769 16.871 L 7.769 14.643 C 7.396 14.68 7.159 14.705 6.748 14.705 C 5.416 14.705 4.831 14.082 4.831 12.663 L 4.831 5.69 L 7.769 5.69 L 7.769 3.437 L 4.831 3.437 L 4.831 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2217.008399999999998px%22 id=%22cNda9Ea2m%22 transform=%22translate(120.137 28.802)%22 width=%227.7690000000000055px%22/><path d=%22M 6.4 14.082 C 10.359 14.082 12.787 11.43 12.787 7.035 C 12.787 2.665 10.347 0 6.4 0 C 2.44 0 0 2.665 0 7.035 C 0 11.43 2.428 14.082 6.4 14.082 Z M 6.4 11.704 C 4.072 11.704 2.764 9.998 2.764 7.035 C 2.764 4.096 4.072 2.378 6.4 2.378 C 8.716 2.378 10.036 4.096 10.036 7.035 C 10.036 9.986 8.716 11.704 6.4 11.704 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2214.0823px%22 id=%22qqfvCfrtg%22 transform=%22translate(129.086 31.915)%22 width=%2212.786999999999978px%22/><path d=%22M 0 13.783 L 2.677 13.783 L 2.677 5.777 C 2.677 3.872 4.109 2.59 6.139 2.59 C 6.611 2.59 7.409 2.677 7.633 2.752 L 7.633 0.112 C 7.346 0.037 6.836 0 6.437 0 C 4.669 0 3.163 0.971 2.777 2.303 L 2.578 2.303 L 2.578 0.224 L 0 0.224 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2213.783499999999997px%22 id=%22ozncIZnPB%22 transform=%22translate(143.949 31.952)%22 width=%227.632999999999981px%22/><path d=%22M 6.237 2.279 C 8.217 2.279 9.512 3.661 9.575 5.79 L 2.726 5.79 C 2.876 3.673 4.257 2.279 6.237 2.279 Z M 9.563 10.16 C 9.065 11.218 7.956 11.804 6.338 11.804 C 4.196 11.804 2.814 10.297 2.726 7.919 L 2.726 7.77 L 12.302 7.77 L 12.302 6.836 C 12.302 2.577 10.023 0 6.251 0 C 2.427 0 0 2.752 0 7.085 C 0 11.418 2.378 14.082 6.263 14.082 C 9.363 14.082 11.529 12.588 12.14 10.16 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2214.082300000000004px%22 id=%22ChYaxBatK%22 transform=%22translate(151.992 31.915)%22 width=%2212.302000000000021px%22/><path d=%22M 0 14.067 C 0.207 17.396 2.982 19.526 7.111 19.526 C 11.525 19.526 14.287 17.293 14.287 13.731 C 14.287 10.931 12.712 9.382 8.892 8.492 L 6.84 7.988 C 4.414 7.42 3.433 6.659 3.433 5.33 C 3.433 3.652 4.956 2.555 7.24 2.555 C 9.408 2.555 10.905 3.626 11.176 5.343 L 13.99 5.343 C 13.822 2.207 11.06 0 7.279 0 C 3.214 0 0.504 2.207 0.504 5.523 C 0.504 8.26 2.039 9.886 5.408 10.673 L 7.808 11.254 C 10.273 11.834 11.357 12.686 11.357 14.106 C 11.357 15.757 9.654 16.958 7.331 16.958 C 4.84 16.958 3.111 15.835 2.865 14.067 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2219.5257px%22 id=%22IjThgD9dd%22 transform=%22translate(104.734 26.662)%22 width=%2214.287000000000006px%22/><path d=%22M 7.035 0 C 5.192 0 3.598 0.921 2.776 2.465 L 2.577 2.465 L 2.577 0.224 L 0 0.224 L 0 18.291 L 2.677 18.291 L 2.677 11.729 L 2.888 11.729 C 3.598 13.161 5.13 14.008 7.06 14.008 C 10.484 14.008 12.663 11.306 12.663 6.998 C 12.663 2.689 10.484 0 7.035 0 Z M 6.275 11.605 C 4.034 11.605 2.627 9.837 2.627 7.01 C 2.627 4.171 4.034 2.403 6.288 2.403 C 8.554 2.403 9.911 4.134 9.911 6.998 C 9.911 9.874 8.554 11.605 6.275 11.605 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.290799999999997px%22 id=%22iVaLanCV_%22 transform=%22translate(69.969 31.952)%22 width=%2212.662800000000004px%22/><path d=%22M 7.035 0 C 5.192 0 3.598 0.921 2.776 2.465 L 2.577 2.465 L 2.577 0.224 L 0 0.224 L 0 18.291 L 2.677 18.291 L 2.677 11.729 L 2.889 11.729 C 3.598 13.161 5.13 14.008 7.06 14.008 C 10.484 14.008 12.663 11.306 12.663 6.998 C 12.663 2.689 10.484 0 7.035 0 Z M 6.275 11.605 C 4.034 11.605 2.627 9.837 2.627 7.01 C 2.627 4.171 4.034 2.403 6.288 2.403 C 8.554 2.403 9.911 4.134 9.911 6.998 C 9.911 9.874 8.554 11.605 6.275 11.605 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.290799999999997px%22 id=%22wCMU1UaNq%22 transform=%22translate(84.961 31.952)%22 width=%2212.662900000000008px%22/><path d=%22M 13.486 18.622 L 16.557 18.622 L 9.834 0 L 6.724 0 L 0 18.622 L 2.968 18.622 L 4.685 13.68 L 11.783 13.68 Z M 8.13 3.381 L 8.35 3.381 L 11.047 11.253 L 5.42 11.253 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.6223px%22 id=%22dv51fN_AU%22 transform=%22translate(51.68 27.113)%22 width=%2216.5574px%22/><path d=%22M 0 0 L 0 8.984 L 3.244 8.984 C 5.921 8.984 7.471 7.334 7.471 4.464 C 7.471 1.637 5.908 0 3.244 0 Z M 1.395 1.27 L 3.088 1.27 C 4.949 1.27 6.051 2.453 6.051 4.482 C 6.051 6.543 4.968 7.713 3.088 7.713 L 1.395 7.713 Z%22 fill=%22rgb(255, 255, 255)%22 height=%228.983600000000001px%22 id=%22kgSqGhbT6%22 transform=%22translate(53.477 13.066)%22 width=%227.470700000000001px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.22 0 0 1.332 0 3.517 C 0 5.715 1.214 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.382 4.999 1.382 3.517 C 1.382 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22X6oepGtxA%22 transform=%22translate(62.496 15.139)%22 width=%226.393600000000006px%22/><path d=%22M 9.395 0 L 8.056 0 L 6.848 5.174 L 6.742 5.174 L 5.348 0 L 4.065 0 L 2.671 5.174 L 2.571 5.174 L 1.357 0 L 0 0 L 1.868 6.78 L 3.244 6.78 L 4.638 1.787 L 4.744 1.787 L 6.145 6.78 L 7.533 6.78 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.779699999999998px%22 id=%22J5qrRVDfp%22 transform=%22translate(69.832 15.27)%22 width=%229.394500000000008px%22/><path d=%22M 0 6.91 L 1.339 6.91 L 1.339 2.945 C 1.339 1.886 1.967 1.226 2.957 1.226 C 3.947 1.226 4.42 1.768 4.42 2.857 L 4.42 6.91 L 5.759 6.91 L 5.759 2.521 C 5.759 0.909 4.925 0 3.412 0 C 2.391 0 1.718 0.454 1.388 1.208 L 1.289 1.208 L 1.289 0.131 L 0 0.131 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.910400000000001px%22 id=%22cRWG_5cD1%22 transform=%22translate(80.769 15.139)%22 width=%225.758700000000005px%22/><path d=%22M 0 9.425 L 1.339 9.425 L 1.339 0 L 0 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.425499999999998px%22 id=%22eJ7ddPydU%22 transform=%22translate(88.637 12.624)%22 width=%221.3385999999999996px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.22 0 0 1.332 0 3.517 C 0 5.715 1.214 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.382 4.999 1.382 3.517 C 1.382 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22JDAra7JIl%22 transform=%22translate(91.809 15.139)%22 width=%226.393600000000006px%22/><path d=%22M 2.602 5.896 C 1.874 5.896 1.345 5.541 1.345 4.931 C 1.345 4.333 1.768 4.015 2.702 3.953 L 4.358 3.847 L 4.358 4.414 C 4.358 5.254 3.611 5.896 2.602 5.896 Z M 2.26 7.022 C 3.15 7.022 3.891 6.636 4.296 5.958 L 4.401 5.958 L 4.401 6.91 L 5.69 6.91 L 5.69 2.278 C 5.69 0.847 4.731 0 3.032 0 C 1.494 0 0.398 0.747 0.261 1.911 L 1.556 1.911 C 1.706 1.432 2.222 1.158 2.969 1.158 C 3.885 1.158 4.358 1.563 4.358 2.278 L 4.358 2.864 L 2.521 2.97 C 0.909 3.069 0 3.773 0 4.993 C 0 6.232 0.952 7.022 2.26 7.022 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.022399999999999px%22 id=%22e2IYJHcgl%22 transform=%22translate(99.586 15.139)%22 width=%225.690100000000001px%22/><path d=%22M 2.814 9.538 C 3.748 9.538 4.538 9.095 4.943 8.355 L 5.049 8.355 L 5.049 9.425 L 6.331 9.425 L 6.331 0 L 4.993 0 L 4.993 3.723 L 4.893 3.723 C 4.526 2.976 3.742 2.534 2.814 2.534 C 1.102 2.534 0 3.891 0 6.033 C 0 8.18 1.089 9.538 2.814 9.538 Z M 3.194 3.735 C 4.314 3.735 5.018 4.626 5.018 6.039 C 5.018 7.458 4.321 8.336 3.194 8.336 C 2.061 8.336 1.382 7.471 1.382 6.033 C 1.382 4.607 2.067 3.735 3.194 3.735 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.5376px%22 id=%22K5cX3nCIG%22 transform=%22translate(107.004 12.624)%22 width=%226.330999999999989px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.221 0 0 1.332 0 3.517 C 0 5.715 1.215 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.383 4.999 1.383 3.517 C 1.383 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22NIqN4iVvg%22 transform=%22translate(118.816 15.139)%22 width=%226.393999999999991px%22/><path d=%22M 0 6.91 L 1.339 6.91 L 1.339 2.945 C 1.339 1.886 1.968 1.226 2.958 1.226 C 3.947 1.226 4.421 1.768 4.421 2.857 L 4.421 6.91 L 5.759 6.91 L 5.759 2.521 C 5.759 0.909 4.925 0 3.412 0 C 2.391 0 1.719 0.454 1.389 1.208 L 1.289 1.208 L 1.289 0.131 L 0 0.131 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.910400000000001px%22 id=%22NTi0rfi1Y%22 transform=%22translate(126.98 15.139)%22 width=%225.759px%22/><path d=%22M 1.077 0 L 1.077 1.718 L 0 1.718 L 0 2.845 L 1.077 2.845 L 1.077 6.674 C 1.077 7.981 1.669 8.504 3.156 8.504 C 3.418 8.504 3.667 8.473 3.885 8.436 L 3.885 7.321 C 3.698 7.34 3.58 7.352 3.374 7.352 C 2.708 7.352 2.416 7.041 2.416 6.331 L 2.416 2.845 L 3.885 2.845 L 3.885 1.718 L 2.416 1.718 L 2.416 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%228.5041px%22 id=%22Nos9Cp5Hh%22 transform=%22translate(137.828 13.583)%22 width=%223.884999999999991px%22/><path d=%22M 0 9.425 L 1.338 9.425 L 1.338 5.466 C 1.338 4.439 1.948 3.748 3.044 3.748 C 3.99 3.748 4.495 4.296 4.495 5.379 L 4.495 9.425 L 5.833 9.425 L 5.833 5.049 C 5.833 3.437 4.943 2.521 3.499 2.521 C 2.478 2.521 1.762 2.976 1.432 3.735 L 1.326 3.735 L 1.326 0 L 0 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.425499999999998px%22 id=%22LVQbLUqUF%22 transform=%22translate(143.512 12.624)%22 width=%225.832999999999998px%22/><path d=%22M 3.118 1.139 C 4.108 1.139 4.756 1.83 4.788 2.895 L 1.363 2.895 C 1.438 1.836 2.128 1.139 3.118 1.139 Z M 4.78 5.08 C 4.531 5.609 3.978 5.902 3.168 5.902 C 2.098 5.902 1.407 5.149 1.363 3.959 L 1.363 3.885 L 6.15 3.885 L 6.15 3.418 C 6.15 1.289 5.011 0 3.124 0 C 1.213 0 0 1.376 0 3.542 C 0 5.709 1.189 7.041 3.13 7.041 C 4.681 7.041 5.764 6.294 6.069 5.08 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22vrbdmjN_S%22 transform=%22translate(151.055 15.139)%22 width=%226.150000000000006px%22/><path d=%22M 22.196 9.492 C 22.228 6.97 23.583 4.584 25.731 3.263 C 24.376 1.328 22.106 0.101 19.744 0.027 C 17.225 -0.237 14.783 1.534 13.5 1.534 C 12.191 1.534 10.215 0.053 8.087 0.097 C 5.313 0.187 2.727 1.764 1.378 4.189 C -1.523 9.211 0.64 16.593 3.419 20.653 C 4.81 22.641 6.435 24.862 8.561 24.783 C 10.641 24.696 11.418 23.456 13.93 23.456 C 16.418 23.456 17.147 24.783 19.316 24.733 C 21.549 24.696 22.956 22.736 24.297 20.729 C 25.296 19.312 26.065 17.747 26.575 16.09 C 23.951 14.98 22.199 12.34 22.196 9.492 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2224.78491613833904px%22 id=%22PM7rCRhKC%22 transform=%22translate(14.957 20.96)%22 width=%2226.574785466484332px%22/><path d=%22M 4.869 5.236 C 6.087 3.775 6.686 1.896 6.541 0 C 4.681 0.195 2.964 1.084 1.73 2.489 C 0.523 3.862 -0.105 5.708 0.014 7.532 C 1.875 7.551 3.703 6.686 4.869 5.236 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.531813143919933px%22 id=%22KMIzbdHNa%22 transform=%22translate(28.188 13.081)%22 width=%226.562434978114929px%22/></g></svg>"
                            />
                          </a>
                          {/*/$*/}
                          {/*$*/}
                          <a
                            className="framer-1mj9ybf framer-1r6wjfa"
                            data-framer-name="Playstore"
                            href="https://play.google.com/store/apps/details?id=com.app.scallop"
                            target="_blank"
                            rel="noopener"
                          >
                            <img
                              alt=""
                              className="framer-z0HLy framer-1u48p0z"
                              src="data:image/svg+xml,<svg display=%22block%22 role=%22presentation%22 viewBox=%220 0 201 59%22 xmlns=%22http://www.w3.org/2000/svg%22><g d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z M 158.062 43.115 L 160.834 43.115 L 160.834 24.844 L 158.062 24.844 Z M 183.023 31.426 L 179.846 39.348 L 179.751 39.348 L 176.455 31.426 L 173.47 31.426 L 178.415 42.498 L 175.594 48.657 L 178.484 48.657 L 186.105 31.426 Z M 167.307 41.04 C 166.401 41.04 165.133 40.593 165.133 39.489 C 165.133 38.078 166.711 37.537 168.071 37.537 C 169.289 37.537 169.864 37.795 170.603 38.148 C 170.388 39.842 168.907 41.04 167.307 41.04 Z M 167.641 31.027 C 165.635 31.027 163.557 31.897 162.698 33.824 L 165.157 34.834 C 165.683 33.824 166.661 33.495 167.689 33.495 C 169.122 33.495 170.579 34.34 170.602 35.846 L 170.602 36.033 C 170.1 35.751 169.025 35.328 167.712 35.328 C 165.06 35.328 162.362 36.762 162.362 39.441 C 162.362 41.887 164.536 43.461 166.972 43.461 C 168.834 43.461 169.863 42.639 170.506 41.674 L 170.602 41.674 L 170.602 43.086 L 173.279 43.086 L 173.279 36.08 C 173.279 32.836 170.816 31.027 167.641 31.027 Z M 150.516 33.65 L 146.574 33.65 L 146.574 27.387 L 150.516 27.387 C 152.587 27.387 153.764 29.075 153.764 30.519 C 153.764 31.935 152.587 33.65 150.516 33.65 Z M 150.445 24.844 L 143.804 24.844 L 143.804 43.115 L 146.574 43.115 L 146.574 36.194 L 150.445 36.194 C 153.516 36.194 156.535 34.005 156.535 30.519 C 156.535 27.033 153.516 24.844 150.445 24.844 Z M 114.236 41.044 C 112.321 41.044 110.719 39.465 110.719 37.3 C 110.719 35.109 112.321 33.509 114.236 33.509 C 116.126 33.509 117.609 35.109 117.609 37.3 C 117.609 39.465 116.126 41.044 114.236 41.044 Z M 117.418 32.449 L 117.322 32.449 C 116.7 31.719 115.503 31.06 113.996 31.06 C 110.838 31.06 107.943 33.792 107.943 37.3 C 107.943 40.785 110.838 43.492 113.996 43.492 C 115.503 43.492 116.7 42.833 117.322 42.08 L 117.418 42.08 L 117.418 42.974 C 117.418 45.353 116.126 46.624 114.044 46.624 C 112.346 46.624 111.293 45.423 110.863 44.41 L 108.446 45.399 C 109.139 47.047 110.982 49.073 114.044 49.073 C 117.299 49.073 120.05 47.189 120.05 42.597 L 120.05 31.437 L 117.418 31.437 Z M 121.964 43.115 L 124.738 43.115 L 124.738 24.844 L 121.964 24.844 Z M 128.83 37.088 C 128.759 34.686 130.721 33.461 132.132 33.461 C 133.233 33.461 134.166 34.003 134.477 34.78 Z M 137.443 35.016 C 136.917 33.627 135.314 31.06 132.036 31.06 C 128.782 31.06 126.079 33.579 126.079 37.276 C 126.079 40.761 128.759 43.492 132.347 43.492 C 135.242 43.492 136.917 41.75 137.611 40.738 L 135.458 39.324 C 134.74 40.361 133.76 41.044 132.347 41.044 C 130.936 41.044 129.93 40.408 129.286 39.159 L 137.73 35.722 Z M 70.158 32.967 L 70.158 35.604 L 76.569 35.604 C 76.378 37.088 75.875 38.171 75.11 38.925 C 74.177 39.843 72.717 40.855 70.158 40.855 C 66.21 40.855 63.124 37.723 63.124 33.839 C 63.124 29.954 66.21 26.821 70.158 26.821 C 72.287 26.821 73.842 27.645 74.99 28.706 L 76.881 26.845 C 75.277 25.338 73.149 24.185 70.158 24.185 C 64.749 24.185 60.204 28.518 60.204 33.839 C 60.204 39.159 64.749 43.492 70.158 43.492 C 73.076 43.492 75.277 42.55 77 40.785 C 78.77 39.042 79.321 36.593 79.321 34.615 C 79.321 34.003 79.272 33.438 79.177 32.967 Z M 86.611 41.044 C 84.696 41.044 83.045 39.49 83.045 37.276 C 83.045 35.039 84.696 33.509 86.611 33.509 C 88.524 33.509 90.175 35.039 90.175 37.276 C 90.175 39.49 88.524 41.044 86.611 41.044 Z M 86.611 31.06 C 83.117 31.06 80.27 33.674 80.27 37.276 C 80.27 40.855 83.117 43.492 86.611 43.492 C 90.103 43.492 92.951 40.855 92.951 37.276 C 92.951 33.674 90.103 31.06 86.611 31.06 Z M 100.441 41.044 C 98.528 41.044 96.876 39.49 96.876 37.276 C 96.876 35.039 98.528 33.509 100.441 33.509 C 102.355 33.509 104.006 35.039 104.006 37.276 C 104.006 39.49 102.355 41.044 100.441 41.044 Z M 100.441 31.06 C 96.948 31.06 94.101 33.674 94.101 37.276 C 94.101 40.855 96.948 43.492 100.441 43.492 C 103.934 43.492 106.782 40.855 106.782 37.276 C 106.782 33.674 103.934 31.06 100.441 31.06 Z M 65.309 18.612 C 63.977 18.612 62.832 18.15 61.909 17.242 C 60.985 16.333 60.517 15.194 60.517 13.871 C 60.517 12.547 60.985 11.411 61.909 10.5 C 62.832 9.591 63.977 9.13 65.309 9.13 C 65.987 9.13 66.619 9.246 67.224 9.485 C 67.83 9.725 68.325 10.059 68.715 10.495 L 68.806 10.598 L 67.768 11.62 L 67.665 11.495 C 67.406 11.182 67.077 10.939 66.66 10.763 C 66.245 10.586 65.791 10.505 65.309 10.505 C 64.371 10.505 63.593 10.818 62.942 11.456 C 62.307 12.105 61.988 12.897 61.988 13.871 C 61.988 14.845 62.308 15.637 62.943 16.287 C 63.593 16.925 64.371 17.239 65.309 17.239 C 66.167 17.239 66.877 17.004 67.435 16.537 C 67.955 16.102 68.267 15.515 68.377 14.766 L 65.16 14.766 L 65.16 13.419 L 69.768 13.419 L 69.788 13.542 C 69.825 13.779 69.852 14.007 69.852 14.228 C 69.852 15.485 69.462 16.511 68.699 17.266 C 67.835 18.166 66.702 18.612 65.309 18.612 Z M 105.319 18.612 C 103.986 18.612 102.853 18.15 101.942 17.242 C 101.942 17.242 101.941 17.242 101.941 17.241 C 101.029 16.343 100.576 15.205 100.576 13.871 C 100.576 12.537 101.029 11.399 101.941 10.501 C 101.941 10.5 101.941 10.5 101.941 10.5 C 102.852 9.592 103.986 9.13 105.319 9.13 C 106.65 9.13 107.782 9.592 108.694 10.513 C 109.606 11.412 110.06 12.548 110.06 13.871 C 110.06 15.205 109.606 16.343 108.694 17.241 C 107.782 18.15 106.639 18.612 105.319 18.612 Z M 71.171 18.415 L 71.171 9.327 L 71.319 9.327 L 76.686 9.327 L 76.686 10.701 L 72.616 10.701 L 72.616 13.198 L 76.287 13.198 L 76.287 14.544 L 72.616 14.544 L 72.616 17.043 L 76.686 17.043 L 76.686 18.415 Z M 80.061 18.415 L 80.061 10.701 L 77.564 10.701 L 77.564 9.327 L 84.004 9.327 L 84.004 10.701 L 83.855 10.701 L 81.507 10.701 L 81.507 18.415 Z M 88.119 18.415 L 88.119 9.327 L 89.565 9.327 L 89.565 9.473 L 89.565 18.415 Z M 93.204 18.415 L 93.204 10.701 L 90.707 10.701 L 90.707 9.327 L 97.147 9.327 L 97.147 10.701 L 96.998 10.701 L 94.65 10.701 L 94.65 18.415 Z M 111.343 18.415 L 111.343 9.327 L 112.973 9.327 L 117.122 15.863 L 117.086 14.622 L 117.086 9.327 L 118.532 9.327 L 118.532 18.415 L 117.101 18.415 L 112.753 11.549 L 112.788 12.788 L 112.788 12.79 L 112.788 18.415 Z M 105.319 17.239 C 106.257 17.239 107.022 16.925 107.646 16.288 L 107.647 16.287 L 107.648 16.286 C 108.281 15.664 108.591 14.861 108.591 13.871 C 108.591 12.883 108.281 12.078 107.648 11.456 L 107.647 11.455 L 107.646 11.454 C 107.022 10.817 106.257 10.505 105.319 10.505 C 104.379 10.505 103.614 10.817 102.978 11.453 L 102.978 11.454 C 102.356 12.092 102.047 12.883 102.047 13.871 C 102.047 14.861 102.356 15.65 102.978 16.288 C 103.614 16.925 104.38 17.239 105.319 17.239 Z M 30.02 27.657 L 14.209 44.173 C 14.21 44.175 14.211 44.178 14.211 44.182 C 14.696 45.975 16.361 47.296 18.336 47.296 C 19.126 47.296 19.867 47.086 20.503 46.717 L 20.553 46.687 L 38.351 36.581 Z M 46.018 24.845 L 46.003 24.835 L 38.319 20.452 L 29.663 28.032 L 38.35 36.579 L 45.993 32.24 C 47.332 31.528 48.242 30.138 48.242 28.534 C 48.242 26.943 47.344 25.559 46.018 24.845 Z M 14.206 12.827 C 14.111 13.172 14.062 13.534 14.062 13.909 L 14.062 43.092 C 14.062 43.467 14.111 43.83 14.207 44.173 L 30.562 28.08 Z M 30.138 28.5 L 38.321 20.449 L 20.545 10.305 C 19.898 9.924 19.144 9.705 18.337 9.705 C 16.362 9.705 14.695 11.027 14.21 12.823 C 14.209 12.825 14.209 12.825 14.209 12.827 Z%22 fill=%22transparent%22 height=%2257px%22 id=%22R56oJ523S%22 transform=%22translate(1 1)%22 width=%22199px%22><path d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z%22 fill=%22rgb(0, 0, 0)%22 height=%2257px%22 id=%22PoPW8TCll%22 width=%22199px%22/><path d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z%22 fill=%22transparent%22 height=%2257px%22 id=%22h1k_KDtzz%22 stroke-dasharray=%22%22 stroke-linecap=%22butt%22 stroke-linejoin=%22miter%22 stroke-miterlimit=%2210%22 stroke-width=%221.5%22 stroke=%22rgb(166, 166, 166)%22 width=%22199px%22/><path d=%22M 97.858 18.931 L 100.63 18.931 L 100.63 0.66 L 97.858 0.66 Z M 122.819 7.241 L 119.643 15.163 L 119.548 15.163 L 116.251 7.241 L 113.266 7.241 L 118.211 18.313 L 115.39 24.472 L 118.28 24.472 L 125.901 7.241 Z M 107.103 16.855 C 106.197 16.855 104.929 16.408 104.929 15.304 C 104.929 13.893 106.507 13.353 107.867 13.353 C 109.085 13.353 109.66 13.611 110.399 13.964 C 110.184 15.657 108.703 16.855 107.103 16.855 Z M 107.437 6.842 C 105.431 6.842 103.353 7.712 102.494 9.639 L 104.953 10.65 C 105.479 9.639 106.457 9.31 107.485 9.31 C 108.918 9.31 110.375 10.156 110.398 11.661 L 110.398 11.848 C 109.897 11.567 108.821 11.144 107.509 11.144 C 104.856 11.144 102.158 12.577 102.158 15.257 C 102.158 17.702 104.332 19.277 106.768 19.277 C 108.63 19.277 109.659 18.454 110.302 17.49 L 110.398 17.49 L 110.398 18.902 L 113.075 18.902 L 113.075 11.895 C 113.075 8.651 110.612 6.842 107.437 6.842 Z M 90.312 9.466 L 86.37 9.466 L 86.37 3.203 L 90.312 3.203 C 92.384 3.203 93.56 4.89 93.56 6.334 C 93.56 7.75 92.384 9.466 90.312 9.466 Z M 90.241 0.66 L 83.6 0.66 L 83.6 18.931 L 86.37 18.931 L 86.37 12.009 L 90.241 12.009 C 93.312 12.009 96.331 9.821 96.331 6.334 C 96.331 2.848 93.312 0.66 90.241 0.66 Z M 54.032 16.859 C 52.117 16.859 50.515 15.281 50.515 13.116 C 50.515 10.925 52.117 9.325 54.032 9.325 C 55.922 9.325 57.405 10.925 57.405 13.116 C 57.405 15.281 55.922 16.859 54.032 16.859 Z M 57.214 8.265 L 57.118 8.265 C 56.496 7.535 55.299 6.875 53.792 6.875 C 50.634 6.875 47.739 9.607 47.739 13.116 C 47.739 16.6 50.634 19.308 53.792 19.308 C 55.299 19.308 56.496 18.648 57.118 17.895 L 57.214 17.895 L 57.214 18.79 C 57.214 21.168 55.922 22.439 53.84 22.439 C 52.142 22.439 51.09 21.238 50.659 20.226 L 48.242 21.215 C 48.935 22.862 50.778 24.888 53.84 24.888 C 57.095 24.888 59.847 23.005 59.847 18.412 L 59.847 7.252 L 57.214 7.252 Z M 61.76 18.931 L 64.534 18.931 L 64.534 0.66 L 61.76 0.66 Z M 68.626 12.903 C 68.555 10.502 70.517 9.277 71.928 9.277 C 73.029 9.277 73.962 9.819 74.274 10.595 Z M 77.239 10.831 C 76.713 9.443 75.11 6.875 71.832 6.875 C 68.578 6.875 65.875 9.395 65.875 13.091 C 65.875 16.577 68.555 19.308 72.143 19.308 C 75.038 19.308 76.713 17.566 77.407 16.553 L 75.254 15.14 C 74.536 16.176 73.556 16.859 72.143 16.859 C 70.732 16.859 69.726 16.223 69.082 14.975 L 77.526 11.537 Z M 9.954 8.783 L 9.954 11.419 L 16.366 11.419 C 16.174 12.903 15.671 13.986 14.906 14.74 C 13.973 15.658 12.513 16.67 9.954 16.67 C 6.006 16.67 2.92 13.539 2.92 9.654 C 2.92 5.769 6.006 2.637 9.954 2.637 C 12.083 2.637 13.638 3.461 14.786 4.521 L 16.677 2.66 C 15.074 1.154 12.945 0 9.954 0 C 4.545 0 0 4.333 0 9.654 C 0 14.975 4.545 19.308 9.954 19.308 C 12.872 19.308 15.074 18.366 16.796 16.6 C 18.566 14.857 19.117 12.408 19.117 10.431 C 19.117 9.819 19.068 9.253 18.973 8.783 Z M 26.407 16.859 C 24.492 16.859 22.841 15.305 22.841 13.091 C 22.841 10.854 24.492 9.325 26.407 9.325 C 28.32 9.325 29.971 10.854 29.971 13.091 C 29.971 15.305 28.32 16.859 26.407 16.859 Z M 26.407 6.875 C 22.913 6.875 20.066 9.489 20.066 13.091 C 20.066 16.67 22.913 19.308 26.407 19.308 C 29.9 19.308 32.747 16.67 32.747 13.091 C 32.747 9.489 29.9 6.875 26.407 6.875 Z M 40.237 16.859 C 38.324 16.859 36.672 15.305 36.672 13.091 C 36.672 10.854 38.324 9.325 40.237 9.325 C 42.151 9.325 43.802 10.854 43.802 13.091 C 43.802 15.305 42.151 16.859 40.237 16.859 Z M 40.237 6.875 C 36.744 6.875 33.897 9.489 33.897 13.091 C 33.897 16.67 36.744 19.308 40.237 19.308 C 43.73 19.308 46.578 16.67 46.578 13.091 C 46.578 9.489 43.73 6.875 40.237 6.875 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2224.88805208343115px%22 id=%22VRx02W92J%22 transform=%22translate(60.204 24.185)%22 width=%22125.90066281355669px%22/><path d=%22M 4.792 9.482 C 3.46 9.482 2.315 9.021 1.392 8.113 C 0.468 7.203 0 6.064 0 4.741 C 0 3.417 0.468 2.281 1.392 1.37 C 2.315 0.461 3.46 0 4.792 0 C 5.47 0 6.103 0.116 6.708 0.356 C 7.313 0.595 7.809 0.93 8.199 1.365 L 8.29 1.468 L 7.251 2.49 L 7.148 2.365 C 6.889 2.052 6.56 1.809 6.143 1.633 C 5.728 1.457 5.274 1.375 4.792 1.375 C 3.854 1.375 3.076 1.689 2.426 2.326 C 1.79 2.976 1.471 3.767 1.471 4.741 C 1.471 5.716 1.791 6.508 2.427 7.158 C 3.076 7.795 3.854 8.11 4.792 8.11 C 5.65 8.11 6.36 7.874 6.918 7.407 C 7.438 6.973 7.75 6.385 7.86 5.637 L 4.643 5.637 L 4.643 4.289 L 9.251 4.289 L 9.271 4.412 C 9.308 4.65 9.335 4.878 9.335 5.098 C 9.335 6.355 8.945 7.381 8.182 8.136 C 7.318 9.036 6.185 9.482 4.792 9.482 Z M 44.802 9.482 C 43.469 9.482 42.337 9.021 41.426 8.113 C 41.426 8.113 41.425 8.113 41.425 8.112 C 40.512 7.213 40.059 6.075 40.059 4.741 C 40.059 3.407 40.512 2.269 41.425 1.371 C 41.425 1.37 41.425 1.37 41.425 1.37 C 42.336 0.462 43.469 0 44.802 0 C 46.133 0 47.265 0.462 48.177 1.384 C 49.09 2.282 49.543 3.418 49.543 4.741 C 49.543 6.075 49.09 7.213 48.177 8.112 C 47.265 9.021 46.122 9.482 44.802 9.482 Z M 10.654 9.286 L 10.654 0.197 L 10.802 0.197 L 16.169 0.197 L 16.169 1.572 L 12.099 1.572 L 12.099 4.068 L 15.771 4.068 L 15.771 5.415 L 12.099 5.415 L 12.099 7.913 L 16.169 7.913 L 16.169 9.286 Z M 19.545 9.286 L 19.545 1.572 L 17.048 1.572 L 17.048 0.197 L 23.487 0.197 L 23.487 1.572 L 23.338 1.572 L 20.99 1.572 L 20.99 9.286 Z M 27.603 9.286 L 27.603 0.197 L 29.048 0.197 L 29.048 0.343 L 29.048 9.286 Z M 32.687 9.286 L 32.687 1.572 L 30.191 1.572 L 30.191 0.197 L 36.63 0.197 L 36.63 1.572 L 36.481 1.572 L 34.133 1.572 L 34.133 9.286 Z M 50.826 9.286 L 50.826 0.197 L 52.456 0.197 L 56.605 6.733 L 56.569 5.492 L 56.569 0.197 L 58.015 0.197 L 58.015 9.286 L 56.584 9.286 L 52.236 2.419 L 52.272 3.659 L 52.272 3.661 L 52.272 9.286 Z M 44.802 8.11 C 45.74 8.11 46.506 7.795 47.129 7.159 L 47.13 7.158 L 47.131 7.157 C 47.764 6.534 48.074 5.731 48.074 4.741 C 48.074 3.753 47.764 2.948 47.131 2.326 L 47.13 2.325 L 47.129 2.324 C 46.506 1.688 45.74 1.375 44.802 1.375 C 43.862 1.375 43.097 1.688 42.461 2.323 L 42.461 2.324 C 41.84 2.962 41.531 3.753 41.531 4.741 C 41.531 5.731 41.84 6.52 42.461 7.159 C 43.097 7.795 43.863 8.11 44.802 8.11 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.48246153846153px%22 id=%22tGaJPZoet%22 transform=%22translate(60.517 9.13)%22 width=%2258.014938377570175px%22/><path d=%22M 15.811 0 L 0 16.515 C 0.001 16.518 0.002 16.521 0.002 16.525 C 0.487 18.318 2.151 19.639 4.127 19.639 C 4.917 19.639 5.658 19.429 6.294 19.059 L 6.344 19.03 L 24.141 8.924 Z%22 fill=%22rgb(234, 67, 53)%22 height=%2219.639179487179494px%22 id=%22u8JhnVJkI%22 transform=%22translate(14.209 27.657)%22 width=%2224.141373214987368px%22/><path d=%22M 16.355 4.393 L 16.34 4.384 L 8.656 0 L 0 7.581 L 8.687 16.128 L 16.33 11.788 C 17.669 11.077 18.579 9.686 18.579 8.082 C 18.579 6.491 17.681 5.108 16.355 4.393 Z%22 fill=%22rgb(251, 188, 4)%22 height=%2216.127589743589745px%22 id=%22l0goY9u0b%22 transform=%22translate(29.663 20.452)%22 width=%2218.579273631840792px%22/><path d=%22M 0.145 0 C 0.05 0.345 0 0.706 0 1.082 L 0 30.265 C 0 30.64 0.05 31.002 0.146 31.345 L 16.5 15.253 Z%22 fill=%22rgb(66, 133, 244)%22 height=%2231.345128205128226px%22 id=%22j_ASOP5W1%22 transform=%22translate(14.062 12.827)%22 width=%2216.500169154228853px%22/><path d=%22M 15.929 18.795 L 24.112 10.744 L 6.335 0.6 C 5.689 0.219 4.934 0 4.128 0 C 2.152 0 0.486 1.322 0.001 3.118 C 0 3.12 0 3.121 0 3.123 Z%22 fill=%22rgb(52, 168, 83)%22 height=%2218.795384615384606px%22 id=%22QO6a9HaeR%22 transform=%22translate(14.209 9.705)%22 width=%2224.111671722450055px%22/></g></svg>"
                            />
                          </a>
                          {/*/$*/}
                        </div>
                      </div>
                      <div
                        className="framer-snkriq"
                        data-framer-name="Image Wrapper"
                      >
                        <div
                          className="framer-mzxoip scallopplus-s181"
                          data-framer-name="(Replace 2608x1855 Image) Screenshot"
                          tabIndex={0}
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              loading="lazy"
                              width="735"
                              height="855"
                              sizes="(max-width: 809.98px) 553px"
                              srcSet="/framerusercontent.com/images/lA0uEX1WJ7MHJfzDGkaXaOVq04.png 735w"
                              src="/framerusercontent.com/images/lA0uEX1WJ7MHJfzDGkaXaOVq04.png"
                              alt=""
                              className="scallopplus-s47"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
              <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
                <div className="framer-1pqfmc-container" id="cta">
                  <section
                    className="framer-WvUDv framer-p2e38s framer-v-15i9sy0 scallopplus-s3"
                    data-framer-name="Tablet"
                  >
                    <div
                      className="framer-68oizx scallopplus-s182"
                      data-framer-name="CTA Wrapper"
                    >
                      <div
                        className="framer-1j3tetx"
                        data-framer-name="Content Wrapper"
                      >
                        <div className="framer-1kvw61i" data-framer-name="Top">
                          <div
                            className="framer-1u5643w scallopplus-s168 reveal"
                            data-framer-name="Choose how you use your account"
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s183"
                            >
                              Land connected on your next trip
                            </p>
                          </div>
                          <div
                            className="framer-1n8ovd7 scallopplus-s170 reveal"
                            data-framer-name="More features, higher limits, and priority access "
                            data-framer-component-type="RichTextContainer"
                          >
                            <p
                              dir="auto"
                              className="framer-text scallopplus-s179"
                            >
                              <span className="framer-text scallopplus-s180">
                                Set up your eSIM in minutes. Pick a plan when
                                you need it.{' '}
                              </span>
                              <br className="framer-text" />
                              <span className="framer-text scallopplus-s180">
                                Pay the way you want.
                              </span>
                            </p>
                          </div>
                        </div>
                        <div
                          className="framer-x3nt5l"
                          data-framer-appear-id="x3nt5l"
                        >
                          {/*$*/}
                          <a
                            className="framer-1wusars framer-1r6wjfa"
                            data-framer-name="Appstore"
                            href="https://apps.apple.com/app/scallop/id6746711366"
                            target="_blank"
                            rel="noopener"
                          >
                            <img
                              alt=""
                              className="framer-fhIeM framer-rpojg6"
                              src="data:image/svg+xml,<svg display=%22block%22 role=%22presentation%22 viewBox=%220 0 180 60%22 xmlns=%22http://www.w3.org/2000/svg%22><g d=%22M 0 60 L 0 0 L 180 0 L 180 60 Z M 169.5 0.75 L 10.5 0.75 C 5.115 0.75 0.75 5.115 0.75 10.5 L 0.75 49.5 C 0.75 54.885 5.115 59.25 10.5 59.25 L 169.5 59.25 C 174.885 59.25 179.25 54.885 179.25 49.5 L 179.25 10.5 C 179.25 5.115 174.885 0.75 169.5 0.75 Z M 169.5 0.75 L 10.5 0.75 C 5.115 0.75 0.75 5.115 0.75 10.5 L 0.75 49.5 C 0.75 54.885 5.115 59.25 10.5 59.25 L 169.5 59.25 C 174.885 59.25 179.25 54.885 179.25 49.5 L 179.25 10.5 C 179.25 5.115 174.885 0.75 169.5 0.75 Z M 122.291 28.802 L 122.291 32.239 L 120.137 32.239 L 120.137 34.492 L 122.291 34.492 L 122.291 42.15 C 122.291 44.764 123.474 45.81 126.449 45.81 C 126.972 45.81 127.471 45.748 127.906 45.673 L 127.906 43.445 C 127.533 43.482 127.296 43.507 126.885 43.507 C 125.553 43.507 124.968 42.884 124.968 41.465 L 124.968 34.492 L 127.906 34.492 L 127.906 32.239 L 124.968 32.239 L 124.968 28.802 Z M 135.486 45.997 C 139.445 45.997 141.873 43.345 141.873 38.95 C 141.873 34.579 139.433 31.915 135.486 31.915 C 131.526 31.915 129.086 34.579 129.086 38.95 C 129.086 43.345 131.514 45.997 135.486 45.997 Z M 135.486 43.619 C 133.158 43.619 131.85 41.913 131.85 38.95 C 131.85 36.011 133.158 34.293 135.486 34.293 C 137.802 34.293 139.122 36.011 139.122 38.95 C 139.122 41.901 137.802 43.619 135.486 43.619 Z M 143.949 45.736 L 146.626 45.736 L 146.626 37.73 C 146.626 35.825 148.058 34.542 150.088 34.542 C 150.56 34.542 151.358 34.629 151.582 34.704 L 151.582 32.064 C 151.295 31.989 150.785 31.952 150.386 31.952 C 148.618 31.952 147.112 32.923 146.726 34.256 L 146.527 34.256 L 146.527 32.176 L 143.949 32.176 Z M 158.229 34.193 C 160.209 34.193 161.504 35.576 161.567 37.705 L 154.718 37.705 C 154.868 35.588 156.249 34.193 158.229 34.193 Z M 161.555 42.075 C 161.057 43.133 159.948 43.719 158.33 43.719 C 156.188 43.719 154.806 42.212 154.718 39.834 L 154.718 39.684 L 164.294 39.684 L 164.294 38.751 C 164.294 34.492 162.015 31.915 158.243 31.915 C 154.419 31.915 151.992 34.667 151.992 39 C 151.992 43.333 154.37 45.997 158.255 45.997 C 161.355 45.997 163.521 44.503 164.132 42.075 Z M 104.734 40.728 C 104.941 44.058 107.716 46.187 111.845 46.187 C 116.259 46.187 119.021 43.955 119.021 40.393 C 119.021 37.592 117.446 36.044 113.626 35.153 L 111.574 34.65 C 109.148 34.082 108.167 33.321 108.167 31.992 C 108.167 30.314 109.69 29.217 111.974 29.217 C 114.142 29.217 115.639 30.288 115.91 32.005 L 118.724 32.005 C 118.556 28.868 115.794 26.662 112.013 26.662 C 107.948 26.662 105.238 28.868 105.238 32.185 C 105.238 34.921 106.773 36.547 110.142 37.334 L 112.542 37.915 C 115.007 38.496 116.091 39.348 116.091 40.767 C 116.091 42.419 114.388 43.619 112.065 43.619 C 109.574 43.619 107.845 42.496 107.599 40.728 Z M 77.004 31.952 C 75.161 31.952 73.567 32.874 72.745 34.418 L 72.546 34.418 L 72.546 32.176 L 69.969 32.176 L 69.969 50.243 L 72.646 50.243 L 72.646 43.681 L 72.857 43.681 C 73.567 45.113 75.099 45.96 77.029 45.96 C 80.453 45.96 82.632 43.258 82.632 38.95 C 82.632 34.642 80.453 31.952 77.004 31.952 Z M 76.244 43.557 C 74.003 43.557 72.596 41.789 72.596 38.962 C 72.596 36.123 74.003 34.355 76.257 34.355 C 78.523 34.355 79.88 36.086 79.88 38.95 C 79.88 41.826 78.523 43.557 76.244 43.557 Z M 91.996 31.952 C 90.153 31.952 88.559 32.874 87.737 34.418 L 87.538 34.418 L 87.538 32.176 L 84.961 32.176 L 84.961 50.243 L 87.638 50.243 L 87.638 43.681 L 87.849 43.681 C 88.559 45.113 90.091 45.96 92.021 45.96 C 95.445 45.96 97.624 43.258 97.624 38.95 C 97.624 34.642 95.445 31.952 91.996 31.952 Z M 91.236 43.557 C 88.995 43.557 87.588 41.789 87.588 38.962 C 87.588 36.123 88.995 34.355 91.249 34.355 C 93.515 34.355 94.872 36.086 94.872 38.95 C 94.872 41.826 93.515 43.557 91.236 43.557 Z M 65.166 45.736 L 68.237 45.736 L 61.513 27.113 L 58.403 27.113 L 51.68 45.736 L 54.648 45.736 L 56.364 40.793 L 63.462 40.793 Z M 59.81 30.495 L 60.029 30.495 L 62.727 38.367 L 57.1 38.367 Z M 53.477 13.066 L 53.477 22.05 L 56.72 22.05 C 59.397 22.05 60.947 20.4 60.947 17.53 C 60.947 14.703 59.385 13.066 56.72 13.066 Z M 54.871 14.336 L 56.565 14.336 C 58.426 14.336 59.528 15.519 59.528 17.548 C 59.528 19.609 58.445 20.779 56.565 20.779 L 54.871 20.779 Z M 65.696 22.18 C 67.676 22.18 68.89 20.854 68.89 18.657 C 68.89 16.472 67.67 15.139 65.696 15.139 C 63.716 15.139 62.496 16.472 62.496 18.657 C 62.496 20.854 63.71 22.18 65.696 22.18 Z M 65.696 20.991 C 64.532 20.991 63.878 20.138 63.878 18.657 C 63.878 17.188 64.532 16.328 65.696 16.328 C 66.854 16.328 67.514 17.188 67.514 18.657 C 67.514 20.132 66.854 20.991 65.696 20.991 Z M 79.227 15.27 L 77.888 15.27 L 76.68 20.443 L 76.574 20.443 L 75.18 15.27 L 73.897 15.27 L 72.503 20.443 L 72.403 20.443 L 71.189 15.27 L 69.832 15.27 L 71.7 22.05 L 73.076 22.05 L 74.47 17.057 L 74.576 17.057 L 75.977 22.05 L 77.365 22.05 Z M 80.77 22.05 L 82.108 22.05 L 82.108 18.084 C 82.108 17.026 82.737 16.366 83.727 16.366 C 84.717 16.366 85.19 16.907 85.19 17.997 L 85.19 22.05 L 86.528 22.05 L 86.528 17.661 C 86.528 16.048 85.694 15.139 84.181 15.139 C 83.16 15.139 82.488 15.594 82.158 16.347 L 82.058 16.347 L 82.058 15.27 L 80.77 15.27 Z M 88.637 22.049 L 89.975 22.049 L 89.975 12.624 L 88.637 12.624 Z M 95.009 22.18 C 96.988 22.18 98.202 20.854 98.202 18.657 C 98.202 16.472 96.982 15.139 95.009 15.139 C 93.029 15.139 91.809 16.472 91.809 18.657 C 91.809 20.854 93.023 22.18 95.009 22.18 Z M 95.009 20.991 C 93.844 20.991 93.191 20.138 93.191 18.657 C 93.191 17.188 93.844 16.328 95.009 16.328 C 96.167 16.328 96.826 17.188 96.826 18.657 C 96.826 20.132 96.167 20.991 95.009 20.991 Z M 102.188 21.035 C 101.46 21.035 100.931 20.68 100.931 20.07 C 100.931 19.472 101.354 19.155 102.288 19.093 L 103.944 18.987 L 103.944 19.553 C 103.944 20.394 103.197 21.035 102.188 21.035 Z M 101.846 22.162 C 102.736 22.162 103.477 21.776 103.882 21.097 L 103.987 21.097 L 103.987 22.05 L 105.276 22.05 L 105.276 17.418 C 105.276 15.986 104.317 15.139 102.618 15.139 C 101.08 15.139 99.984 15.886 99.847 17.05 L 101.142 17.05 C 101.292 16.571 101.808 16.297 102.555 16.297 C 103.471 16.297 103.944 16.702 103.944 17.418 L 103.944 18.003 L 102.107 18.109 C 100.495 18.208 99.586 18.912 99.586 20.132 C 99.586 21.371 100.538 22.162 101.846 22.162 Z M 109.818 22.162 C 110.752 22.162 111.542 21.719 111.947 20.979 L 112.053 20.979 L 112.053 22.049 L 113.335 22.049 L 113.335 12.624 L 111.997 12.624 L 111.997 16.347 L 111.897 16.347 C 111.53 15.6 110.746 15.158 109.818 15.158 C 108.106 15.158 107.004 16.515 107.004 18.656 C 107.004 20.804 108.093 22.162 109.818 22.162 Z M 110.198 16.359 C 111.318 16.359 112.022 17.25 112.022 18.663 C 112.022 20.082 111.325 20.96 110.198 20.96 C 109.065 20.96 108.386 20.095 108.386 18.656 C 108.386 17.231 109.071 16.359 110.198 16.359 Z M 122.016 22.18 C 123.996 22.18 125.21 20.854 125.21 18.657 C 125.21 16.472 123.99 15.139 122.016 15.139 C 120.037 15.139 118.816 16.472 118.816 18.657 C 118.816 20.854 120.031 22.18 122.016 22.18 Z M 122.016 20.991 C 120.852 20.991 120.199 20.138 120.199 18.657 C 120.199 17.188 120.852 16.328 122.016 16.328 C 123.174 16.328 123.834 17.188 123.834 18.657 C 123.834 20.132 123.174 20.991 122.016 20.991 Z M 126.98 22.05 L 128.319 22.05 L 128.319 18.084 C 128.319 17.026 128.948 16.366 129.938 16.366 C 130.927 16.366 131.401 16.907 131.401 17.997 L 131.401 22.05 L 132.739 22.05 L 132.739 17.661 C 132.739 16.048 131.905 15.139 130.392 15.139 C 129.371 15.139 128.699 15.594 128.369 16.347 L 128.269 16.347 L 128.269 15.27 L 126.98 15.27 Z M 138.905 13.583 L 138.905 15.301 L 137.828 15.301 L 137.828 16.428 L 138.905 16.428 L 138.905 20.257 C 138.905 21.564 139.497 22.087 140.984 22.087 C 141.246 22.087 141.495 22.056 141.713 22.018 L 141.713 20.904 C 141.526 20.923 141.408 20.935 141.202 20.935 C 140.536 20.935 140.244 20.624 140.244 19.914 L 140.244 16.428 L 141.713 16.428 L 141.713 15.301 L 140.244 15.301 L 140.244 13.583 Z M 143.512 22.049 L 144.85 22.049 L 144.85 18.09 C 144.85 17.063 145.46 16.372 146.556 16.372 C 147.502 16.372 148.007 16.92 148.007 18.003 L 148.007 22.049 L 149.345 22.049 L 149.345 17.673 C 149.345 16.06 148.455 15.145 147.011 15.145 C 145.99 15.145 145.274 15.6 144.944 16.359 L 144.838 16.359 L 144.838 12.624 L 143.512 12.624 Z M 154.173 16.279 C 155.163 16.279 155.811 16.97 155.843 18.034 L 152.418 18.034 C 152.493 16.976 153.183 16.279 154.173 16.279 Z M 155.835 20.219 C 155.586 20.749 155.033 21.041 154.223 21.041 C 153.153 21.041 152.462 20.288 152.418 19.099 L 152.418 19.024 L 157.205 19.024 L 157.205 18.557 C 157.205 16.428 156.066 15.139 154.179 15.139 C 152.268 15.139 151.055 16.515 151.055 18.682 C 151.055 20.848 152.244 22.18 154.185 22.18 C 155.736 22.18 156.819 21.433 157.124 20.219 Z M 37.153 30.452 C 37.185 27.93 38.54 25.544 40.688 24.224 C 39.333 22.288 37.063 21.061 34.701 20.987 C 32.182 20.723 29.74 22.494 28.457 22.494 C 27.148 22.494 25.172 21.013 23.044 21.057 C 20.27 21.147 17.684 22.724 16.335 25.149 C 13.434 30.172 15.597 37.553 18.376 41.613 C 19.767 43.601 21.392 45.822 23.518 45.743 C 25.598 45.657 26.375 44.416 28.887 44.416 C 31.375 44.416 32.104 45.743 34.273 45.693 C 36.506 45.657 37.913 43.696 39.254 41.689 C 40.253 40.273 41.022 38.707 41.532 37.05 C 38.908 35.941 37.156 33.3 37.153 30.452 Z M 33.057 18.317 C 34.274 16.856 34.874 14.977 34.729 13.081 C 32.869 13.276 31.151 14.165 29.917 15.571 C 28.711 16.943 28.083 18.789 28.202 20.613 C 30.062 20.632 31.891 19.767 33.057 18.317 Z%22 fill=%22transparent%22 height=%2260px%22 id=%22V_5LYGIvk%22 width=%22180px%22><path d=%22M 0 60 L 0 0 L 180 0 L 180 60 Z%22 fill=%22transparent%22 height=%2260px%22 id=%22kHaGt6RyA%22 width=%22180px%22/><path d=%22M 168.75 0 L 9.75 0 C 4.365 0 0 4.365 0 9.75 L 0 48.75 C 0 54.135 4.365 58.5 9.75 58.5 L 168.75 58.5 C 174.135 58.5 178.5 54.135 178.5 48.75 L 178.5 9.75 C 178.5 4.365 174.135 0 168.75 0 Z%22 fill=%22rgb(12, 13, 16)%22 height=%2258.5px%22 id=%22FEPf0vY4T%22 transform=%22translate(0.75 0.75)%22 width=%22178.5px%22/><path d=%22M 168.75 0 L 9.75 0 C 4.365 0 0 4.365 0 9.75 L 0 48.75 C 0 54.135 4.365 58.5 9.75 58.5 L 168.75 58.5 C 174.135 58.5 178.5 54.135 178.5 48.75 L 178.5 9.75 C 178.5 4.365 174.135 0 168.75 0 Z%22 fill=%22transparent%22 height=%2258.5px%22 id=%22R3Yc3UvLE%22 stroke-dasharray=%22%22 stroke-linecap=%22butt%22 stroke-linejoin=%22miter%22 stroke-miterlimit=%2210%22 stroke-width=%221.5%22 stroke=%22rgb(166, 166, 166)%22 transform=%22translate(0.75 0.75)%22 width=%22178.5px%22/><path d=%22M 2.154 0 L 2.154 3.437 L 0 3.437 L 0 5.69 L 2.154 5.69 L 2.154 13.348 C 2.154 15.962 3.337 17.008 6.312 17.008 C 6.835 17.008 7.334 16.946 7.769 16.871 L 7.769 14.643 C 7.396 14.68 7.159 14.705 6.748 14.705 C 5.416 14.705 4.831 14.082 4.831 12.663 L 4.831 5.69 L 7.769 5.69 L 7.769 3.437 L 4.831 3.437 L 4.831 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2217.008399999999998px%22 id=%22cNda9Ea2m%22 transform=%22translate(120.137 28.802)%22 width=%227.7690000000000055px%22/><path d=%22M 6.4 14.082 C 10.359 14.082 12.787 11.43 12.787 7.035 C 12.787 2.665 10.347 0 6.4 0 C 2.44 0 0 2.665 0 7.035 C 0 11.43 2.428 14.082 6.4 14.082 Z M 6.4 11.704 C 4.072 11.704 2.764 9.998 2.764 7.035 C 2.764 4.096 4.072 2.378 6.4 2.378 C 8.716 2.378 10.036 4.096 10.036 7.035 C 10.036 9.986 8.716 11.704 6.4 11.704 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2214.0823px%22 id=%22qqfvCfrtg%22 transform=%22translate(129.086 31.915)%22 width=%2212.786999999999978px%22/><path d=%22M 0 13.783 L 2.677 13.783 L 2.677 5.777 C 2.677 3.872 4.109 2.59 6.139 2.59 C 6.611 2.59 7.409 2.677 7.633 2.752 L 7.633 0.112 C 7.346 0.037 6.836 0 6.437 0 C 4.669 0 3.163 0.971 2.777 2.303 L 2.578 2.303 L 2.578 0.224 L 0 0.224 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2213.783499999999997px%22 id=%22ozncIZnPB%22 transform=%22translate(143.949 31.952)%22 width=%227.632999999999981px%22/><path d=%22M 6.237 2.279 C 8.217 2.279 9.512 3.661 9.575 5.79 L 2.726 5.79 C 2.876 3.673 4.257 2.279 6.237 2.279 Z M 9.563 10.16 C 9.065 11.218 7.956 11.804 6.338 11.804 C 4.196 11.804 2.814 10.297 2.726 7.919 L 2.726 7.77 L 12.302 7.77 L 12.302 6.836 C 12.302 2.577 10.023 0 6.251 0 C 2.427 0 0 2.752 0 7.085 C 0 11.418 2.378 14.082 6.263 14.082 C 9.363 14.082 11.529 12.588 12.14 10.16 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2214.082300000000004px%22 id=%22ChYaxBatK%22 transform=%22translate(151.992 31.915)%22 width=%2212.302000000000021px%22/><path d=%22M 0 14.067 C 0.207 17.396 2.982 19.526 7.111 19.526 C 11.525 19.526 14.287 17.293 14.287 13.731 C 14.287 10.931 12.712 9.382 8.892 8.492 L 6.84 7.988 C 4.414 7.42 3.433 6.659 3.433 5.33 C 3.433 3.652 4.956 2.555 7.24 2.555 C 9.408 2.555 10.905 3.626 11.176 5.343 L 13.99 5.343 C 13.822 2.207 11.06 0 7.279 0 C 3.214 0 0.504 2.207 0.504 5.523 C 0.504 8.26 2.039 9.886 5.408 10.673 L 7.808 11.254 C 10.273 11.834 11.357 12.686 11.357 14.106 C 11.357 15.757 9.654 16.958 7.331 16.958 C 4.84 16.958 3.111 15.835 2.865 14.067 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2219.5257px%22 id=%22IjThgD9dd%22 transform=%22translate(104.734 26.662)%22 width=%2214.287000000000006px%22/><path d=%22M 7.035 0 C 5.192 0 3.598 0.921 2.776 2.465 L 2.577 2.465 L 2.577 0.224 L 0 0.224 L 0 18.291 L 2.677 18.291 L 2.677 11.729 L 2.888 11.729 C 3.598 13.161 5.13 14.008 7.06 14.008 C 10.484 14.008 12.663 11.306 12.663 6.998 C 12.663 2.689 10.484 0 7.035 0 Z M 6.275 11.605 C 4.034 11.605 2.627 9.837 2.627 7.01 C 2.627 4.171 4.034 2.403 6.288 2.403 C 8.554 2.403 9.911 4.134 9.911 6.998 C 9.911 9.874 8.554 11.605 6.275 11.605 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.290799999999997px%22 id=%22iVaLanCV_%22 transform=%22translate(69.969 31.952)%22 width=%2212.662800000000004px%22/><path d=%22M 7.035 0 C 5.192 0 3.598 0.921 2.776 2.465 L 2.577 2.465 L 2.577 0.224 L 0 0.224 L 0 18.291 L 2.677 18.291 L 2.677 11.729 L 2.889 11.729 C 3.598 13.161 5.13 14.008 7.06 14.008 C 10.484 14.008 12.663 11.306 12.663 6.998 C 12.663 2.689 10.484 0 7.035 0 Z M 6.275 11.605 C 4.034 11.605 2.627 9.837 2.627 7.01 C 2.627 4.171 4.034 2.403 6.288 2.403 C 8.554 2.403 9.911 4.134 9.911 6.998 C 9.911 9.874 8.554 11.605 6.275 11.605 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.290799999999997px%22 id=%22wCMU1UaNq%22 transform=%22translate(84.961 31.952)%22 width=%2212.662900000000008px%22/><path d=%22M 13.486 18.622 L 16.557 18.622 L 9.834 0 L 6.724 0 L 0 18.622 L 2.968 18.622 L 4.685 13.68 L 11.783 13.68 Z M 8.13 3.381 L 8.35 3.381 L 11.047 11.253 L 5.42 11.253 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2218.6223px%22 id=%22dv51fN_AU%22 transform=%22translate(51.68 27.113)%22 width=%2216.5574px%22/><path d=%22M 0 0 L 0 8.984 L 3.244 8.984 C 5.921 8.984 7.471 7.334 7.471 4.464 C 7.471 1.637 5.908 0 3.244 0 Z M 1.395 1.27 L 3.088 1.27 C 4.949 1.27 6.051 2.453 6.051 4.482 C 6.051 6.543 4.968 7.713 3.088 7.713 L 1.395 7.713 Z%22 fill=%22rgb(255, 255, 255)%22 height=%228.983600000000001px%22 id=%22kgSqGhbT6%22 transform=%22translate(53.477 13.066)%22 width=%227.470700000000001px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.22 0 0 1.332 0 3.517 C 0 5.715 1.214 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.382 4.999 1.382 3.517 C 1.382 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22X6oepGtxA%22 transform=%22translate(62.496 15.139)%22 width=%226.393600000000006px%22/><path d=%22M 9.395 0 L 8.056 0 L 6.848 5.174 L 6.742 5.174 L 5.348 0 L 4.065 0 L 2.671 5.174 L 2.571 5.174 L 1.357 0 L 0 0 L 1.868 6.78 L 3.244 6.78 L 4.638 1.787 L 4.744 1.787 L 6.145 6.78 L 7.533 6.78 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.779699999999998px%22 id=%22J5qrRVDfp%22 transform=%22translate(69.832 15.27)%22 width=%229.394500000000008px%22/><path d=%22M 0 6.91 L 1.339 6.91 L 1.339 2.945 C 1.339 1.886 1.967 1.226 2.957 1.226 C 3.947 1.226 4.42 1.768 4.42 2.857 L 4.42 6.91 L 5.759 6.91 L 5.759 2.521 C 5.759 0.909 4.925 0 3.412 0 C 2.391 0 1.718 0.454 1.388 1.208 L 1.289 1.208 L 1.289 0.131 L 0 0.131 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.910400000000001px%22 id=%22cRWG_5cD1%22 transform=%22translate(80.769 15.139)%22 width=%225.758700000000005px%22/><path d=%22M 0 9.425 L 1.339 9.425 L 1.339 0 L 0 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.425499999999998px%22 id=%22eJ7ddPydU%22 transform=%22translate(88.637 12.624)%22 width=%221.3385999999999996px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.22 0 0 1.332 0 3.517 C 0 5.715 1.214 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.382 4.999 1.382 3.517 C 1.382 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22JDAra7JIl%22 transform=%22translate(91.809 15.139)%22 width=%226.393600000000006px%22/><path d=%22M 2.602 5.896 C 1.874 5.896 1.345 5.541 1.345 4.931 C 1.345 4.333 1.768 4.015 2.702 3.953 L 4.358 3.847 L 4.358 4.414 C 4.358 5.254 3.611 5.896 2.602 5.896 Z M 2.26 7.022 C 3.15 7.022 3.891 6.636 4.296 5.958 L 4.401 5.958 L 4.401 6.91 L 5.69 6.91 L 5.69 2.278 C 5.69 0.847 4.731 0 3.032 0 C 1.494 0 0.398 0.747 0.261 1.911 L 1.556 1.911 C 1.706 1.432 2.222 1.158 2.969 1.158 C 3.885 1.158 4.358 1.563 4.358 2.278 L 4.358 2.864 L 2.521 2.97 C 0.909 3.069 0 3.773 0 4.993 C 0 6.232 0.952 7.022 2.26 7.022 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.022399999999999px%22 id=%22e2IYJHcgl%22 transform=%22translate(99.586 15.139)%22 width=%225.690100000000001px%22/><path d=%22M 2.814 9.538 C 3.748 9.538 4.538 9.095 4.943 8.355 L 5.049 8.355 L 5.049 9.425 L 6.331 9.425 L 6.331 0 L 4.993 0 L 4.993 3.723 L 4.893 3.723 C 4.526 2.976 3.742 2.534 2.814 2.534 C 1.102 2.534 0 3.891 0 6.033 C 0 8.18 1.089 9.538 2.814 9.538 Z M 3.194 3.735 C 4.314 3.735 5.018 4.626 5.018 6.039 C 5.018 7.458 4.321 8.336 3.194 8.336 C 2.061 8.336 1.382 7.471 1.382 6.033 C 1.382 4.607 2.067 3.735 3.194 3.735 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.5376px%22 id=%22K5cX3nCIG%22 transform=%22translate(107.004 12.624)%22 width=%226.330999999999989px%22/><path d=%22M 3.2 7.041 C 5.18 7.041 6.394 5.715 6.394 3.517 C 6.394 1.332 5.174 0 3.2 0 C 1.221 0 0 1.332 0 3.517 C 0 5.715 1.215 7.041 3.2 7.041 Z M 3.2 5.852 C 2.036 5.852 1.383 4.999 1.383 3.517 C 1.383 2.048 2.036 1.189 3.2 1.189 C 4.358 1.189 5.018 2.048 5.018 3.517 C 5.018 4.993 4.358 5.852 3.2 5.852 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22NIqN4iVvg%22 transform=%22translate(118.816 15.139)%22 width=%226.393999999999991px%22/><path d=%22M 0 6.91 L 1.339 6.91 L 1.339 2.945 C 1.339 1.886 1.968 1.226 2.958 1.226 C 3.947 1.226 4.421 1.768 4.421 2.857 L 4.421 6.91 L 5.759 6.91 L 5.759 2.521 C 5.759 0.909 4.925 0 3.412 0 C 2.391 0 1.719 0.454 1.389 1.208 L 1.289 1.208 L 1.289 0.131 L 0 0.131 Z%22 fill=%22rgb(255, 255, 255)%22 height=%226.910400000000001px%22 id=%22NTi0rfi1Y%22 transform=%22translate(126.98 15.139)%22 width=%225.759px%22/><path d=%22M 1.077 0 L 1.077 1.718 L 0 1.718 L 0 2.845 L 1.077 2.845 L 1.077 6.674 C 1.077 7.981 1.669 8.504 3.156 8.504 C 3.418 8.504 3.667 8.473 3.885 8.436 L 3.885 7.321 C 3.698 7.34 3.58 7.352 3.374 7.352 C 2.708 7.352 2.416 7.041 2.416 6.331 L 2.416 2.845 L 3.885 2.845 L 3.885 1.718 L 2.416 1.718 L 2.416 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%228.5041px%22 id=%22Nos9Cp5Hh%22 transform=%22translate(137.828 13.583)%22 width=%223.884999999999991px%22/><path d=%22M 0 9.425 L 1.338 9.425 L 1.338 5.466 C 1.338 4.439 1.948 3.748 3.044 3.748 C 3.99 3.748 4.495 4.296 4.495 5.379 L 4.495 9.425 L 5.833 9.425 L 5.833 5.049 C 5.833 3.437 4.943 2.521 3.499 2.521 C 2.478 2.521 1.762 2.976 1.432 3.735 L 1.326 3.735 L 1.326 0 L 0 0 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.425499999999998px%22 id=%22LVQbLUqUF%22 transform=%22translate(143.512 12.624)%22 width=%225.832999999999998px%22/><path d=%22M 3.118 1.139 C 4.108 1.139 4.756 1.83 4.788 2.895 L 1.363 2.895 C 1.438 1.836 2.128 1.139 3.118 1.139 Z M 4.78 5.08 C 4.531 5.609 3.978 5.902 3.168 5.902 C 2.098 5.902 1.407 5.149 1.363 3.959 L 1.363 3.885 L 6.15 3.885 L 6.15 3.418 C 6.15 1.289 5.011 0 3.124 0 C 1.213 0 0 1.376 0 3.542 C 0 5.709 1.189 7.041 3.13 7.041 C 4.681 7.041 5.764 6.294 6.069 5.08 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.041199999999998px%22 id=%22vrbdmjN_S%22 transform=%22translate(151.055 15.139)%22 width=%226.150000000000006px%22/><path d=%22M 22.196 9.492 C 22.228 6.97 23.583 4.584 25.731 3.263 C 24.376 1.328 22.106 0.101 19.744 0.027 C 17.225 -0.237 14.783 1.534 13.5 1.534 C 12.191 1.534 10.215 0.053 8.087 0.097 C 5.313 0.187 2.727 1.764 1.378 4.189 C -1.523 9.211 0.64 16.593 3.419 20.653 C 4.81 22.641 6.435 24.862 8.561 24.783 C 10.641 24.696 11.418 23.456 13.93 23.456 C 16.418 23.456 17.147 24.783 19.316 24.733 C 21.549 24.696 22.956 22.736 24.297 20.729 C 25.296 19.312 26.065 17.747 26.575 16.09 C 23.951 14.98 22.199 12.34 22.196 9.492 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2224.78491613833904px%22 id=%22PM7rCRhKC%22 transform=%22translate(14.957 20.96)%22 width=%2226.574785466484332px%22/><path d=%22M 4.869 5.236 C 6.087 3.775 6.686 1.896 6.541 0 C 4.681 0.195 2.964 1.084 1.73 2.489 C 0.523 3.862 -0.105 5.708 0.014 7.532 C 1.875 7.551 3.703 6.686 4.869 5.236 Z%22 fill=%22rgb(255, 255, 255)%22 height=%227.531813143919933px%22 id=%22KMIzbdHNa%22 transform=%22translate(28.188 13.081)%22 width=%226.562434978114929px%22/></g></svg>"
                            />
                          </a>
                          {/*/$*/}
                          {/*$*/}
                          <a
                            className="framer-1mj9ybf framer-1r6wjfa"
                            data-framer-name="Playstore"
                            href="https://play.google.com/store/apps/details?id=com.app.scallop"
                            target="_blank"
                            rel="noopener"
                          >
                            <img
                              alt=""
                              className="framer-z0HLy framer-1u48p0z"
                              src="data:image/svg+xml,<svg display=%22block%22 role=%22presentation%22 viewBox=%220 0 201 59%22 xmlns=%22http://www.w3.org/2000/svg%22><g d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z M 158.062 43.115 L 160.834 43.115 L 160.834 24.844 L 158.062 24.844 Z M 183.023 31.426 L 179.846 39.348 L 179.751 39.348 L 176.455 31.426 L 173.47 31.426 L 178.415 42.498 L 175.594 48.657 L 178.484 48.657 L 186.105 31.426 Z M 167.307 41.04 C 166.401 41.04 165.133 40.593 165.133 39.489 C 165.133 38.078 166.711 37.537 168.071 37.537 C 169.289 37.537 169.864 37.795 170.603 38.148 C 170.388 39.842 168.907 41.04 167.307 41.04 Z M 167.641 31.027 C 165.635 31.027 163.557 31.897 162.698 33.824 L 165.157 34.834 C 165.683 33.824 166.661 33.495 167.689 33.495 C 169.122 33.495 170.579 34.34 170.602 35.846 L 170.602 36.033 C 170.1 35.751 169.025 35.328 167.712 35.328 C 165.06 35.328 162.362 36.762 162.362 39.441 C 162.362 41.887 164.536 43.461 166.972 43.461 C 168.834 43.461 169.863 42.639 170.506 41.674 L 170.602 41.674 L 170.602 43.086 L 173.279 43.086 L 173.279 36.08 C 173.279 32.836 170.816 31.027 167.641 31.027 Z M 150.516 33.65 L 146.574 33.65 L 146.574 27.387 L 150.516 27.387 C 152.587 27.387 153.764 29.075 153.764 30.519 C 153.764 31.935 152.587 33.65 150.516 33.65 Z M 150.445 24.844 L 143.804 24.844 L 143.804 43.115 L 146.574 43.115 L 146.574 36.194 L 150.445 36.194 C 153.516 36.194 156.535 34.005 156.535 30.519 C 156.535 27.033 153.516 24.844 150.445 24.844 Z M 114.236 41.044 C 112.321 41.044 110.719 39.465 110.719 37.3 C 110.719 35.109 112.321 33.509 114.236 33.509 C 116.126 33.509 117.609 35.109 117.609 37.3 C 117.609 39.465 116.126 41.044 114.236 41.044 Z M 117.418 32.449 L 117.322 32.449 C 116.7 31.719 115.503 31.06 113.996 31.06 C 110.838 31.06 107.943 33.792 107.943 37.3 C 107.943 40.785 110.838 43.492 113.996 43.492 C 115.503 43.492 116.7 42.833 117.322 42.08 L 117.418 42.08 L 117.418 42.974 C 117.418 45.353 116.126 46.624 114.044 46.624 C 112.346 46.624 111.293 45.423 110.863 44.41 L 108.446 45.399 C 109.139 47.047 110.982 49.073 114.044 49.073 C 117.299 49.073 120.05 47.189 120.05 42.597 L 120.05 31.437 L 117.418 31.437 Z M 121.964 43.115 L 124.738 43.115 L 124.738 24.844 L 121.964 24.844 Z M 128.83 37.088 C 128.759 34.686 130.721 33.461 132.132 33.461 C 133.233 33.461 134.166 34.003 134.477 34.78 Z M 137.443 35.016 C 136.917 33.627 135.314 31.06 132.036 31.06 C 128.782 31.06 126.079 33.579 126.079 37.276 C 126.079 40.761 128.759 43.492 132.347 43.492 C 135.242 43.492 136.917 41.75 137.611 40.738 L 135.458 39.324 C 134.74 40.361 133.76 41.044 132.347 41.044 C 130.936 41.044 129.93 40.408 129.286 39.159 L 137.73 35.722 Z M 70.158 32.967 L 70.158 35.604 L 76.569 35.604 C 76.378 37.088 75.875 38.171 75.11 38.925 C 74.177 39.843 72.717 40.855 70.158 40.855 C 66.21 40.855 63.124 37.723 63.124 33.839 C 63.124 29.954 66.21 26.821 70.158 26.821 C 72.287 26.821 73.842 27.645 74.99 28.706 L 76.881 26.845 C 75.277 25.338 73.149 24.185 70.158 24.185 C 64.749 24.185 60.204 28.518 60.204 33.839 C 60.204 39.159 64.749 43.492 70.158 43.492 C 73.076 43.492 75.277 42.55 77 40.785 C 78.77 39.042 79.321 36.593 79.321 34.615 C 79.321 34.003 79.272 33.438 79.177 32.967 Z M 86.611 41.044 C 84.696 41.044 83.045 39.49 83.045 37.276 C 83.045 35.039 84.696 33.509 86.611 33.509 C 88.524 33.509 90.175 35.039 90.175 37.276 C 90.175 39.49 88.524 41.044 86.611 41.044 Z M 86.611 31.06 C 83.117 31.06 80.27 33.674 80.27 37.276 C 80.27 40.855 83.117 43.492 86.611 43.492 C 90.103 43.492 92.951 40.855 92.951 37.276 C 92.951 33.674 90.103 31.06 86.611 31.06 Z M 100.441 41.044 C 98.528 41.044 96.876 39.49 96.876 37.276 C 96.876 35.039 98.528 33.509 100.441 33.509 C 102.355 33.509 104.006 35.039 104.006 37.276 C 104.006 39.49 102.355 41.044 100.441 41.044 Z M 100.441 31.06 C 96.948 31.06 94.101 33.674 94.101 37.276 C 94.101 40.855 96.948 43.492 100.441 43.492 C 103.934 43.492 106.782 40.855 106.782 37.276 C 106.782 33.674 103.934 31.06 100.441 31.06 Z M 65.309 18.612 C 63.977 18.612 62.832 18.15 61.909 17.242 C 60.985 16.333 60.517 15.194 60.517 13.871 C 60.517 12.547 60.985 11.411 61.909 10.5 C 62.832 9.591 63.977 9.13 65.309 9.13 C 65.987 9.13 66.619 9.246 67.224 9.485 C 67.83 9.725 68.325 10.059 68.715 10.495 L 68.806 10.598 L 67.768 11.62 L 67.665 11.495 C 67.406 11.182 67.077 10.939 66.66 10.763 C 66.245 10.586 65.791 10.505 65.309 10.505 C 64.371 10.505 63.593 10.818 62.942 11.456 C 62.307 12.105 61.988 12.897 61.988 13.871 C 61.988 14.845 62.308 15.637 62.943 16.287 C 63.593 16.925 64.371 17.239 65.309 17.239 C 66.167 17.239 66.877 17.004 67.435 16.537 C 67.955 16.102 68.267 15.515 68.377 14.766 L 65.16 14.766 L 65.16 13.419 L 69.768 13.419 L 69.788 13.542 C 69.825 13.779 69.852 14.007 69.852 14.228 C 69.852 15.485 69.462 16.511 68.699 17.266 C 67.835 18.166 66.702 18.612 65.309 18.612 Z M 105.319 18.612 C 103.986 18.612 102.853 18.15 101.942 17.242 C 101.942 17.242 101.941 17.242 101.941 17.241 C 101.029 16.343 100.576 15.205 100.576 13.871 C 100.576 12.537 101.029 11.399 101.941 10.501 C 101.941 10.5 101.941 10.5 101.941 10.5 C 102.852 9.592 103.986 9.13 105.319 9.13 C 106.65 9.13 107.782 9.592 108.694 10.513 C 109.606 11.412 110.06 12.548 110.06 13.871 C 110.06 15.205 109.606 16.343 108.694 17.241 C 107.782 18.15 106.639 18.612 105.319 18.612 Z M 71.171 18.415 L 71.171 9.327 L 71.319 9.327 L 76.686 9.327 L 76.686 10.701 L 72.616 10.701 L 72.616 13.198 L 76.287 13.198 L 76.287 14.544 L 72.616 14.544 L 72.616 17.043 L 76.686 17.043 L 76.686 18.415 Z M 80.061 18.415 L 80.061 10.701 L 77.564 10.701 L 77.564 9.327 L 84.004 9.327 L 84.004 10.701 L 83.855 10.701 L 81.507 10.701 L 81.507 18.415 Z M 88.119 18.415 L 88.119 9.327 L 89.565 9.327 L 89.565 9.473 L 89.565 18.415 Z M 93.204 18.415 L 93.204 10.701 L 90.707 10.701 L 90.707 9.327 L 97.147 9.327 L 97.147 10.701 L 96.998 10.701 L 94.65 10.701 L 94.65 18.415 Z M 111.343 18.415 L 111.343 9.327 L 112.973 9.327 L 117.122 15.863 L 117.086 14.622 L 117.086 9.327 L 118.532 9.327 L 118.532 18.415 L 117.101 18.415 L 112.753 11.549 L 112.788 12.788 L 112.788 12.79 L 112.788 18.415 Z M 105.319 17.239 C 106.257 17.239 107.022 16.925 107.646 16.288 L 107.647 16.287 L 107.648 16.286 C 108.281 15.664 108.591 14.861 108.591 13.871 C 108.591 12.883 108.281 12.078 107.648 11.456 L 107.647 11.455 L 107.646 11.454 C 107.022 10.817 106.257 10.505 105.319 10.505 C 104.379 10.505 103.614 10.817 102.978 11.453 L 102.978 11.454 C 102.356 12.092 102.047 12.883 102.047 13.871 C 102.047 14.861 102.356 15.65 102.978 16.288 C 103.614 16.925 104.38 17.239 105.319 17.239 Z M 30.02 27.657 L 14.209 44.173 C 14.21 44.175 14.211 44.178 14.211 44.182 C 14.696 45.975 16.361 47.296 18.336 47.296 C 19.126 47.296 19.867 47.086 20.503 46.717 L 20.553 46.687 L 38.351 36.581 Z M 46.018 24.845 L 46.003 24.835 L 38.319 20.452 L 29.663 28.032 L 38.35 36.579 L 45.993 32.24 C 47.332 31.528 48.242 30.138 48.242 28.534 C 48.242 26.943 47.344 25.559 46.018 24.845 Z M 14.206 12.827 C 14.111 13.172 14.062 13.534 14.062 13.909 L 14.062 43.092 C 14.062 43.467 14.111 43.83 14.207 44.173 L 30.562 28.08 Z M 30.138 28.5 L 38.321 20.449 L 20.545 10.305 C 19.898 9.924 19.144 9.705 18.337 9.705 C 16.362 9.705 14.695 11.027 14.21 12.823 C 14.209 12.825 14.209 12.825 14.209 12.827 Z%22 fill=%22transparent%22 height=%2257px%22 id=%22R56oJ523S%22 transform=%22translate(1 1)%22 width=%22199px%22><path d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z%22 fill=%22rgb(0, 0, 0)%22 height=%2257px%22 id=%22PoPW8TCll%22 width=%22199px%22/><path d=%22M 6.683 57 C 2.992 57 0 54.055 0 50.423 L 0 6.577 C 0 2.945 2.992 0 6.683 0 L 192.317 0 C 196.008 0 199 2.945 199 6.577 L 199 50.423 C 199 54.055 196.008 57 192.317 57 Z%22 fill=%22transparent%22 height=%2257px%22 id=%22h1k_KDtzz%22 stroke-dasharray=%22%22 stroke-linecap=%22butt%22 stroke-linejoin=%22miter%22 stroke-miterlimit=%2210%22 stroke-width=%221.5%22 stroke=%22rgb(166, 166, 166)%22 width=%22199px%22/><path d=%22M 97.858 18.931 L 100.63 18.931 L 100.63 0.66 L 97.858 0.66 Z M 122.819 7.241 L 119.643 15.163 L 119.548 15.163 L 116.251 7.241 L 113.266 7.241 L 118.211 18.313 L 115.39 24.472 L 118.28 24.472 L 125.901 7.241 Z M 107.103 16.855 C 106.197 16.855 104.929 16.408 104.929 15.304 C 104.929 13.893 106.507 13.353 107.867 13.353 C 109.085 13.353 109.66 13.611 110.399 13.964 C 110.184 15.657 108.703 16.855 107.103 16.855 Z M 107.437 6.842 C 105.431 6.842 103.353 7.712 102.494 9.639 L 104.953 10.65 C 105.479 9.639 106.457 9.31 107.485 9.31 C 108.918 9.31 110.375 10.156 110.398 11.661 L 110.398 11.848 C 109.897 11.567 108.821 11.144 107.509 11.144 C 104.856 11.144 102.158 12.577 102.158 15.257 C 102.158 17.702 104.332 19.277 106.768 19.277 C 108.63 19.277 109.659 18.454 110.302 17.49 L 110.398 17.49 L 110.398 18.902 L 113.075 18.902 L 113.075 11.895 C 113.075 8.651 110.612 6.842 107.437 6.842 Z M 90.312 9.466 L 86.37 9.466 L 86.37 3.203 L 90.312 3.203 C 92.384 3.203 93.56 4.89 93.56 6.334 C 93.56 7.75 92.384 9.466 90.312 9.466 Z M 90.241 0.66 L 83.6 0.66 L 83.6 18.931 L 86.37 18.931 L 86.37 12.009 L 90.241 12.009 C 93.312 12.009 96.331 9.821 96.331 6.334 C 96.331 2.848 93.312 0.66 90.241 0.66 Z M 54.032 16.859 C 52.117 16.859 50.515 15.281 50.515 13.116 C 50.515 10.925 52.117 9.325 54.032 9.325 C 55.922 9.325 57.405 10.925 57.405 13.116 C 57.405 15.281 55.922 16.859 54.032 16.859 Z M 57.214 8.265 L 57.118 8.265 C 56.496 7.535 55.299 6.875 53.792 6.875 C 50.634 6.875 47.739 9.607 47.739 13.116 C 47.739 16.6 50.634 19.308 53.792 19.308 C 55.299 19.308 56.496 18.648 57.118 17.895 L 57.214 17.895 L 57.214 18.79 C 57.214 21.168 55.922 22.439 53.84 22.439 C 52.142 22.439 51.09 21.238 50.659 20.226 L 48.242 21.215 C 48.935 22.862 50.778 24.888 53.84 24.888 C 57.095 24.888 59.847 23.005 59.847 18.412 L 59.847 7.252 L 57.214 7.252 Z M 61.76 18.931 L 64.534 18.931 L 64.534 0.66 L 61.76 0.66 Z M 68.626 12.903 C 68.555 10.502 70.517 9.277 71.928 9.277 C 73.029 9.277 73.962 9.819 74.274 10.595 Z M 77.239 10.831 C 76.713 9.443 75.11 6.875 71.832 6.875 C 68.578 6.875 65.875 9.395 65.875 13.091 C 65.875 16.577 68.555 19.308 72.143 19.308 C 75.038 19.308 76.713 17.566 77.407 16.553 L 75.254 15.14 C 74.536 16.176 73.556 16.859 72.143 16.859 C 70.732 16.859 69.726 16.223 69.082 14.975 L 77.526 11.537 Z M 9.954 8.783 L 9.954 11.419 L 16.366 11.419 C 16.174 12.903 15.671 13.986 14.906 14.74 C 13.973 15.658 12.513 16.67 9.954 16.67 C 6.006 16.67 2.92 13.539 2.92 9.654 C 2.92 5.769 6.006 2.637 9.954 2.637 C 12.083 2.637 13.638 3.461 14.786 4.521 L 16.677 2.66 C 15.074 1.154 12.945 0 9.954 0 C 4.545 0 0 4.333 0 9.654 C 0 14.975 4.545 19.308 9.954 19.308 C 12.872 19.308 15.074 18.366 16.796 16.6 C 18.566 14.857 19.117 12.408 19.117 10.431 C 19.117 9.819 19.068 9.253 18.973 8.783 Z M 26.407 16.859 C 24.492 16.859 22.841 15.305 22.841 13.091 C 22.841 10.854 24.492 9.325 26.407 9.325 C 28.32 9.325 29.971 10.854 29.971 13.091 C 29.971 15.305 28.32 16.859 26.407 16.859 Z M 26.407 6.875 C 22.913 6.875 20.066 9.489 20.066 13.091 C 20.066 16.67 22.913 19.308 26.407 19.308 C 29.9 19.308 32.747 16.67 32.747 13.091 C 32.747 9.489 29.9 6.875 26.407 6.875 Z M 40.237 16.859 C 38.324 16.859 36.672 15.305 36.672 13.091 C 36.672 10.854 38.324 9.325 40.237 9.325 C 42.151 9.325 43.802 10.854 43.802 13.091 C 43.802 15.305 42.151 16.859 40.237 16.859 Z M 40.237 6.875 C 36.744 6.875 33.897 9.489 33.897 13.091 C 33.897 16.67 36.744 19.308 40.237 19.308 C 43.73 19.308 46.578 16.67 46.578 13.091 C 46.578 9.489 43.73 6.875 40.237 6.875 Z%22 fill=%22rgb(255, 255, 255)%22 height=%2224.88805208343115px%22 id=%22VRx02W92J%22 transform=%22translate(60.204 24.185)%22 width=%22125.90066281355669px%22/><path d=%22M 4.792 9.482 C 3.46 9.482 2.315 9.021 1.392 8.113 C 0.468 7.203 0 6.064 0 4.741 C 0 3.417 0.468 2.281 1.392 1.37 C 2.315 0.461 3.46 0 4.792 0 C 5.47 0 6.103 0.116 6.708 0.356 C 7.313 0.595 7.809 0.93 8.199 1.365 L 8.29 1.468 L 7.251 2.49 L 7.148 2.365 C 6.889 2.052 6.56 1.809 6.143 1.633 C 5.728 1.457 5.274 1.375 4.792 1.375 C 3.854 1.375 3.076 1.689 2.426 2.326 C 1.79 2.976 1.471 3.767 1.471 4.741 C 1.471 5.716 1.791 6.508 2.427 7.158 C 3.076 7.795 3.854 8.11 4.792 8.11 C 5.65 8.11 6.36 7.874 6.918 7.407 C 7.438 6.973 7.75 6.385 7.86 5.637 L 4.643 5.637 L 4.643 4.289 L 9.251 4.289 L 9.271 4.412 C 9.308 4.65 9.335 4.878 9.335 5.098 C 9.335 6.355 8.945 7.381 8.182 8.136 C 7.318 9.036 6.185 9.482 4.792 9.482 Z M 44.802 9.482 C 43.469 9.482 42.337 9.021 41.426 8.113 C 41.426 8.113 41.425 8.113 41.425 8.112 C 40.512 7.213 40.059 6.075 40.059 4.741 C 40.059 3.407 40.512 2.269 41.425 1.371 C 41.425 1.37 41.425 1.37 41.425 1.37 C 42.336 0.462 43.469 0 44.802 0 C 46.133 0 47.265 0.462 48.177 1.384 C 49.09 2.282 49.543 3.418 49.543 4.741 C 49.543 6.075 49.09 7.213 48.177 8.112 C 47.265 9.021 46.122 9.482 44.802 9.482 Z M 10.654 9.286 L 10.654 0.197 L 10.802 0.197 L 16.169 0.197 L 16.169 1.572 L 12.099 1.572 L 12.099 4.068 L 15.771 4.068 L 15.771 5.415 L 12.099 5.415 L 12.099 7.913 L 16.169 7.913 L 16.169 9.286 Z M 19.545 9.286 L 19.545 1.572 L 17.048 1.572 L 17.048 0.197 L 23.487 0.197 L 23.487 1.572 L 23.338 1.572 L 20.99 1.572 L 20.99 9.286 Z M 27.603 9.286 L 27.603 0.197 L 29.048 0.197 L 29.048 0.343 L 29.048 9.286 Z M 32.687 9.286 L 32.687 1.572 L 30.191 1.572 L 30.191 0.197 L 36.63 0.197 L 36.63 1.572 L 36.481 1.572 L 34.133 1.572 L 34.133 9.286 Z M 50.826 9.286 L 50.826 0.197 L 52.456 0.197 L 56.605 6.733 L 56.569 5.492 L 56.569 0.197 L 58.015 0.197 L 58.015 9.286 L 56.584 9.286 L 52.236 2.419 L 52.272 3.659 L 52.272 3.661 L 52.272 9.286 Z M 44.802 8.11 C 45.74 8.11 46.506 7.795 47.129 7.159 L 47.13 7.158 L 47.131 7.157 C 47.764 6.534 48.074 5.731 48.074 4.741 C 48.074 3.753 47.764 2.948 47.131 2.326 L 47.13 2.325 L 47.129 2.324 C 46.506 1.688 45.74 1.375 44.802 1.375 C 43.862 1.375 43.097 1.688 42.461 2.323 L 42.461 2.324 C 41.84 2.962 41.531 3.753 41.531 4.741 C 41.531 5.731 41.84 6.52 42.461 7.159 C 43.097 7.795 43.863 8.11 44.802 8.11 Z%22 fill=%22rgb(255, 255, 255)%22 height=%229.48246153846153px%22 id=%22tGaJPZoet%22 transform=%22translate(60.517 9.13)%22 width=%2258.014938377570175px%22/><path d=%22M 15.811 0 L 0 16.515 C 0.001 16.518 0.002 16.521 0.002 16.525 C 0.487 18.318 2.151 19.639 4.127 19.639 C 4.917 19.639 5.658 19.429 6.294 19.059 L 6.344 19.03 L 24.141 8.924 Z%22 fill=%22rgb(234, 67, 53)%22 height=%2219.639179487179494px%22 id=%22u8JhnVJkI%22 transform=%22translate(14.209 27.657)%22 width=%2224.141373214987368px%22/><path d=%22M 16.355 4.393 L 16.34 4.384 L 8.656 0 L 0 7.581 L 8.687 16.128 L 16.33 11.788 C 17.669 11.077 18.579 9.686 18.579 8.082 C 18.579 6.491 17.681 5.108 16.355 4.393 Z%22 fill=%22rgb(251, 188, 4)%22 height=%2216.127589743589745px%22 id=%22l0goY9u0b%22 transform=%22translate(29.663 20.452)%22 width=%2218.579273631840792px%22/><path d=%22M 0.145 0 C 0.05 0.345 0 0.706 0 1.082 L 0 30.265 C 0 30.64 0.05 31.002 0.146 31.345 L 16.5 15.253 Z%22 fill=%22rgb(66, 133, 244)%22 height=%2231.345128205128226px%22 id=%22j_ASOP5W1%22 transform=%22translate(14.062 12.827)%22 width=%2216.500169154228853px%22/><path d=%22M 15.929 18.795 L 24.112 10.744 L 6.335 0.6 C 5.689 0.219 4.934 0 4.128 0 C 2.152 0 0.486 1.322 0.001 3.118 C 0 3.12 0 3.121 0 3.123 Z%22 fill=%22rgb(52, 168, 83)%22 height=%2218.795384615384606px%22 id=%22QO6a9HaeR%22 transform=%22translate(14.209 9.705)%22 width=%2224.111671722450055px%22/></g></svg>"
                            />
                          </a>
                          {/*/$*/}
                        </div>
                      </div>
                      <div className="framer-upau9f-container scallopplus-s172">
                        {/*$*/}
                        <div className="scallopplus-s173">
                          <div className="scallopplus-s174">
                            <div className="scallopplus-s175">
                              <div className="scallopplus-s102">
                                <div />
                              </div>
                            </div>
                          </div>
                        </div>
                        {/*/$*/}
                      </div>
                      <div
                        className="framer-snkriq"
                        data-framer-name="Image Wrapper"
                      >
                        <div
                          className="framer-mzxoip scallopplus-s184"
                          data-framer-name="(Replace 2608x1855 Image) Screenshot"
                          tabIndex={0}
                        >
                          <div
                            data-framer-background-image-wrapper="true"
                            className="scallopplus-s28"
                          >
                            <img
                              decoding="async"
                              loading="lazy"
                              width="1024"
                              height="880"
                              sizes="(min-width: 1440px) 685px, (min-width: 810px) and (max-width: 1439.98px) 482px"
                              srcSet="/framerusercontent.com/images/Ra6gAwrRiHXlnb1P0399RrvcEHs.png 512w,/framerusercontent.com/images/Ra6gAwrRiHXlnb1P0399RrvcEHs.png 1024w"
                              src="/framerusercontent.com/images/Ra6gAwrRiHXlnb1P0399RrvcEHs.png"
                              alt=""
                              className="scallopplus-s29"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
            <div className="framer-1p1s45z" data-framer-name="Send Money">
              <div
                className="framer-145q6s0"
                data-framer-name="Country content"
              >
                <div className="ssr-variant hidden-18dn0ox">
                  <div
                    className="framer-5exdos scallopplus-s51 reveal"
                    data-framer-name="Section title"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s185">
                      Travel &amp; eSIM with Scallop+
                    </p>
                  </div>
                </div>
                <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
                  <div
                    className="framer-5exdos scallopplus-s51 reveal"
                    data-framer-name="Section title"
                    data-framer-component-type="RichTextContainer"
                  >
                    <p dir="auto" className="framer-text scallopplus-s186">
                      Travel &amp; eSIM with Scallop+
                    </p>
                  </div>
                </div>
                <div className="framer-1mibcjz" data-framer-name="Country grid">
                  <div
                    className="framer-s3jg2r"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-tu6mlc">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="54"
                          height="54"
                          src="/framerusercontent.com/images/H9OEQ1mKEc0OMKgpdFTVurZm8.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-7ca9z8 scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-nigeria-travel-data-with-scallop-%f0%9f%87%b3%f0%9f%87%ac/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Nigeria
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-1p81f3d"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-18bad4d">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/FS2aYidfUvtOMyH4gzaPy8iovKM.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-pg648m scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-india-travel-data-with-scallop-%f0%9f%87%ae%f0%9f%87%b3/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for India
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-vqz96k"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-c6id1d">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/R4reWXSjSIlwhnueBcVu7bKqDlw.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-o4u03d scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-poland-travel-data-with-scallop%f0%9f%87%b5%f0%9f%87%b1/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Poland
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-ze1156"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1dwhgzx">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/BPGJnoUUzFxDdAhwvRoklt4FE.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-y7v1ja scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-kazakhstan-travel-data-with-scallop-%f0%9f%87%b0%f0%9f%87%bf/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Kazakhstan
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-h3y76n"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-vtg7ni">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/f0pEAIz8CyXt7B2qIZ1EJ6hP2I.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-1d5lufj scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-north-macedonia-travel-data-with-scallop%f0%9f%87%b2%f0%9f%87%b0/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for North Macedonia
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-dqvup5"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1jm6lt">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/87Yq1nvY2hYSCihY8yIrDJb1zbU.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-19jy0re scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-ghana-travel-data-with-scallop-%f0%9f%87%ac%f0%9f%87%ad/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Ghana
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-1k41nw2"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1uqwm9c">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/0OV7TNkJYuFVmrJHzI16KMUq50.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-bnclyy scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-dubai-the-uae-travel-data-with-scallop-%f0%9f%87%a6%f0%9f%87%aa/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Dubai &amp; the UAE
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-1dm884n"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-qvij8m">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/1cA1R9kEvXCP0rPDvqvjk5JQzvw.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-8fy554 scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-saudi-arabia-travel-data-with-scallop%f0%9f%87%b8%f0%9f%87%a6/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Saudi Arabia
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-rqw46d"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-495nec">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/9V49O3RY5argijn9BrUn98Ao.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-62vrst scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-the-united-kingdom-travel-data-with-scallop-%f0%9f%87%ac%f0%9f%87%a7/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for the United Kingdom
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-fd084j"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1qzic9z">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/hEDwlKPYtskg0EOrDqcZzQzEmE.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-1i4d46l scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-brazil-travel-data-with-scallop%f0%9f%87%a7%f0%9f%87%b7/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Brazil
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-gseez0"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1erausu">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/u1jCE0YdocarRBjOPssyIqdg90.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-msgp3i scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-the-united-states-travel-data-with-scallop%f0%9f%87%ba%f0%9f%87%b8/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for the United States
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-lcptyw"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1umo1iz">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/fF2xoDbCYrcjk6T6nRs9kdA9jw.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-qm40av scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-vietnam-travel-data-with-scallop-%f0%9f%87%bb%f0%9f%87%b3/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Vietnam
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-lv7jim"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-xeuv30">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/XlUjavCuwjXPAARSAHg0TgMPeYQ.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-v1m12a scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-the-philippines-travel-data-with-scallop-%f0%9f%87%b5%f0%9f%87%ad/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for the Philippines
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-8r1pc7"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-nf6kmc">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/z4K3BkkqKntJGPcLeiWqWSks.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-1sz0pc6 scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-spain-travel-data-with-scallop%f0%9f%87%aa%f0%9f%87%b8/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Spain
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-e9bypg"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1bzylqy">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/2rhXPobnsZO8SenmplDKQkY1W0.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-v0itxr scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-mexico-travel-data-with-scallop%f0%9f%87%b2%f0%9f%87%bd/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Mexico
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-3tuwu5"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-qbbptc">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/AYce7yXLuipEEZX4VBEWf8es.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-1t49mzy scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-peru-travel-data-with-scallop%f0%9f%87%b5%f0%9f%87%aa/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Peru
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-1yk6mbt"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-2p4kk4">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/pvImMXWFzozZo8V0AlhN7y0iQ.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-xufzxs scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-turkey-travel-data-with-scallop-%f0%9f%87%b9%f0%9f%87%b7/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Turkey
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-btmlo1"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-48wzs9">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/nnaZvFCFOs6OtEAXiJBA75QZhg.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-vwdeih scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-indonesia-travel-data-with-scallop-%f0%9f%87%ae%f0%9f%87%a9/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Indonesia
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-gj9ac5"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1hggbzp">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/EoyE8BiVLiNhFGcwDTENvpM54.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-d2inhv scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-thailand-travel-data-with-scallop%f0%9f%87%b9%f0%9f%87%ad/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Thailand
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-1ygtnew"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1vqoyly">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/y1MptPLQoLkDmGFQNN1AoCMS8.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-zavvjl scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-sri-lanka-travel-data-with-scallop-%f0%9f%87%b1%f0%9f%87%b0/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Sri Lanka
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-5ciwj3"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-7kqr6p">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/oocxMNYprfNdjR4WORojATqxM4.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-ekgxn5 scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-egypt-travel-data-with-scallop-%f0%9f%87%aa%f0%9f%87%ac/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Egypt
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-gol5fx"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1j752gg">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/Jw8oXBLDPWhicTB5Et9G3cB6qI.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-abdglh scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-kenya-travel-data-with-scallop-%f0%9f%87%b0%f0%9f%87%aa/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Kenya
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-1gx4oqo"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-ebp4d1">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/LHV96CkCr3rDmSIAEHsD5j73o.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-p5pen6 scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-morocco-travel-data-with-scallop-%f0%9f%87%b2%f0%9f%87%a6/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Morocco
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-96qre0"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-1skpd19">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/05LeRiyQplp5I5UkCZNouhWrySg.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-ty6xdk scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-colombia-travel-data-with-scallop%f0%9f%87%a8%f0%9f%87%b4/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for Colombia
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                  <div
                    className="framer-12yj4p"
                    data-framer-name="Country item / Andorra"
                  >
                    <div className="framer-fuvzk3">
                      <div
                        data-framer-background-image-wrapper="true"
                        className="scallopplus-s28"
                      >
                        <img
                          decoding="async"
                          loading="lazy"
                          width="120"
                          height="120"
                          src="/framerusercontent.com/images/wmgDsa5lRuPMkpObBBIJbjV7svE.png"
                          alt=""
                          className="scallopplus-s29"
                        />
                      </div>
                    </div>
                    <div
                      className="framer-gmoozj scallopplus-s55 reveal"
                      data-framer-name="Send money to Andorra"
                      data-framer-component-type="RichTextContainer"
                    >
                      <p dir="auto" className="framer-text scallopplus-s187">
                        {/*$*/}
                        <a
                          className="framer-text framer-styles-preset-11r53mh"
                          data-styles-preset="LO9i42feF"
                          href="https://blog.scallopx.com/esim-for-south-africa-travel-data-with-scallop-%f0%9f%87%bf%f0%9f%87%a6/"
                          target="_blank"
                          rel="noopener"
                        >
                          eSIM for South Africa
                        </a>
                        {/*/$*/}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-18dn0ox hidden-otbxcl">
              <div className="framer-d5ov34-container">
                <div
                  className="framer-60Kb5 framer-1rilglu framer-v-1rilglu scallopplus-s188"
                  data-framer-name="Desktop"
                >
                  <div
                    className="framer-14npji9"
                    data-framer-name="Frame 33595"
                  >
                    <div className="framer-1nbyyte">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1ick3k6 scallopplus-s33"
                        aria-hidden="true"
                      >
                        <div className="svgContainer scallopplus-s34">
                          <svg
                            viewBox="0 0 699 196"
                            className="scallopplus-s35"
                          >
                            <use href="#svg-1137231340_6119" />
                          </svg>
                        </div>
                      </div>
                      <div className="framer-6st60f">
                        <div
                          className="framer-9nwe5k scallopplus-s189 reveal"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s190"
                          >
                            <strong className="framer-text">
                              One account for all your currencies{' '}
                              <br className="framer-text" />
                            </strong>
                            Manage, send, and receive money globally
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1d0k7xa"
                        data-framer-name="ICO Logo_Blue"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            width="1440"
                            height="1440"
                            sizes="(min-width: 1440px) 60px, (max-width: 809.98px) 60px, (min-width: 810px) and (max-width: 1439.98px) 60px"
                            srcSet="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 512w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1024w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1440w"
                            src="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      className="framer-c50yne"
                      data-framer-name="Frame 33594"
                    >
                      <div className="framer-5dec6k" data-framer-name="Menu">
                        <div
                          className="framer-nmphiu scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Programs
                          </p>
                        </div>
                        <div className="framer-1odpfed" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1tz9h0j-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/ambassador"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/ambassador"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Ambassador
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                      <div className="framer-a8htgk" data-framer-name="Menu">
                        <div
                          className="framer-1rugbzn scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Products
                          </p>
                        </div>
                        <div className="framer-1oe4fxe" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-153yqwe-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/accounts"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/accounts"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Accounts
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1di1zw4-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/cards"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/cards"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Cards
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-165n05h-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-pay"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-pay"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Pay
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1ddhrxh-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-plus"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-plus"
                                    data-framer-page-link-current="true"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Plus
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-nsl31k-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://blog.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://blog.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Blogs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                      <div className="framer-bcfdyi" data-framer-name="Menu">
                        <div
                          className="framer-cv5tks scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Support
                          </p>
                        </div>
                        <div className="framer-16j5a70" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1apiy1v-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/support"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/support"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Contact Us
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-650678-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/helpdesk"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/helpdesk"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    FAQs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-1935128-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://docs.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://docs.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Docs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-1l4w042"
                    data-framer-name="Frame 33831"
                  >
                    <div className="framer-jk8g5w" data-framer-name="Link">
                      <div className="framer-80yh0c">
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="ic:baseline-copyright"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-m3skko scallopplus-s33"
                          aria-hidden="true"
                        >
                          <div className="svgContainer scallopplus-s34">
                            <svg
                              viewBox="0 0 20 20"
                              className="scallopplus-s35"
                            >
                              <use href="#svg-1839680863_948" />
                            </svg>
                          </div>
                        </div>
                        <div
                          className="framer-hv1cvb scallopplus-s194 reveal"
                          data-framer-name="Item"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s195"
                          >
                            2026 Scallop Group. All rights reserved.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1c28m8b">
                      {/*$*/}
                      <a
                        data-framer-component-type="SVG"
                        as="a"
                        data-framer-name="X"
                        href="https://x.com/Scallopcom"
                        target="_blank"
                        rel="noopener"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-dziac9 framer-1d3y7cu scallopplus-s196"
                        aria-hidden="true"
                      />
                      {/*/$*/}
                      {/*$*/}
                      <a
                        className="framer-n9nhin framer-1d3y7cu"
                        data-framer-name="LinkdIn"
                        href="https://www.linkedin.com/company/scallopcom"
                        target="_blank"
                        rel="noopener"
                      >
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="linkd"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-1ggemab scallopplus-s197"
                          aria-hidden="true"
                        />
                      </a>
                      {/*/$*/}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
              <div className="framer-d5ov34-container">
                <div
                  className="framer-60Kb5 framer-1rilglu framer-v-ltf6f8 scallopplus-s188"
                  data-framer-name="Mobile"
                >
                  <div
                    className="framer-14npji9"
                    data-framer-name="Frame 33595"
                  >
                    <div className="framer-1nbyyte">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1ick3k6 scallopplus-s33"
                        aria-hidden="true"
                      >
                        <div className="svgContainer scallopplus-s34">
                          <svg
                            viewBox="0 0 699 196"
                            className="scallopplus-s35"
                          >
                            <use href="#svg-1137231340_6119" />
                          </svg>
                        </div>
                      </div>
                      <div className="framer-6st60f">
                        <div
                          className="framer-9nwe5k scallopplus-s189 reveal"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s190"
                          >
                            <strong className="framer-text">
                              One account for all your currencies{' '}
                              <br className="framer-text" />
                            </strong>
                            Manage, send, and receive money globally
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1d0k7xa"
                        data-framer-name="ICO Logo_Blue"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            width="1440"
                            height="1440"
                            sizes="(min-width: 1440px) 60px, (max-width: 809.98px) 60px, (min-width: 810px) and (max-width: 1439.98px) 60px"
                            srcSet="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 512w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1024w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1440w"
                            src="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      className="framer-c50yne"
                      data-framer-name="Frame 33594"
                    >
                      <div className="framer-5dec6k" data-framer-name="Menu">
                        <div
                          className="framer-nmphiu scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Programs
                          </p>
                        </div>
                        <div className="framer-1odpfed" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1tz9h0j-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/ambassador"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/ambassador"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Ambassador
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                      <div className="framer-a8htgk" data-framer-name="Menu">
                        <div
                          className="framer-1rugbzn scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Products
                          </p>
                        </div>
                        <div className="framer-1oe4fxe" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-153yqwe-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/accounts"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/accounts"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Accounts
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1di1zw4-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/cards"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/cards"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Cards
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-165n05h-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-pay"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-pay"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Pay
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1ddhrxh-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-plus"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-plus"
                                    data-framer-page-link-current="true"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Plus
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-nsl31k-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://blog.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://blog.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Blogs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                      <div className="framer-bcfdyi" data-framer-name="Menu">
                        <div
                          className="framer-cv5tks scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Support
                          </p>
                        </div>
                        <div className="framer-16j5a70" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1apiy1v-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/support"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/support"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Contact Us
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-650678-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/helpdesk"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/helpdesk"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    FAQs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-1935128-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://docs.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://docs.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Docs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-1l4w042"
                    data-framer-name="Frame 33831"
                  >
                    <div className="framer-jk8g5w" data-framer-name="Link">
                      <div className="framer-80yh0c">
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="ic:baseline-copyright"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-m3skko scallopplus-s33"
                          aria-hidden="true"
                        >
                          <div className="svgContainer scallopplus-s34">
                            <svg
                              viewBox="0 0 20 20"
                              className="scallopplus-s35"
                            >
                              <use href="#svg-1839680863_948" />
                            </svg>
                          </div>
                        </div>
                        <div
                          className="framer-hv1cvb scallopplus-s194 reveal"
                          data-framer-name="Item"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s195"
                          >
                            2026 Scallop Group. All rights reserved.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1c28m8b">
                      {/*$*/}
                      <a
                        data-framer-component-type="SVG"
                        as="a"
                        data-framer-name="X"
                        href="https://x.com/Scallopcom"
                        target="_blank"
                        rel="noopener"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-dziac9 framer-1d3y7cu scallopplus-s196"
                        aria-hidden="true"
                      />
                      {/*/$*/}
                      {/*$*/}
                      <a
                        className="framer-n9nhin framer-1d3y7cu"
                        data-framer-name="LinkdIn"
                        href="https://www.linkedin.com/company/scallopcom"
                        target="_blank"
                        rel="noopener"
                      >
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="linkd"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-1ggemab scallopplus-s197"
                          aria-hidden="true"
                        />
                      </a>
                      {/*/$*/}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="ssr-variant hidden-18dn0ox hidden-1kt7ub1">
              <div className="framer-d5ov34-container">
                <div
                  className="framer-60Kb5 framer-1rilglu framer-v-183e6ee scallopplus-s188"
                  data-framer-name="Tablet"
                >
                  <div
                    className="framer-14npji9"
                    data-framer-name="Frame 33595"
                  >
                    <div className="framer-1nbyyte">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1ick3k6 scallopplus-s33"
                        aria-hidden="true"
                      >
                        <div className="svgContainer scallopplus-s34">
                          <svg
                            viewBox="0 0 699 196"
                            className="scallopplus-s35"
                          >
                            <use href="#svg-1137231340_6119" />
                          </svg>
                        </div>
                      </div>
                      <div className="framer-6st60f">
                        <div
                          className="framer-9nwe5k scallopplus-s189 reveal"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s190"
                          >
                            <strong className="framer-text">
                              One account for all your currencies{' '}
                              <br className="framer-text" />
                            </strong>
                            Manage, send, and receive money globally
                          </p>
                        </div>
                      </div>
                      <div
                        className="framer-1d0k7xa"
                        data-framer-name="ICO Logo_Blue"
                      >
                        <div
                          data-framer-background-image-wrapper="true"
                          className="scallopplus-s28"
                        >
                          <img
                            decoding="async"
                            width="1440"
                            height="1440"
                            sizes="(min-width: 1440px) 60px, (max-width: 809.98px) 60px, (min-width: 810px) and (max-width: 1439.98px) 60px"
                            srcSet="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 512w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1024w,/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png 1440w"
                            src="/framerusercontent.com/images/eFAqz3bGw0AvBGw3Cz1JaKPyTI.png"
                            alt=""
                            className="scallopplus-s29"
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      className="framer-c50yne"
                      data-framer-name="Frame 33594"
                    >
                      <div className="framer-5dec6k" data-framer-name="Menu">
                        <div
                          className="framer-nmphiu scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Programs
                          </p>
                        </div>
                        <div className="framer-1odpfed" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1tz9h0j-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/ambassador"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/ambassador"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Ambassador
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                        </div>
                      </div>
                      <div className="framer-a8htgk" data-framer-name="Menu">
                        <div
                          className="framer-1rugbzn scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Products
                          </p>
                        </div>
                        <div className="framer-1oe4fxe" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-153yqwe-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/accounts"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/accounts"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Accounts
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1di1zw4-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/cards"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/cards"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Cards
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-165n05h-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-pay"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-pay"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Pay
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-1ddhrxh-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/scallop-plus"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/scallop-plus"
                                    data-framer-page-link-current="true"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Plus
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-nsl31k-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://blog.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://blog.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Blogs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                      <div className="framer-bcfdyi" data-framer-name="Menu">
                        <div
                          className="framer-cv5tks scallopplus-s191 reveal"
                          data-framer-name="Heading"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s192"
                          >
                            Support
                          </p>
                        </div>
                        <div className="framer-16j5a70" data-framer-name="List">
                          {/*$*/}
                          <div className="framer-1apiy1v-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/support"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/support"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Contact Us
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          {/*$*/}
                          <div className="framer-650678-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="/helpdesk"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="/helpdesk"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    FAQs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                          {/*/$*/}
                          <div className="framer-1935128-container">
                            {/*$*/}
                            <a
                              className="framer-A3ctK framer-6lXp4 framer-xhaz2i framer-v-xhaz2i framer-l5iotj"
                              data-framer-name="Link"
                              href="https://docs.scallopx.com/"
                            >
                              <div
                                className="framer-wlx7jb scallopplus-s191 reveal"
                                data-framer-name="Item"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s193">
                                  {/*$*/}
                                  <span
                                    className="framer-text framer-styles-preset-11r53mh"
                                    data-styles-preset="LO9i42feF"
                                    href="https://docs.scallopx.com/"
                                    rel="noopener"
                                    data-nested-link="true"
                                    role="link"
                                    tabIndex={0}
                                  >
                                    Scallop Docs
                                  </span>
                                  {/*/$*/}
                                </p>
                              </div>
                            </a>
                            {/*/$*/}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="framer-1l4w042"
                    data-framer-name="Frame 33831"
                  >
                    <div className="framer-jk8g5w" data-framer-name="Link">
                      <div className="framer-80yh0c">
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="ic:baseline-copyright"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-m3skko scallopplus-s33"
                          aria-hidden="true"
                        >
                          <div className="svgContainer scallopplus-s34">
                            <svg
                              viewBox="0 0 20 20"
                              className="scallopplus-s35"
                            >
                              <use href="#svg-1839680863_948" />
                            </svg>
                          </div>
                        </div>
                        <div
                          className="framer-hv1cvb scallopplus-s194 reveal"
                          data-framer-name="Item"
                          data-framer-component-type="RichTextContainer"
                        >
                          <p
                            dir="auto"
                            className="framer-text scallopplus-s195"
                          >
                            2026 Scallop Group. All rights reserved.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="framer-1c28m8b">
                      {/*$*/}
                      <a
                        data-framer-component-type="SVG"
                        as="a"
                        data-framer-name="X"
                        href="https://x.com/Scallopcom"
                        target="_blank"
                        rel="noopener"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-dziac9 framer-1d3y7cu scallopplus-s196"
                        aria-hidden="true"
                      />
                      {/*/$*/}
                      {/*$*/}
                      <a
                        className="framer-n9nhin framer-1d3y7cu"
                        data-framer-name="LinkdIn"
                        href="https://www.linkedin.com/company/scallopcom"
                        target="_blank"
                        rel="noopener"
                      >
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="linkd"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-1ggemab scallopplus-s197"
                          aria-hidden="true"
                        />
                      </a>
                      {/*/$*/}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="framer-18j7ugo-container">
            {/*$*/}
            {/*/$*/}
          </div>
          <div className="ssr-variant hidden-18dn0ox">
            <div
              className="framer-14xydkj-container scallopplus-s2"
              data-framer-appear-id="14xydkj"
              id="14xydkj"
            >
              <div className="ssr-variant hidden-otbxcl">
                <div
                  className="framer-8MFL4 framer-15zawlh framer-v-15zawlh scallopplus-s3"
                  data-framer-name="Desktop [Default Boxed]"
                >
                  <header
                    className="framer-ooxjtq scallopplus-s4"
                    data-border="true"
                    data-framer-name="Main Area"
                  >
                    <div
                      className="framer-p578i7 scallopplus-s5"
                      data-framer-name="Background [Default]"
                    />
                    <div
                      className="framer-pc27k4 scallopplus-s6"
                      data-framer-name="Background [Boxed Mini]"
                    />
                    <div className="framer-cjwofg" data-framer-name="Content">
                      {/*$*/}
                      <a className="framer-1jy87r1 framer-145ct8v" href="/">
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="Logo + Logo Type"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-1hjthz scallopplus-s7"
                          aria-hidden="true"
                        />
                      </a>
                      {/*/$*/}
                      <nav className="framer-xe9vp0" data-framer-name="Nav">
                        <div
                          className="framer-1hg3ikx-container"
                          data-framer-name="Menu [Desktop]"
                          name="Menu [Desktop]"
                        >
                          <div
                            name="Menu [Desktop]"
                            className="framer-n0D3w framer-1itjusz framer-v-1itjusz scallopplus-s8"
                            data-framer-name="Menu"
                          >
                            {/*$*/}
                            <div className="framer-mquu2a-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/accounts"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    Accounts{' '}
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                            {/*$*/}
                            <div className="framer-1yixouk-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/scallop-pay"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    Scallop Pay
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                            {/*$*/}
                            <div className="framer-wi7xty-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/cards"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    Cards
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                            {/*$*/}
                            <div className="framer-1tpzm7c-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/scallop-plus"
                                data-framer-page-link-current="true"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    Scallop+
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                            {/*$*/}
                            <div className="framer-u2qi4r-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/helpdesk"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    FAQs
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                          </div>
                        </div>
                      </nav>
                      <div
                        className="framer-14uxsxw"
                        data-framer-name="Menu Icon"
                      >
                        <div
                          className="framer-16051d2"
                          data-framer-name="QR Overlay Trigger"
                          data-highlight="true"
                          tabIndex={0}
                        >
                          <div className="framer-168591e-container">
                            <div
                              className="framer-wWHmH framer-ly6l8a framer-v-18roq29 scallopplus-s12"
                              data-border="true"
                              data-framer-name="Variant 2"
                              data-highlight="true"
                              tabIndex={0}
                            >
                              <div
                                className="framer-1ebu3nm scallopplus-s13 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p
                                  dir="auto"
                                  className="framer-text scallopplus-s14"
                                >
                                  Get Started
                                </p>
                              </div>
                              <div
                                className="framer-1u4w7ze scallopplus-s15 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s16">
                                  Coming soon
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </header>
                </div>
              </div>
              <div className="ssr-variant hidden-1kt7ub1">
                <div
                  className="framer-8MFL4 framer-15zawlh framer-v-15viyb0 scallopplus-s3"
                  data-framer-name="Desktop [Boxed Mini]"
                >
                  <header
                    className="framer-ooxjtq scallopplus-s198"
                    data-border="true"
                    data-framer-name="Main Area"
                  >
                    <div
                      className="framer-p578i7 scallopplus-s6"
                      data-framer-name="Background [Default]"
                    />
                    <div
                      className="framer-pc27k4 scallopplus-s5"
                      data-framer-name="Background [Boxed Mini]"
                    />
                    <div className="framer-cjwofg" data-framer-name="Content">
                      {/*$*/}
                      <a className="framer-1jy87r1 framer-145ct8v" href="/">
                        <div
                          data-framer-component-type="SVG"
                          data-framer-name="Logo + Logo Type"
                          parentsize="0"
                          _constraints="[object Object]"
                          rotation="0"
                          shadows=""
                          className="framer-1hjthz scallopplus-s7"
                          aria-hidden="true"
                        />
                      </a>
                      {/*/$*/}
                      <nav className="framer-xe9vp0" data-framer-name="Nav">
                        <div
                          className="framer-1hg3ikx-container"
                          data-framer-name="Menu [Desktop]"
                          name="Menu [Desktop]"
                        >
                          <div
                            name="Menu [Desktop]"
                            className="framer-n0D3w framer-1itjusz framer-v-1itjusz scallopplus-s8"
                            data-framer-name="Menu"
                          >
                            {/*$*/}
                            <div className="framer-mquu2a-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/accounts"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    Accounts{' '}
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                            {/*$*/}
                            <div className="framer-1yixouk-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/scallop-pay"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    Scallop Pay
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                            {/*$*/}
                            <div className="framer-wi7xty-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/cards"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    Cards
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                            {/*$*/}
                            <div className="framer-1tpzm7c-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/scallop-plus"
                                data-framer-page-link-current="true"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    Scallop+
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                            {/*$*/}
                            <div className="framer-u2qi4r-container">
                              {/*$*/}
                              <a
                                className="framer-2DxPx framer-9un4ss framer-v-9un4ss framer-1tncwgq scallopplus-s9"
                                data-framer-name="Menu Item Simple"
                                data-highlight="true"
                                href="/helpdesk"
                                tabIndex={0}
                              >
                                <div
                                  className="framer-19g30xi scallopplus-s10 reveal"
                                  data-framer-component-type="RichTextContainer"
                                >
                                  <p
                                    dir="auto"
                                    className="framer-text scallopplus-s11"
                                  >
                                    FAQs
                                  </p>
                                </div>
                              </a>
                              {/*/$*/}
                            </div>
                            {/*/$*/}
                          </div>
                        </div>
                      </nav>
                      <div
                        className="framer-14uxsxw"
                        data-framer-name="Menu Icon"
                      >
                        <div
                          className="framer-16051d2"
                          data-framer-name="QR Overlay Trigger"
                          data-highlight="true"
                          tabIndex={0}
                        >
                          <div className="framer-168591e-container">
                            <div
                              className="framer-wWHmH framer-ly6l8a framer-v-18roq29 scallopplus-s12"
                              data-border="true"
                              data-framer-name="Variant 2"
                              data-highlight="true"
                              tabIndex={0}
                            >
                              <div
                                className="framer-1ebu3nm scallopplus-s13 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p
                                  dir="auto"
                                  className="framer-text scallopplus-s14"
                                >
                                  Get Started
                                </p>
                              </div>
                              <div
                                className="framer-1u4w7ze scallopplus-s15 reveal"
                                data-framer-component-type="RichTextContainer"
                              >
                                <p className="framer-text scallopplus-s16">
                                  Coming soon
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </header>
                </div>
              </div>
            </div>
          </div>
          <div className="ssr-variant hidden-1kt7ub1 hidden-otbxcl">
            <div
              className="framer-14xydkj-container"
              data-framer-appear-id="14xydkj"
              id="14xydkj"
            >
              <div
                className="framer-8MFL4 framer-15zawlh framer-v-ewbo4l scallopplus-s3"
                data-framer-name="Mobile"
              >
                <header
                  className="framer-ooxjtq scallopplus-s17"
                  data-border="true"
                  data-framer-name="Main Area"
                >
                  <div
                    className="framer-p578i7 scallopplus-s5"
                    data-framer-name="Background [Default]"
                  />
                  <div
                    className="framer-pc27k4 scallopplus-s6"
                    data-framer-name="Background [Boxed Mini]"
                  />
                  <div className="framer-cjwofg" data-framer-name="Content">
                    {/*$*/}
                    <a className="framer-1jy87r1 framer-145ct8v" href="/">
                      <div
                        data-framer-component-type="SVG"
                        data-framer-name="Logo + Logo Type"
                        parentsize="0"
                        _constraints="[object Object]"
                        rotation="0"
                        shadows=""
                        className="framer-1hjthz scallopplus-s7"
                        aria-hidden="true"
                      />
                    </a>
                    {/*/$*/}
                    <nav className="framer-xe9vp0" data-framer-name="Nav" />
                    <div
                      className="framer-14uxsxw"
                      data-framer-name="Menu Icon"
                    >
                      <div className="framer-1yr51k6-container">
                        {/*$*/}
                        <div
                          className="framer-OuhX3 framer-1ib4qbi framer-v-1ib4qbi scallopplus-s18"
                          data-framer-name="Mobile Menu"
                          data-highlight="true"
                          tabIndex={0}
                        >
                          <div className="framer-1cux5of scallopplus-s19" />
                          <div className="framer-1w1bul6 scallopplus-s19" />
                          <div className="framer-1ku2ucf scallopplus-s20" />
                        </div>
                        {/*/$*/}
                      </div>
                    </div>
                  </div>
                </header>
              </div>
            </div>
          </div>
        </div>
        <div id="overlay" />
        {/*/$*/}
      </>
    </div>
  );
}

export default ScallopPlus;
